import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { formatPullTime } from '../utils/time';
import { ScrollWindow } from './Research';

const API = '/api';

function apiCall(path: string, token: string, options: RequestInit = {}) {
  return fetch(`${API}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
}

function Toggle({ active, onChange, disabled }: { active: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors flex-shrink-0 ${
        active ? 'bg-green-600' : 'bg-gray-600'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${active ? 'translate-x-5' : 'translate-x-1'}`} />
    </button>
  );
}

// ─── Tab: Data Sources ────────────────────────────────────────────────────────

interface DataSource {
  id: number;
  name: string;
  description?: string;
  category_id?: number;
  category_name?: string;
  priority: number;
  weight: number;
  is_active: boolean;
  api_key: string;
  api_endpoint: string;
  auth_type: string;
  refresh_label: string;
  last_pull_at?: string;
  env_key?: string;
  is_system?: boolean;
  disabled_reason?: string;
  applies_to_exchanges?: string[];
  exchange_weights?: Record<string, number>;
  trust_score?: number;
}

interface DataCategory {
  id: number;
  name: string;
  description?: string;
  sort_order: number;
  source_count: number;
  active_count: number;
  exchange_weights: Record<string, number>;
  excluded_from_category_weights?: boolean;
  is_event_driven?: boolean;
}

const AEST_OFFSET_MS = 10 * 60 * 60 * 1000;

function parseUTC(iso: string): Date {
  const s = (iso.endsWith('Z') || iso.includes('+')) ? iso : iso + 'Z';
  return new Date(s);
}

function fmtAEST(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const utcDate = parseUTC(iso);
    const aestDate = new Date(utcDate.getTime() + AEST_OFFSET_MS);
    const timeStr = aestDate.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
    return aestDate.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }) + ' ' + timeStr + ' AEST';
  } catch { return iso ?? '—'; }
}


const SOURCE_TYPE_COLORS: Record<string, string> = {
  api: 'bg-status-blue-soft text-status-blue-muted',
  rss: 'bg-status-amber-soft text-status-amber-muted',
  podcast: 'bg-accent-soft text-text-secondary',
  social: 'bg-status-red-soft text-status-red-muted',
  price: 'bg-status-green-soft text-status-green-muted',
  asx_announcement: 'bg-status-blue-soft text-status-blue-muted',
  News: 'bg-status-amber-soft text-status-amber-muted',
  Price: 'bg-status-green-soft text-status-green-muted',
  Fundamentals: 'bg-status-blue-soft text-status-blue-muted',
  Macro: 'bg-status-red-soft text-status-red-muted',
  Sentiment: 'bg-status-amber-soft text-status-amber-muted',
};

const EXCHANGES = ['ASX', 'CHI-X', 'NYSE', 'NASDAQ', 'AMEX', 'NYSE_ARCA'] as const;
type ExchangeCode = typeof EXCHANGES[number];
const EXCHANGE_DISPLAY: Record<string, string> = {
  'ASX': 'ASX', 'CHI-X': 'Chi-X', 'NYSE': 'NYSE',
  'NASDAQ': 'NASDAQ', 'AMEX': 'AMEX', 'NYSE_ARCA': 'NYSE Arca',
};
type ExchangeFilter = 'ALL' | ExchangeCode;

function CategoryWeightsSection({ token, categories, onRefresh }: {
  token: string;
  categories: DataCategory[];
  onRefresh: () => void;
}) {
  const [collapsed, setCollapsed] = useState(true);
  const [editingCat, setEditingCat] = useState<DataCategory | null>(null);
  const [saveMsg, setSaveMsg] = useState('');

  // Weight totals per exchange — exclude event-driven / excluded categories
  const weightCategories = categories.filter(c => !c.excluded_from_category_weights);
  const catTotals: Record<string, number> = {};
  for (const ex of EXCHANGES) {
    catTotals[ex] = weightCategories.reduce((sum, c) => sum + (c.exchange_weights[ex] ?? 0), 0);
  }
  const activeExchanges = EXCHANGES.filter(ex => catTotals[ex] > 0);
  const anyWrong = activeExchanges.some(ex => Math.abs(catTotals[ex] - 100) > 0.5);

  return (
    <div className="bg-surface border border-border-base rounded-lg overflow-hidden">
      {/* Section heading */}
      <div
        className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-surface-hover/30 select-none border-b border-border-base"
        onClick={() => setCollapsed(c => !c)}
      >
        <span className="text-text-secondary text-xs w-3">{collapsed ? '▶' : '▼'}</span>
        <span className="font-semibold text-sm">Category Weights</span>
        <div className="flex items-center gap-2 ml-2 flex-wrap">
          {activeExchanges.map(ex => {
            const t = catTotals[ex];
            const ok = Math.abs(t - 100) < 0.5;
            return (
              <span key={ex} className={`text-xs font-mono ${ok ? 'text-status-green' : 'text-status-red'}`}>
                {EXCHANGE_DISPLAY[ex]}: {t}%{ok ? ' ✓' : ' ⚠'}
              </span>
            );
          })}
          {anyWrong && <span className="text-status-red text-xs">⚠ Weights must sum to 100% per exchange</span>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {saveMsg && <span className="text-status-green text-xs">{saveMsg}</span>}
        </div>
      </div>

      {!collapsed && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-text-muted border-b border-border-base text-xs uppercase">
                {/* Weight totals row */}
              </tr>
              <tr className="text-text-secondary border-b border-border-base text-xs bg-surface-2">
                <th className="text-left px-4 py-2 font-semibold text-text-primary">Totals</th>
                <th className="px-3 py-2 hidden md:table-cell"></th>
                {EXCHANGES.map(ex => (
                  <th key={ex} className="text-center px-2 py-2">
                    {(() => {
                      const t = catTotals[ex];
                      if (t === 0) return <span className="text-text-muted">—</span>;
                      const ok = Math.abs(t - 100) < 0.5;
                      return <span className={`font-mono font-bold ${ok ? 'text-status-green' : 'text-status-red'}`}>{t}%{ok ? ' ✓' : ' ⚠'}</span>;
                    })()}
                  </th>
                ))}
                <th className="text-center px-3 py-2 hidden sm:table-cell"></th>
                <th className="px-4 py-2"></th>
              </tr>
              <tr className="text-text-muted border-b border-border-base text-xs uppercase">
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Description</th>
                {EXCHANGES.map(ex => (
                  <th key={ex} className="text-center px-2 py-2">{EXCHANGE_DISPLAY[ex]}</th>
                ))}
                <th className="text-center px-3 py-2 hidden sm:table-cell">Active</th>
                <th className="text-right px-4 py-2">Edit</th>
              </tr>
            </thead>
            <tbody>
              {weightCategories.map(cat => (
                <tr key={cat.id} className="border-b border-border-base/50 hover:bg-surface-hover/30">
                  <td className="px-4 py-2.5 font-medium text-sm">{cat.name}</td>
                  <td className="px-4 py-2.5 text-xs text-text-muted hidden md:table-cell">{cat.description || '—'}</td>
                  {EXCHANGES.map(ex => {
                    const w = cat.exchange_weights[ex] ?? 0;
                    return (
                      <td key={ex} className="px-2 py-2.5 text-center">
                        <span className={`text-xs font-mono ${w > 0 ? 'text-status-blue-muted' : 'text-text-muted'}`}>
                          {w > 0 ? `${w}%` : ''}
                        </span>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2.5 text-center hidden sm:table-cell">
                    <span className={`text-xs ${cat.active_count > 0 ? 'text-status-green' : 'text-text-muted'}`}>
                      {cat.active_count}/{cat.source_count}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      onClick={() => setEditingCat(cat)}
                      className="text-xs bg-surface-2 hover:bg-surface-hover px-2.5 py-1 rounded-btn-sm"
                    >Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingCat && (
        <CategoryModal
          category={editingCat}
          token={token}
          onSave={async () => {
            await onRefresh();
            setEditingCat(null);
            setSaveMsg('Saved');
            setTimeout(() => setSaveMsg(''), 2500);
          }}
          onClose={() => setEditingCat(null)}
        />
      )}
    </div>
  );
}

function CategoryModal({ category, token, onSave, onClose }: {
  category: DataCategory;
  token: string;
  onSave: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(category.name);
  const [description, setDescription] = useState(category.description || '');
  const [weights, setWeights] = useState<Record<string, number>>(
    Object.fromEntries(EXCHANGES.map(ex => [ex, category.exchange_weights[ex] ?? 0]))
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setW = (ex: string, val: number) => setWeights(prev => ({ ...prev, [ex]: Math.max(0, Math.min(100, isNaN(val) ? 0 : val)) }));

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    try {
      const [r1, r2] = await Promise.all([
        apiCall(`/admin/data-source-categories/${category.id}`, token, {
          method: 'PUT', body: JSON.stringify({ name, description }),
        }),
        apiCall(`/admin/data-source-categories/${category.id}/weights`, token, {
          method: 'PUT', body: JSON.stringify(weights),
        }),
      ]);
      if (!r1.ok || !r2.ok) { setErr('Save failed'); setSaving(false); return; }
      onSave();
    } catch (e: any) { setErr(e.message); setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete category "${category.name}"? Sources in this category will become uncategorised.`)) return;
    setSaving(true);
    try {
      const res = await apiCall(`/admin/data-source-categories/${category.id}`, token, { method: 'DELETE' });
      if (!res.ok) { setErr('Delete failed'); setSaving(false); return; }
      onSave();
    } catch (e: any) { setErr(e.message); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-surface-60 flex items-center justify-center z-50 p-4">
      <div className="bg-surface border border-border-base rounded-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">Edit Category: {category.name}</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-bright text-xl leading-none">×</button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-text-secondary text-xs">Name</label>
            <input value={name} onChange={e => setName(e.target.value)}
              className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm mt-1" />
          </div>
          <div>
            <label className="text-text-secondary text-xs">Description</label>
            <input value={description} onChange={e => setDescription(e.target.value)}
              className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm mt-1"
              placeholder="Brief description..." />
          </div>
          <div>
            <label className="text-text-secondary text-xs block mb-2">Exchange Weights (% of total signal contribution)</label>
            <div className="grid grid-cols-3 gap-2">
              {EXCHANGES.map(ex => (
                <div key={ex}>
                  <label className="text-xs text-text-muted">{EXCHANGE_DISPLAY[ex]}</label>
                  <input type="number" min="0" max="100"
                    value={weights[ex] ?? 0}
                    onChange={e => setW(ex, parseInt(e.target.value) || 0)}
                    className="w-full bg-surface-2 border border-border-light rounded px-2 py-1 text-sm mt-0.5" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {err && <div className="text-status-red text-xs">{err}</div>}

        <div className="flex gap-2 pt-2 border-t border-border-base">
          <button onClick={handleSave} disabled={saving}
            className="flex-1 bg-status-green-btn hover:bg-status-green-btn-hover disabled:opacity-50 text-text-bright py-2 rounded-btn text-sm font-medium">
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button onClick={onClose}
            className="bg-surface-2 hover:bg-surface-hover text-text-bright px-4 py-1.5 rounded-btn text-sm">
            Cancel
          </button>
        </div>
        <button onClick={handleDelete} disabled={saving}
          className="w-full bg-status-red-soft hover:bg-status-red-btn-hover disabled:opacity-50 text-text-bright py-2 rounded-btn text-sm">
          Delete Category
        </button>
      </div>
    </div>
  );
}

function DataSourcesTab({ token }: { token: string }) {
  const [sources, setSources] = useState<DataSource[]>([]);
  const [categories, setCategories] = useState<DataCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [exchangeFilter, setExchangeFilter] = useState<ExchangeFilter>('ALL');
  const [editingSource, setEditingSource] = useState<DataSource | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [savingId, setSavingId] = useState<number | null>(null);
  const [saveMsg, setSaveMsg] = useState('');
  const [testResults, setTestResults] = useState<Record<number, { status: 'testing' | 'ok' | 'fail'; msg?: string }>>({});
  const [editAllCatId, setEditAllCatId] = useState<number | null>(null);
  const [editAllDraft, setEditAllDraft] = useState<Record<number, Record<string, number>>>({});
  const [editAllTrust, setEditAllTrust] = useState<Record<number, number>>({});
  const [editAllSaving, setEditAllSaving] = useState(false);

  // Announcement card collapse state
  const [annOpen, setAnnOpen] = useState(false);
  const [annSlidersOpen, setAnnSlidersOpen] = useState(false);
  const [markitOpen, setMarkitOpen] = useState(false);
  const [eaSourcesOpen, setEaSourcesOpen] = useState(true);
  const [connConfigOpen, setConnConfigOpen] = useState(false);

  // Markit connector config
  const [markitConfig, setMarkitConfig] = useState<Record<string, string>>({});
  const [markitDraft, setMarkitDraft] = useState<Record<string, string>>({});
  const [markitSaving, setMarkitSaving] = useState<Record<string, boolean>>({});
  const [markitSaveMsg, setMarkitSaveMsg] = useState<Record<string, string>>({});
  const [connTestStatus, setConnTestStatus] = useState<'idle' | 'testing' | 'ok' | 'fail'>('idle');
  const [connTestMsg, setConnTestMsg] = useState('');

  // Announcement weights
  interface AnnouncementWeight { id: number; exchange: string; signal_weight: number; }
  const [annWeights, setAnnWeights] = useState<AnnouncementWeight[]>([]);
  const [annWeightEdits, setAnnWeightEdits] = useState<Record<string, string>>({});
  const [annSaveMsg, setAnnSaveMsg] = useState<Record<string, string>>({});

  // Price & Market Data — Markit Digital (ASX Prices) quote source (migrated from Exchange Management, Session 9)
  const [priceMarkitHealth, setPriceMarkitHealth] = useState<{ status: string; last_checked_at: string | null } | null>(null);
  const [priceMarkitLastSuccess, setPriceMarkitLastSuccess] = useState<string | null>(null);
  const [monCapAsx, setMonCapAsx] = useState<number>(60);
  const [monCapUs, setMonCapUs] = useState<number>(40);
  const [monCapAsxDraft, setMonCapAsxDraft] = useState<number>(60);
  const [monCapUsDraft, setMonCapUsDraft] = useState<number>(40);
  const [monCapSaving, setMonCapSaving] = useState(false);
  const [monCapMsg, setMonCapMsg] = useState('');
  const [monCountAsx, setMonCountAsx] = useState<number>(0);
  const [monCountUs, setMonCountUs] = useState<number>(0);

  const loadPriceMarkitStatus = useCallback(() => {
    apiCall('/admin/broker-health', token).then(r => r.ok ? r.json() : null).then(d => {
      if (Array.isArray(d)) {
        const mh = d.find((bh: any) => bh.broker_name === 'Markit Price Feed');
        if (mh) setPriceMarkitHealth(mh);
      }
    }).catch(() => {});
  }, [token]);

  const loadMonitoringCaps = useCallback(() => {
    apiCall('/admin/system-config?prefix=monitoring_cap_', token).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.config) {
        const asx = parseInt(d.config['monitoring_cap_asx'] ?? '60');
        const us = parseInt(d.config['monitoring_cap_us'] ?? '40');
        if (!isNaN(asx)) { setMonCapAsx(asx); setMonCapAsxDraft(asx); }
        if (!isNaN(us)) { setMonCapUs(us); setMonCapUsDraft(us); }
      }
    }).catch(() => {});
    apiCall('/admin/system-config?prefix=markit_price_last_success', token).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.config?.markit_price_last_success) setPriceMarkitLastSuccess(d.config.markit_price_last_success);
    }).catch(() => {});
    apiCall('/tickers?tier=monitoring&exchange=ASX&limit=200', token).then(r => r.ok ? r.json() : null).then(d => {
      if (d) setMonCountAsx(d.total ?? d.count ?? 0);
    }).catch(() => {});
    apiCall('/tickers?tier=monitoring&exchange=US&limit=200', token).then(r => r.ok ? r.json() : null).then(d => {
      if (d) setMonCountUs(d.total ?? d.count ?? 0);
    }).catch(() => {});
  }, [token]);

  const saveMonitoringCaps = async () => {
    setMonCapSaving(true);
    try {
      const [r1, r2] = await Promise.all([
        apiCall('/admin/system-config/monitoring_cap_asx', token, { method: 'PUT', body: JSON.stringify({ value: String(monCapAsxDraft) }) }),
        apiCall('/admin/system-config/monitoring_cap_us', token, { method: 'PUT', body: JSON.stringify({ value: String(monCapUsDraft) }) }),
      ]);
      if (r1.ok && r2.ok) {
        setMonCapAsx(monCapAsxDraft);
        setMonCapUs(monCapUsDraft);
        setMonCapMsg('Saved');
      } else {
        setMonCapMsg('Save failed');
      }
    } catch { setMonCapMsg('Save failed'); }
    setMonCapSaving(false);
    setTimeout(() => setMonCapMsg(''), 3000);
  };

  useEffect(() => {
    loadPriceMarkitStatus();
    loadMonitoringCaps();
    const interval = setInterval(loadPriceMarkitStatus, 60000);
    return () => clearInterval(interval);
  }, [loadPriceMarkitStatus, loadMonitoringCaps]);

  const loadAnnWeights = useCallback(async () => {
    try {
      const res = await apiCall('/admin/announcement-weights', token);
      if (res.ok) {
        const data = await res.json();
        const weights: AnnouncementWeight[] = data.weights || [];
        setAnnWeights(weights);
        const edits: Record<string, string> = {};
        weights.forEach((w: AnnouncementWeight) => { edits[w.exchange] = String(w.signal_weight); });
        setAnnWeightEdits(edits);
      }
    } catch {}
  }, [token]);

  const loadMarkitConfig = useCallback(async () => {
    try {
      const res = await apiCall('/admin/system-config?prefix=markit_', token);
      if (res.ok) {
        const data = await res.json();
        setMarkitConfig(data.config || {});
        setMarkitDraft(data.config || {});
      }
    } catch {}
  }, [token]);

  const saveMarkitKey = async (key: string) => {
    setMarkitSaving(p => ({ ...p, [key]: true }));
    try {
      const res = await apiCall(`/admin/system-config/${key}`, token, {
        method: 'PUT',
        body: JSON.stringify({ value: markitDraft[key] }),
      });
      setMarkitSaveMsg(p => ({ ...p, [key]: res.ok ? 'Saved' : 'Error' }));
      if (res.ok) setMarkitConfig(p => ({ ...p, [key]: markitDraft[key] }));
    } catch {
      setMarkitSaveMsg(p => ({ ...p, [key]: 'Error' }));
    } finally {
      setMarkitSaving(p => ({ ...p, [key]: false }));
      setTimeout(() => setMarkitSaveMsg(p => ({ ...p, [key]: '' })), 2500);
    }
  };

  const testMarkitConn = async () => {
    setConnTestStatus('testing');
    setConnTestMsg('');
    try {
      const res = await apiCall('/admin/asx-announcements/test', token);
      if (res.ok) {
        const data = await res.json();
        setConnTestStatus('ok');
        setConnTestMsg(`OK — ${data.count ?? 0} announcements returned`);
      } else {
        setConnTestStatus('fail');
        setConnTestMsg(`HTTP ${res.status}`);
      }
    } catch (e: any) {
      setConnTestStatus('fail');
      setConnTestMsg(String(e));
    }
  };

  const saveAnnWeight = async (exchange: string) => {
    const val = parseFloat(annWeightEdits[exchange]);
    if (isNaN(val) || val < 0 || val > 3) {
      setAnnSaveMsg(p => ({ ...p, [exchange]: 'Must be 0–3' }));
      setTimeout(() => setAnnSaveMsg(p => ({ ...p, [exchange]: '' })), 3000);
      return;
    }
    try {
      const res = await apiCall(`/admin/announcement-weights/${exchange}`, token, {
        method: 'PUT',
        body: JSON.stringify({ signal_weight: val }),
      });
      setAnnSaveMsg(p => ({ ...p, [exchange]: res.ok ? 'Saved' : 'Error' }));
      if (res.ok) loadAnnWeights();
    } catch {
      setAnnSaveMsg(p => ({ ...p, [exchange]: 'Error' }));
    }
    setTimeout(() => setAnnSaveMsg(p => ({ ...p, [exchange]: '' })), 3000);
  };

  const load = useCallback(async () => {
    try {
      const [srcRes, catRes] = await Promise.all([
        apiCall('/admin/data-sources', token),
        apiCall('/admin/data-source-categories', token),
      ]);
      if (srcRes.ok) { const d = await srcRes.json(); setSources(d.data_sources || []); }
      if (catRes.ok) {
        const d = await catRes.json();
        const cats: DataCategory[] = d.categories || [];
        setCategories(cats);
        setCollapsed(new Set(cats.map(c => c.id))); // all category cards load minimised by default
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAnnWeights(); }, [loadAnnWeights]);
  useEffect(() => { loadMarkitConfig(); }, [loadMarkitConfig]);

  const toggleActive = async (s: DataSource) => {
    setSavingId(s.id);
    try {
      const res = await apiCall(`/admin/data-sources/${s.id}`, token, {
        method: 'PUT', body: JSON.stringify({ is_active: !s.is_active }),
      });
      if (res.ok) setSources(prev => prev.map(x => x.id === s.id ? { ...x, is_active: !x.is_active } : x));
    } catch {}
    setSavingId(null);
  };

  const toggleCollapse = (catId: number) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId); else next.add(catId);
      return next;
    });
  };

  const testSource = async (s: DataSource) => {
    setTestResults(prev => ({ ...prev, [s.id]: { status: 'testing' } }));
    try {
      const res = await apiCall('/admin/data-sources/test', token, {
        method: 'POST',
        body: JSON.stringify({ name: s.name, url: s.api_endpoint }),
      });
      const d = await res.json();
      setTestResults(prev => ({ ...prev, [s.id]: { status: d.success ? 'ok' : 'fail', msg: d.message } }));
    } catch (e: any) {
      setTestResults(prev => ({ ...prev, [s.id]: { status: 'fail', msg: String(e) } }));
    }
    setTimeout(() => setTestResults(prev => {
      const next = { ...prev };
      delete next[s.id];
      return next;
    }), 5000);
  };

  const startEditAll = (catId: number, catSources: DataSource[]) => {
    const draft: Record<number, Record<string, number>> = {};
    const trust: Record<number, number> = {};
    for (const s of catSources) {
      draft[s.id] = Object.fromEntries(EXCHANGES.map(ex => [ex, (s.exchange_weights || {})[ex] ?? 0]));
      trust[s.id] = s.trust_score ?? 0.70;
    }
    setEditAllDraft(draft);
    setEditAllTrust(trust);
    setEditAllCatId(catId);
  };

  const cancelEditAll = () => { setEditAllCatId(null); setEditAllDraft({}); setEditAllTrust({}); };

  const requestEdit = (s: DataSource) => {
    if (editingSource && editingSource.id !== s.id) {
      const ok = window.confirm(`Discard changes to "${editingSource.name}"?`);
      if (!ok) return;
    }
    setEditingSource(s);
    setShowAddModal(false);
  };

  const deleteSource = async (s: DataSource) => {
    if (!window.confirm(`Delete "${s.name}"? This cannot be undone.`)) return;
    try {
      const res = await apiCall(`/admin/data-sources/${s.id}`, token, { method: 'DELETE' });
      if (res.ok) {
        setSources(prev => prev.filter(x => x.id !== s.id));
        setSaveMsg('Deleted');
        setTimeout(() => setSaveMsg(''), 2500);
      } else {
        const d = await res.json().catch(() => ({}));
        setSaveMsg(d.detail || 'Delete failed');
        setTimeout(() => setSaveMsg(''), 3000);
      }
    } catch {
      setSaveMsg('Delete failed');
      setTimeout(() => setSaveMsg(''), 3000);
    }
  };

  const saveEditAll = async (catSources: DataSource[]) => {
    setEditAllSaving(true);
    try {
      await Promise.all(
        catSources.map(s =>
          apiCall(`/admin/data-sources/${s.id}/exchange-weights`, token, {
            method: 'PUT',
            body: JSON.stringify(editAllDraft[s.id] || {}),
          })
        )
      );
      // Save trust scores via PUT
      await Promise.all(
        catSources
          .filter(s => editAllTrust[s.id] !== undefined)
          .map(s =>
            apiCall(`/admin/data-sources/${s.id}`, token, {
              method: 'PUT',
              body: JSON.stringify({ trust_score: editAllTrust[s.id] }),
            })
          )
      );
      setSources(prev => prev.map(s => {
        if (editAllDraft[s.id]) return { ...s, exchange_weights: editAllDraft[s.id], trust_score: editAllTrust[s.id] ?? s.trust_score };
        return s;
      }));
      setSaveMsg('Saved');
      setTimeout(() => setSaveMsg(''), 2500);
    } catch {}
    setEditAllSaving(false);
    setEditAllCatId(null);
    setEditAllDraft({});
    setEditAllTrust({});
  };

  // Filter sources by exchange
  const filteredSources = sources.filter(s => {
    if (exchangeFilter === 'ALL') return true;
    const w = s.exchange_weights || {};
    return (w[exchangeFilter] ?? 0) > 0;
  });

  const blankSource: DataSource = {
    id: -1, name: '', description: '', priority: 99, weight: 0,
    is_active: true, api_key: '', api_endpoint: '', auth_type: 'apikey', refresh_label: '30 min',
    env_key: '', is_system: false, applies_to_exchanges: [], trust_score: 0.70,
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        {/* Title */}
        <div className="flex items-center gap-3">
          <h2 className="font-semibold">Data Sources</h2>
          <span className="text-xs text-text-muted">{sources.filter(s => s.is_active).length} active of {sources.length}</span>
        </div>
        {/* Row 1: export/report buttons */}
        <div className="flex items-center justify-end gap-2 flex-wrap mt-2">
          {saveMsg && <span className="text-status-green text-sm mr-auto">{saveMsg}</span>}
          <button
            onClick={async () => {
              try {
                const res = await fetch('/api/admin/data-sources/quality-report', {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!res.ok) throw new Error('Failed to generate report');
                const blob = await res.blob();
                const contentDisposition = res.headers.get('Content-Disposition') || '';
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                const filename = filenameMatch ? filenameMatch[1] : 'quality_report.csv';
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } catch (err) {
                console.error('Quality report download failed:', err);
                alert('Failed to generate quality report');
              }
            }}
            className="bg-surface-2 hover:bg-surface-hover text-text-bright px-2.5 py-1 rounded text-xs"
          >
            Data Quality Report
          </button>
          <button onClick={async () => {
            try {
              const res = await apiCall('/admin/data-sources/config-export', token);
              if (!res.ok) throw new Error('Failed to export config');
              const blob = await res.blob();
              const contentDisposition = res.headers.get('Content-Disposition') || '';
              const filenameMatch = contentDisposition.match(/filename="(.+)"/);
              const filename = filenameMatch ? filenameMatch[1] : 'data_source_config.csv';
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            } catch (err) {
              console.error('Config export failed:', err);
              alert('Failed to export config');
            }
          }}
            className="bg-surface-2 hover:bg-surface-hover text-text-bright px-2.5 py-1 rounded text-xs">
            Data Source Export
          </button>
          <button onClick={async () => {
            try {
              const res = await apiCall('/admin/data-sources/category-weights-export', token);
              if (!res.ok) throw new Error('Failed to export weights');
              const blob = await res.blob();
              const contentDisposition = res.headers.get('Content-Disposition') || '';
              const filenameMatch = contentDisposition.match(/filename="(.+)"/);
              const filename = filenameMatch ? filenameMatch[1] : 'category_weights.csv';
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              window.URL.revokeObjectURL(url);
              document.body.removeChild(a);
            } catch (err) {
              console.error('Weights export failed:', err);
              alert('Failed to export weights');
            }
          }}
            className="bg-surface-2 hover:bg-surface-hover text-text-bright px-2.5 py-1 rounded text-xs">
            Data Category Export
          </button>
        </div>
        {/* Row 2: Add Source */}
        <div className="flex items-center justify-end gap-2 mt-2">
          <button onClick={() => { setEditingSource(blankSource); setShowAddModal(true); }}
            className="px-4 py-1.5 bg-status-green-btn hover:bg-status-green-btn-hover text-text-bright text-sm rounded-btn flex items-center gap-2">
            + Add Source
          </button>
        </div>
        {/* Row 3: exchange filter pills */}
        <div className="flex items-center justify-end gap-2 flex-wrap mt-2">
          <button onClick={() => setExchangeFilter('ALL')}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${exchangeFilter === 'ALL' ? 'bg-status-blue-btn border-border-blue text-text-bright' : 'border-border-light text-text-secondary hover:border-border-light'}`}>
            All Markets
          </button>
          {EXCHANGES.map(ex => (
            <button key={ex} onClick={() => setExchangeFilter(ex)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${exchangeFilter === ex ? 'bg-status-blue-btn border-border-blue text-text-bright' : 'border-border-light text-text-secondary hover:border-border-light'}`}>
              {EXCHANGE_DISPLAY[ex]}
            </button>
          ))}
        </div>
      </div>

      {/* Price & Market Data — Markit Digital (ASX Prices). Session 12: pulled out of its old
          non-collapsible wrapper into the same card/header/toggle pattern as the other tiles on
          this page, and moved above Exchange Announcements per Cameron's requested order. */}
      <div className="bg-surface rounded-lg border border-border-base overflow-hidden">
        <div
          className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-surface-hover/30 select-none border-b border-border-base"
          onClick={() => setMarkitOpen(o => !o)}
        >
          <span className="text-text-secondary text-xs w-3">{markitOpen ? '▼' : '▶'}</span>
          <h2 className="font-semibold text-base">Markit Digital (ASX Prices)</h2>
          <p className="text-text-secondary text-sm ml-2 hidden sm:block">Price & Market Data — ASX quote source and monitoring tier limits.</p>
        </div>

        {markitOpen && (
          <div className="p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex flex-col gap-0.5">
                <span className="text-text-muted text-xs">Source</span>
                <span className="text-text-primary font-mono text-xs">Markit Digital (ASX Prices)</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-text-muted text-xs">Status</span>
                <span className={`text-xs font-medium ${priceMarkitHealth?.status === 'ok' ? 'text-status-green' : priceMarkitHealth?.status === 'error' ? 'text-status-red' : 'text-text-secondary'}`}>
                  {priceMarkitHealth ? (priceMarkitHealth.status === 'ok' ? 'OK' : priceMarkitHealth.status === 'error' ? 'Error' : priceMarkitHealth.status) : 'Unknown'}
                </span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-text-muted text-xs">Auth</span>
                <span className="text-text-primary font-mono text-xs">None — public endpoint</span>
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-text-muted text-xs">Rate Limit</span>
                <span className="text-text-primary font-mono text-xs">60 calls/min</span>
              </div>
              <div className="flex flex-col gap-0.5 sm:col-span-2">
                <span className="text-text-muted text-xs">Endpoint</span>
                <span className="text-text-primary font-mono text-xs">https://asx.api.markitdigital.com/asx-research/1.0</span>
              </div>
              {priceMarkitHealth?.last_checked_at && (
                <div className="sm:col-span-2 text-text-muted text-xs">
                  Last checked: {formatPullTime(priceMarkitHealth.last_checked_at)}
                </div>
              )}
              {priceMarkitLastSuccess && (
                <div className="sm:col-span-2 text-text-muted text-xs">
                  Last successful fetch: {fmtAEST(priceMarkitLastSuccess)}
                </div>
              )}
              <div className="sm:col-span-2">
                <p className="text-text-muted text-xs italic">Public endpoint — no credentials required. Same domain as ASX announcements feed.</p>
              </div>
            </div>

            {/* Monitoring Tier Limits */}
            <div className="mt-5 pt-4 border-t border-border-base">
              <h4 className="font-semibold text-sm mb-1">Monitoring Tier Limits</h4>
              <p className="text-text-muted text-xs mb-4">Maximum number of tickers in the real-time monitoring tier. Reduce caps if ASX price feed is unavailable to conserve Yahoo Finance fallback calls.</p>
              {monCapMsg && <p className={`text-sm mb-3 ${monCapMsg === 'Saved' ? 'text-status-green' : 'text-status-red'}`}>{monCapMsg}</p>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="flex flex-col gap-1">
                  <label className="text-text-muted text-xs">ASX Monitoring Cap</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={1} max={100}
                      value={monCapAsxDraft}
                      onChange={e => setMonCapAsxDraft(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="w-24 bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm text-text-primary"
                    />
                    <span className="text-xs text-text-muted">tickers (current: {monCountAsx} / {monCapAsx})</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-text-muted text-xs">US Monitoring Cap</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min={1} max={100}
                      value={monCapUsDraft}
                      onChange={e => setMonCapUsDraft(Math.min(100, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="w-24 bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm text-text-primary"
                    />
                    <span className="text-xs text-text-muted">tickers (current: {monCountUs} / {monCapUs})</span>
                  </div>
                </div>
              </div>
              <button
                onClick={saveMonitoringCaps}
                disabled={monCapSaving}
                className="text-xs bg-status-green-btn hover:bg-status-green-btn-hover text-text-bright px-2.5 py-1 rounded-btn-sm disabled:opacity-50"
              >
                {monCapSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Exchange Announcements */}
      <div className="bg-surface rounded-lg border border-border-base overflow-hidden">
        {/* Outer header — toggles full card */}
        <div
          className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-surface-hover/30 select-none border-b border-border-base"
          onClick={() => setAnnOpen(o => !o)}
        >
          <span className="text-text-secondary text-xs w-3">{annOpen ? '▼' : '▶'}</span>
          <h2 className="font-semibold text-base">Exchange Announcements</h2>
          <p className="text-text-secondary text-sm ml-2 hidden sm:block">
            Event-driven sources. Multiplier applied to signal score when an announcement exists for a ticker in the last 24h.
          </p>
        </div>

        {annOpen && (
          <div className="p-4 space-y-3">
            {/* Inner section 1: Signal Multipliers — default collapsed */}
            <div className="border border-border-base rounded-lg overflow-hidden">
              <div
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-surface-hover/30 select-none bg-surface-2"
                onClick={() => setAnnSlidersOpen(o => !o)}
              >
                <span className="text-text-secondary text-xs w-3">{annSlidersOpen ? '▼' : '▶'}</span>
                <span className="text-sm font-medium">Signal Multipliers</span>
                <span className="text-xs text-text-muted ml-1">per-exchange boost when announcement present</span>
              </div>
              {annSlidersOpen && (
                <div className="px-3 pb-3 pt-1">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-text-secondary border-b border-border-base">
                        <th className="text-left py-2 pr-4">Exchange</th>
                        <th className="text-left py-2 pr-4">Signal Multiplier</th>
                        <th className="py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {annWeights.map(w => (
                        <tr key={w.exchange} className="border-b border-border-base">
                          <td className="py-2 pr-4 font-medium">{w.exchange}</td>
                          <td className="py-2 pr-4">
                            <div className="flex items-center gap-2">
                              <input
                                type="range" min="0" max="3" step="0.25"
                                value={annWeightEdits[w.exchange] ?? w.signal_weight}
                                onChange={e => setAnnWeightEdits(p => ({ ...p, [w.exchange]: e.target.value }))}
                                className="w-32 accent-blue-500"
                              />
                              <span className="text-text-bright font-mono w-12">
                                {parseFloat(annWeightEdits[w.exchange] ?? String(w.signal_weight)).toFixed(2)}x
                              </span>
                              <button
                                onClick={() => saveAnnWeight(w.exchange)}
                                className="bg-status-blue-btn-hover hover:bg-status-blue-btn-hover text-text-bright px-2.5 py-1 rounded-btn-sm text-xs"
                              >Save</button>
                              {annSaveMsg[w.exchange] && (
                                <span className={`text-xs ${annSaveMsg[w.exchange] === 'Saved' ? 'text-status-green' : 'text-status-red'}`}>
                                  {annSaveMsg[w.exchange]}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Inner section 2: Connector Configuration */}
            <div className="border border-border-base rounded-lg overflow-hidden">
              <div
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-surface-hover/30 select-none bg-surface-2"
                onClick={() => setConnConfigOpen(o => !o)}
              >
                <span className="text-text-secondary text-xs w-3">{connConfigOpen ? '▼' : '▶'}</span>
                <span className="text-sm font-medium">Connector Configuration</span>
                <span className="text-xs text-text-muted ml-1">Markit Digital API settings</span>
                <div className="ml-auto flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={testMarkitConn}
                    disabled={connTestStatus === 'testing'}
                    className="bg-accent hover:bg-accent-hover disabled:opacity-50 text-text-bright px-2.5 py-1 rounded-btn-sm text-xs"
                  >
                    {connTestStatus === 'testing' ? 'Testing…' : 'Test Connection'}
                  </button>
                  {connTestStatus !== 'idle' && connTestStatus !== 'testing' && (
                    <span className={`text-xs ${connTestStatus === 'ok' ? 'text-status-green' : 'text-status-red'}`}>
                      {connTestStatus === 'ok' ? '✅' : '❌'} {connTestMsg}
                    </span>
                  )}
                </div>
              </div>
              {connConfigOpen && (
                <div className="px-3 pb-3 pt-2 space-y-2 border-t border-border-base">
                  {[
                    { key: 'markit_announcements_url', label: 'Announcements Base URL', placeholder: 'https://asx.api.markitdigital.com/asx-research/1.0/markets/announcements' },
                    { key: 'markit_pdf_base_url', label: 'PDF Base URL', placeholder: 'https://cdn-api.markitdigital.com/apiman-gateway/ASX/asx-research/1.0/file' },
                    { key: 'markit_user_agent', label: 'User Agent', placeholder: 'Mozilla/5.0 ...' },
                    { key: 'markit_market_open_aest', label: 'Market Open (AEST)', placeholder: '09:45' },
                    { key: 'markit_market_close_aest', label: 'Market Close (AEST)', placeholder: '16:15' },
                    { key: 'markit_items_per_page', label: 'Items Per Page', placeholder: '50' },
                    { key: 'markit_pdf_max_size_mb', label: 'PDF Max Size (MB)', placeholder: '2' },
                    { key: 'markit_pdf_fetch_delay_seconds', label: 'PDF Fetch Delay (s)', placeholder: '2' },
                    { key: 'markit_poll_interval_market_hours', label: 'Poll Interval Market Hours (s)', placeholder: '300' },
                    { key: 'markit_poll_interval_off_hours', label: 'Poll Interval Off Hours (s)', placeholder: '3600' },
                  ].map(({ key, label, placeholder }) => (
                    <div key={key} className="flex items-center gap-2">
                      <label className="text-text-secondary text-xs w-44 shrink-0">{label}</label>
                      <input
                        value={markitDraft[key] ?? ''}
                        onChange={e => setMarkitDraft(p => ({ ...p, [key]: e.target.value }))}
                        placeholder={placeholder}
                        className="flex-1 bg-surface-2 border border-border-light rounded px-2 py-1 text-xs font-mono"
                      />
                      <button
                        onClick={() => saveMarkitKey(key)}
                        disabled={markitSaving[key] || markitDraft[key] === markitConfig[key]}
                        className="bg-status-blue-btn-hover hover:bg-status-blue-btn-hover disabled:opacity-40 text-text-bright px-2.5 py-1 rounded-btn-sm text-xs"
                      >
                        {markitSaving[key] ? '…' : 'Save'}
                      </button>
                      {markitSaveMsg[key] && (
                        <span className={`text-xs ${markitSaveMsg[key] === 'Saved' ? 'text-status-green' : 'text-status-red'}`}>
                          {markitSaveMsg[key]}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Inner section 3: Source Weights — default expanded */}
            {(() => {
              const eaCat = categories.find(c => c.name === 'Exchange Announcements');
              const eaSources = eaCat
                ? sources.filter(s => s.category_id === eaCat.id)
                : sources.filter(s => s.category_name === 'Exchange Announcements');
              const isEaEditAll = eaCat != null && editAllCatId === eaCat.id;
              const eaSrcTotals: Record<string, number> = {};
              for (const ex of EXCHANGES) {
                const total = eaSources.reduce((sum, s) => sum + ((s.exchange_weights || {})[ex] ?? 0), 0);
                if (total > 0) eaSrcTotals[ex] = total;
              }
              const eaSrcExchanges = Object.keys(eaSrcTotals);
              const eaAnyWrong = eaSrcExchanges.some(ex => Math.abs(eaSrcTotals[ex] - 100) > 0.5);
              return (
                <div className="border border-border-base rounded-lg overflow-hidden">
                  <div
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-surface-hover/30 select-none bg-surface-2 flex-wrap"
                    onClick={() => setEaSourcesOpen(o => !o)}
                  >
                    <span className="text-text-secondary text-xs w-3">{eaSourcesOpen ? '▼' : '▶'}</span>
                    <span className="text-sm font-medium">Source Weights</span>
                    <span className="text-xs text-text-muted ml-1">per-exchange contribution from EA sources</span>
                    {eaSrcExchanges.map(ex => {
                      const t = eaSrcTotals[ex];
                      const ok = Math.abs(t - 100) < 0.5;
                      return (
                        <span key={ex} className={`text-xs font-mono ${ok ? 'text-status-green' : 'text-status-red'}`}>
                          {EXCHANGE_DISPLAY[ex]}: {t}%{ok ? ' ✓' : ' ⚠'}
                        </span>
                      );
                    })}
                    {eaAnyWrong && <span className="text-status-red text-xs">⚠ Weights must sum to 100% per exchange</span>}
                    {eaCat && (
                      <div className="ml-auto" onClick={e => e.stopPropagation()}>
                        {isEaEditAll ? (
                          <>
                            <button
                              onClick={() => saveEditAll(eaSources)}
                              disabled={editAllSaving}
                              className="bg-status-green-btn hover:bg-status-green-btn-hover disabled:opacity-50 text-text-bright px-2.5 py-1 rounded-btn-sm text-xs mr-1"
                            >{editAllSaving ? 'Saving...' : 'Save'}</button>
                            <button
                              onClick={cancelEditAll}
                              className="bg-surface-2 hover:bg-surface-hover text-text-bright px-2.5 py-1 rounded-btn-sm text-xs"
                            >Cancel</button>
                          </>
                        ) : (
                          <button
                            onClick={() => { startEditAll(eaCat.id, eaSources); if (!eaSourcesOpen) setEaSourcesOpen(true); }}
                            className="bg-status-blue-btn-hover hover:bg-status-blue-btn-hover text-text-bright px-2.5 py-1 rounded-btn-sm text-xs"
                          >Edit All</button>
                        )}
                      </div>
                    )}
                  </div>
                  {eaSourcesOpen && (
                    <div className="border-t border-border-base overflow-x-auto">
                      {eaSources.length === 0 ? (
                        <div className="px-3 py-3 text-sm text-text-secondary">
                          No sources added yet. Add sources via the Data Sources list below using the <span className="text-status-blue">'Exchange Announcements'</span> category.
                        </div>
                      ) : (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-text-muted border-b border-border-base text-xs uppercase">
                              <th className="text-left px-4 py-2">Name</th>
                              {EXCHANGES.map(ex => (
                                <th key={ex} className="text-center px-1 py-2 w-12">{EXCHANGE_DISPLAY[ex]}</th>
                              ))}
                              <th className="text-center px-2 py-2 w-24 hidden sm:table-cell">Last Pull</th>
                              <th className="text-center px-2 py-2 w-16 hidden sm:table-cell">Trust</th>
                              <th className="text-center px-2 py-2 w-16">Active</th>
                              <th className="text-right px-3 py-2 w-28">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {eaSources.map(s => {
                              const ew = isEaEditAll ? (editAllDraft[s.id] || {}) : (s.exchange_weights || {});
                              const tr = testResults[s.id];
                              return (
                                <tr key={s.id} className={`border-b border-border-base/50 hover:bg-surface-hover/30 ${!s.is_active ? 'opacity-50' : ''}`}>
                                  <td className="px-4 py-2.5" style={{minWidth:'160px',maxWidth:'220px'}}>
                                    <span className="font-medium text-sm truncate" title={s.name}>
                                      {s.name}{s.is_system ? ' *' : ''}
                                    </span>
                                    {s.description && <div className="text-xs text-text-muted mt-0.5 leading-snug truncate">{s.description}</div>}
                                  </td>
                                  {EXCHANGES.map(ex => (
                                    <td key={ex} className="px-1 py-2.5 text-center" style={{width:'60px',minWidth:'60px'}}>
                                      {isEaEditAll ? (
                                        <input
                                          type="number" min="0" max="100"
                                          value={editAllDraft[s.id]?.[ex] ?? 0}
                                          onChange={e => setEditAllDraft(prev => ({
                                            ...prev,
                                            [s.id]: { ...(prev[s.id] || {}), [ex]: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) },
                                          }))}
                                          className="w-12 bg-surface-2 border border-border-blue rounded px-1 py-0.5 text-xs text-center"
                                        />
                                      ) : (
                                        <span className={`text-xs font-mono ${(ew[ex] ?? 0) > 0 ? 'text-status-blue-muted' : 'text-text-muted'}`}>
                                          {(ew[ex] ?? 0) > 0 ? `${ew[ex]}%` : ''}
                                        </span>
                                      )}
                                    </td>
                                  ))}
                                  <td className="px-3 py-2.5 text-center text-xs text-text-muted hidden sm:table-cell">
                                    {formatPullTime(s.last_pull_at)}
                                  </td>
                                  <td className="px-3 py-2.5 text-center text-xs font-mono hidden sm:table-cell">
                                    {isEaEditAll ? (
                                      <input
                                        type="number"
                                        min="0" max="1" step="0.05"
                                        value={editAllTrust[s.id] ?? s.trust_score ?? 0.70}
                                        onChange={e => setEditAllTrust(prev => ({ ...prev, [s.id]: Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)) }))}
                                        className="w-14 bg-surface-2 border border-border-blue rounded px-1 py-0.5 text-xs text-center"
                                      />
                                    ) : (
                                      s.trust_score != null ? s.trust_score.toFixed(2) : '—'
                                    )}
                                  </td>
                                  <td className="px-3 py-2.5 text-center">
                                    {s.disabled_reason ? (
                                      <span className="text-xs text-status-amber" title={s.disabled_reason}>⚠</span>
                                    ) : (
                                      <Toggle active={s.is_active} onChange={() => toggleActive(s)} disabled={savingId === s.id} />
                                    )}
                                  </td>
                                  <td className="px-4 py-2.5 text-right">
                                    <div className="flex items-center gap-1.5 justify-end">
                                      <button
                                        onClick={() => testSource(s)}
                                        disabled={tr?.status === 'testing'}
                                        className="text-xs bg-accent hover:bg-accent-hover disabled:opacity-50 text-text-bright px-2.5 py-1 rounded-btn-sm"
                                        title="Test connection"
                                      >
                                        {tr?.status === 'testing' ? '…' : 'Test'}
                                      </button>
                                      {tr && tr.status !== 'testing' && (
                                        <span className={`text-xs ${tr.status === 'ok' ? 'text-status-green' : 'text-status-red'}`} title={tr.msg}>
                                          {tr.status === 'ok' ? '✅' : '❌'}
                                        </span>
                                      )}
                                      <button
                                        onClick={() => requestEdit(s)}
                                        className="text-xs bg-surface-2 hover:bg-surface-hover px-2.5 py-1 rounded-btn-sm"
                                      >Edit</button>
                                      {s.is_system ? (
                                        <button
                                          disabled
                                          title="System connector — cannot be deleted"
                                          className="text-xs bg-status-red-soft text-status-red px-2.5 py-1 rounded-btn-sm opacity-50 cursor-not-allowed"
                                        >Del</button>
                                      ) : (
                                        <button
                                          onClick={() => deleteSource(s)}
                                          className="text-xs bg-status-red-soft hover:bg-status-red-btn-hover text-text-bright px-2.5 py-1 rounded-btn-sm"
                                        >Del</button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {loading ? <div className="text-text-secondary text-sm py-4">Loading...</div> : (
        <div className="space-y-4">
          {/* Category weights sum warning banner */}
          {(() => {
            const catTotals: Record<string, number> = {};
            const wCats = categories.filter(c => !c.excluded_from_category_weights);
            for (const ex of EXCHANGES) {
              catTotals[ex] = wCats.reduce((sum, c) => sum + (c.exchange_weights[ex] ?? 0), 0);
            }
            const bad = EXCHANGES.filter(ex => catTotals[ex] > 0 && Math.abs(catTotals[ex] - 100) > 0.5);
            if (bad.length === 0) return null;
            return (
              <div className="bg-status-red-soft border border-border-red rounded-lg px-4 py-3 text-status-red-muted text-sm">
                ⚠ Category weights do not sum to 100% for: {bad.map(ex => `${EXCHANGE_DISPLAY[ex]} (${catTotals[ex]}%)`).join(', ')}. Fix in Category Weights below.
              </div>
            );
          })()}

          {/* 1F — Category Weights section at top */}
          {categories.length > 0 && (
            <CategoryWeightsSection token={token} categories={categories} onRefresh={load} />
          )}

          {/* Category sections */}
          {categories.map(cat => {
            if (cat.name === 'Exchange Announcements') return null;
            const catSources = filteredSources.filter(s => s.category_id === cat.id);
            if (catSources.length === 0) return null;
            const isCollapsed = collapsed.has(cat.id);
            const activeCount = catSources.filter(s => s.is_active).length;
            const isEditAll = editAllCatId === cat.id;

            // Per-exchange weight totals from sources within this category
            const srcTotals: Record<string, number> = {};
            for (const ex of EXCHANGES) {
              const total = catSources.reduce((sum, s) => sum + ((s.exchange_weights || {})[ex] ?? 0), 0);
              if (total > 0) srcTotals[ex] = total;
            }
            const srcExchanges = Object.keys(srcTotals);
            const anySrcWrong = srcExchanges.some(ex => Math.abs(srcTotals[ex] - 100) > 0.5);

            // Duplicate URL detection within this category
            const urlCount: Record<string, number> = {};
            for (const s of catSources) {
              const url = s.api_endpoint?.trim();
              if (url) urlCount[url] = (urlCount[url] || 0) + 1;
            }
            const isDupUrl = (s: DataSource) => {
              const url = s.api_endpoint?.trim();
              return !!(url && urlCount[url] > 1);
            };

            return (
              <div key={cat.id} className="bg-surface border border-border-base rounded-lg overflow-hidden">
                {/* Category header row */}
                <div className="flex items-center gap-2 px-4 py-3 select-none">
                  <span
                    className="text-text-secondary text-xs w-3 cursor-pointer"
                    onClick={() => !isEditAll && toggleCollapse(cat.id)}
                  >{isCollapsed ? '▶' : '▼'}</span>
                  <div
                    className="min-w-0 flex-1 cursor-pointer"
                    onClick={() => !isEditAll && toggleCollapse(cat.id)}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{cat.name}</span>
                      {srcExchanges.map(ex => {
                        const t = srcTotals[ex];
                        const ok = Math.abs(t - 100) < 0.5;
                        return (
                          <span key={ex} className={`text-xs font-mono ${ok ? 'text-status-green' : 'text-status-red'}`}>
                            {EXCHANGE_DISPLAY[ex]}: {t}%{ok ? ' ✓' : ' ⚠'}
                          </span>
                        );
                      })}
                      {anySrcWrong && <span className="text-status-red text-xs">⚠ Weights must sum to 100% per exchange</span>}
                    </div>
                    {cat.description && <div className="text-xs text-text-muted mt-0.5">{cat.description}</div>}
                  </div>
                  <div className="ml-auto flex items-center gap-2 text-xs shrink-0">
                    <span className="text-text-muted">{activeCount}/{catSources.length} active</span>
                    {isEditAll ? (
                      <>
                        <button
                          onClick={() => saveEditAll(catSources)}
                          disabled={editAllSaving}
                          className="bg-status-green-btn hover:bg-status-green-btn-hover disabled:opacity-50 text-text-bright px-2.5 py-1 rounded-btn-sm text-xs"
                        >{editAllSaving ? 'Saving...' : 'Save'}</button>
                        <button
                          onClick={cancelEditAll}
                          className="bg-surface-2 hover:bg-surface-hover text-text-bright px-2.5 py-1 rounded-btn-sm text-xs"
                        >Cancel</button>
                      </>
                    ) : (
                      <button
                        onClick={e => { e.stopPropagation(); startEditAll(cat.id, catSources); if (isCollapsed) toggleCollapse(cat.id); }}
                        className="bg-status-blue-btn-hover hover:bg-status-blue-btn-hover text-text-bright px-2.5 py-1 rounded-btn-sm text-xs"
                      >Edit All</button>
                    )}
                  </div>
                </div>

                {/* Sources table */}
                {!isCollapsed && (
                  <div className="border-t border-border-base overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-text-muted border-b border-border-base text-xs uppercase">
                          <th className="text-left px-4 py-2">Name</th>
                          {EXCHANGES.map(ex => (
                            <th key={ex} className="text-center px-1 py-2 w-12">{EXCHANGE_DISPLAY[ex]}</th>
                          ))}
                          <th className="text-center px-2 py-2 w-24 hidden sm:table-cell">Last Pull</th>
                          <th className="text-center px-2 py-2 w-16 hidden sm:table-cell">Trust</th>
                          <th className="text-center px-2 py-2 w-16">Active</th>
                          <th className="text-right px-3 py-2 w-28">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {catSources.map(s => {
                          const ew = isEditAll ? (editAllDraft[s.id] || {}) : (s.exchange_weights || {});
                          const tr = testResults[s.id];
                          const dupUrl = isDupUrl(s);
                          return (
                            <tr key={s.id} className={`border-b border-border-base/50 hover:bg-surface-hover/30 ${!s.is_active ? 'opacity-50' : ''}`}>
                              <td className="px-4 py-2.5" style={{minWidth:'160px',maxWidth:'220px'}}>
                                <div className="flex items-center gap-1 flex-wrap">
                                  <span className="font-medium text-sm truncate" title={s.name}>
                                    {s.name}{s.is_system ? ' *' : ''}
                                  </span>
                                </div>
                                {dupUrl && (
                                  <div className="mt-1 bg-status-red-soft border border-border-red rounded px-2 py-0.5 text-status-red-muted text-xs">
                                    ⚠ Duplicate URL
                                  </div>
                                )}
                                {s.description && <div className="text-xs text-text-muted mt-0.5 leading-snug truncate">{s.description}</div>}
                              </td>
                              {EXCHANGES.map(ex => (
                                <td key={ex} className="px-1 py-2.5 text-center" style={{width:'60px',minWidth:'60px'}}>
                                  {isEditAll ? (
                                    <input
                                      type="number" min="0" max="100"
                                      value={editAllDraft[s.id]?.[ex] ?? 0}
                                      onChange={e => setEditAllDraft(prev => ({
                                        ...prev,
                                        [s.id]: { ...(prev[s.id] || {}), [ex]: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) },
                                      }))}
                                      className="w-12 bg-surface-2 border border-border-blue rounded px-1 py-0.5 text-xs text-center"
                                    />
                                  ) : (
                                    <span className={`text-xs font-mono ${(ew[ex] ?? 0) > 0 ? 'text-status-blue-muted' : 'text-text-muted'}`}>
                                      {(ew[ex] ?? 0) > 0 ? `${ew[ex]}%` : ''}
                                    </span>
                                  )}
                                </td>
                              ))}
                              <td className="px-3 py-2.5 text-center text-xs text-text-muted hidden sm:table-cell">
                                {formatPullTime(s.last_pull_at)}
                              </td>
                              <td className="px-3 py-2.5 text-center text-xs font-mono hidden sm:table-cell">
                                {isEditAll ? (
                                  <input
                                    type="number"
                                    min="0" max="1" step="0.05"
                                    value={editAllTrust[s.id] ?? s.trust_score ?? 0.70}
                                    onChange={e => setEditAllTrust(prev => ({ ...prev, [s.id]: Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)) }))}
                                    className="w-14 bg-surface-2 border border-border-blue rounded px-1 py-0.5 text-xs text-center"
                                  />
                                ) : (
                                  s.trust_score != null ? s.trust_score.toFixed(2) : '—'
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-center">
                                {s.disabled_reason ? (
                                  <span className="text-xs text-status-amber" title={s.disabled_reason}>⚠</span>
                                ) : (
                                  <Toggle active={s.is_active} onChange={() => toggleActive(s)} disabled={savingId === s.id} />
                                )}
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <div className="flex items-center gap-1.5 justify-end">
                                  <button
                                    onClick={() => testSource(s)}
                                    disabled={tr?.status === 'testing'}
                                    className="text-xs bg-accent hover:bg-accent-hover disabled:opacity-50 text-text-bright px-2.5 py-1 rounded-btn-sm"
                                    title="Test connection"
                                  >
                                    {tr?.status === 'testing' ? '…' : 'Test'}
                                  </button>
                                  {tr && tr.status !== 'testing' && (
                                    <span className={`text-xs ${tr.status === 'ok' ? 'text-status-green' : 'text-status-red'}`}
                                      title={tr.msg}>
                                      {tr.status === 'ok' ? '✅' : '❌'}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => requestEdit(s)}
                                    className="text-xs bg-surface-2 hover:bg-surface-hover px-2.5 py-1 rounded-btn-sm"
                                  >Edit</button>
                                  {s.is_system ? (
                                    <button
                                      disabled
                                      title="System connector — cannot be deleted"
                                      className="text-xs bg-status-red-soft text-status-red px-2.5 py-1 rounded-btn-sm opacity-50 cursor-not-allowed"
                                    >Del</button>
                                  ) : (
                                    <button
                                      onClick={() => deleteSource(s)}
                                      className="text-xs bg-status-red-soft hover:bg-status-red-btn-hover text-text-bright px-2.5 py-1 rounded-btn-sm"
                                    >Del</button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {/* Uncategorised sources */}
          {(() => {
            const uncats = filteredSources.filter(s => !s.category_id);
            if (uncats.length === 0) return null;
            return (
              <div className="bg-surface border border-border-base rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-border-base">
                  <span className="font-medium text-sm text-text-secondary">Uncategorised</span>
                  <span className="text-xs text-text-muted ml-2">{uncats.length} sources</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      {uncats.map(s => {
                        const tr = testResults[s.id];
                        return (
                          <tr key={s.id} className={`border-b border-border-base/50 hover:bg-surface-hover/30 ${!s.is_active ? 'opacity-50' : ''}`}>
                            <td className="px-4 py-2.5">
                              <div className="font-medium">{s.name}</div>
                              {s.description && <div className="text-xs text-text-muted">{s.description}</div>}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <Toggle active={s.is_active} onChange={() => toggleActive(s)} />
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <div className="flex items-center gap-1.5 justify-end">
                                <button onClick={() => testSource(s)}
                                  disabled={tr?.status === 'testing'}
                                  className="text-xs bg-accent hover:bg-accent-hover disabled:opacity-50 text-text-bright px-2.5 py-1 rounded-btn-sm">
                                  {tr?.status === 'testing' ? '…' : 'Test'}
                                </button>
                                {tr && tr.status !== 'testing' && (
                                  <span className={`text-xs ${tr.status === 'ok' ? 'text-status-green' : 'text-status-red'}`} title={tr.msg}>
                                    {tr.status === 'ok' ? '✅' : '❌'}
                                  </span>
                                )}
                                <button onClick={() => requestEdit(s)}
                                  className="text-xs bg-surface-2 hover:bg-surface-hover px-2.5 py-1 rounded-btn-sm">Edit</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* System connector footnote */}
      {sources.some(s => s.is_system) && (
        <p className="text-xs text-text-muted">* System connector — managed automatically</p>
      )}

      {/* Edit / Add panel */}
      {(editingSource || showAddModal) && (
        <SourceModal
          source={editingSource ?? blankSource}
          token={token}
          categories={categories}
          onSave={async () => {
            await load();
            setEditingSource(null);
            setShowAddModal(false);
            setSaveMsg('Saved');
            setTimeout(() => setSaveMsg(''), 2500);
          }}
          onClose={() => { setEditingSource(null); setShowAddModal(false); }}
        />
      )}
    </div>
  );
}

function InlineWeightEdit({ value, onSave, saving }: { value: number; onSave: (v: number) => void; saving: boolean }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));

  useEffect(() => { setDraft(String(value)); }, [value]);

  if (editing) {
    return (
      <input
        type="number" min="0" max="100" value={draft} autoFocus
        onChange={e => setDraft(e.target.value)}
        onBlur={() => { onSave(Math.max(0, Math.min(100, parseInt(draft) || 0))); setEditing(false); }}
        onKeyDown={e => { if (e.key === 'Enter') { onSave(Math.max(0, Math.min(100, parseInt(draft) || 0))); setEditing(false); } if (e.key === 'Escape') setEditing(false); }}
        className="w-14 bg-surface-2 border border-border-blue rounded px-1 py-0.5 text-sm text-right"
      />
    );
  }
  return (
    <button onClick={() => setEditing(true)} title="Click to edit"
      className={`text-sm hover:text-status-blue cursor-pointer ${saving ? 'text-status-amber' : ''}`}>
      {value}%
    </button>
  );
}


const AUTH_TYPE_HELPER: Record<string, string> = {
  apikey: 'Enter the endpoint URL and your API key. Both fields are required.',
  rss: 'Enter the RSS or Atom feed URL. No authentication required.',
  podcast_transcript: 'Enter the podcast RSS feed URL. The bot will extract transcripts from episode show notes.',
  podcast_audio: 'Enter the podcast RSS feed URL. The bot will process audio episodes.',
  email_subscription: 'Enter the Gmail label name the bot should monitor for this subscription. Set up a Gmail filter rule to apply this label to emails from this sender.',
  open_access: 'Enter the endpoint URL. This source is publicly accessible with no authentication.',
  none: 'No connection details required for this source.',
  custom: 'Enter the endpoint URL. Configure authentication manually in the backend connector.',
  asx_announcement: 'Managed connector — polls the Markit Digital API every 5 minutes during market hours. No URL or API key required. Configure via the Connector Configuration section.',
};

function SourceModal({ source, token, categories, onSave, onClose }: {
  source: DataSource;
  token: string;
  categories: DataCategory[];
  onSave: () => void;
  onClose: () => void;
}) {
  const initWeights = Object.fromEntries(
    EXCHANGES.map(ex => [ex, source.exchange_weights?.[ex] ?? 0])
  );
  const [fields, setFields] = useState<any>({ ...source, exchange_weights: initWeights, gmail_label: (source as any).gmail_label || '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const set = (k: string, v: any) => setFields((f: any) => ({ ...f, [k]: v }));
  const setEW = (ex: string, val: number) => setFields((f: any) => ({
    ...f,
    exchange_weights: { ...(f.exchange_weights || {}), [ex]: Math.max(0, Math.min(100, isNaN(val) ? 0 : val)) },
  }));

  const handleAuthTypeChange = (newType: string) => {
    const noApiKey = !['apikey', 'custom'].includes(newType);
    setFields((f: any) => ({ ...f, auth_type: newType, api_key: noApiKey ? '' : f.api_key }));
  };

  const isNew = source.id === -1;
  const isEmailSub = fields.auth_type === 'email_subscription';
  const isAsxAnnouncement = fields.auth_type === 'asx_announcement';
  const showUrl = !['email_subscription', 'none', 'asx_announcement'].includes(fields.auth_type);
  const showApiKey = ['apikey', 'custom', 'API Key'].includes(fields.auth_type);
  const showRateLimit = ['apikey', 'custom', 'open_access', 'API Key'].includes(fields.auth_type);
  const urlLocked = !!fields.is_system;

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!fields.name?.trim()) errs.name = 'Name is required.';
    if (!fields.auth_type) errs.auth_type = 'Type is required.';
    if (isNew && !fields.category_id) errs.category_id = 'Category is required for new sources.';
    if (showUrl && !fields.api_endpoint?.trim()) errs.api_endpoint = 'URL is required.';
    if (isEmailSub && !fields.gmail_label?.trim()) errs.gmail_label = 'Gmail Label is required.';
    if (!fields.refresh_label) errs.refresh_label = 'Refresh Rate is required.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setErr('');
    try {
      if (isNew) {
        const res = await apiCall('/admin/data-sources', token, {
          method: 'POST',
          body: JSON.stringify({ ...fields }),
        });
        if (!res.ok) { const d = await res.json(); setErr(d.detail || 'Save failed'); setSaving(false); return; }
      } else {
        const patch: any = {
          name: fields.name,
          is_active: fields.is_active,
          weight: fields.weight,
          refresh_label: fields.refresh_label,
          api_key: fields.api_key,
          category_id: fields.category_id,
          description: fields.description,
          auth_type: fields.auth_type,
          gmail_label: fields.gmail_label,
          trust_score: fields.trust_score,
        };
        if (!urlLocked) patch.api_endpoint = fields.api_endpoint;
        if (fields.api_secret) patch.api_secret = fields.api_secret;
        if (showRateLimit) patch.rate_limit_per_min = fields.rate_limit_per_min ?? null;
        const res = await apiCall(`/admin/data-sources/${source.id}`, token, {
          method: 'PUT', body: JSON.stringify(patch),
        });
        if (!res.ok) { const d = await res.json(); setErr(d.detail || 'Save failed'); setSaving(false); return; }
        await apiCall(`/admin/data-sources/${source.id}/exchange-weights`, token, {
          method: 'PUT', body: JSON.stringify(fields.exchange_weights),
        });
      }
      onSave();
    } catch (e: any) { setErr(e.message); setSaving(false); }
  };

  const handleDelete = async () => {
    if (source.is_system) return;
    if (!window.confirm(`Delete "${source.name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const res = await apiCall(`/admin/data-sources/${source.id}`, token, { method: 'DELETE' });
      if (!res.ok) {
        const d = await res.json();
        setErr(d.detail || 'Delete failed');
        setSaving(false);
        return;
      }
      onSave();
    } catch (e: any) { setErr(e.message); setSaving(false); }
  };

  const fe = fieldErrors;

  return (
    <div className="fixed inset-0 bg-surface-60 flex items-center justify-center z-50 p-4">
      <div className="bg-surface border border-border-base rounded-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">{isNew ? 'Add Data Source' : `Edit: ${source.name}`}</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-bright text-xl leading-none">×</button>
        </div>

        {source.is_system && (
          <div className="bg-surface-2 border border-border-light rounded px-3 py-2 text-xs text-text-primary">
            System connector — name, URL and auth managed by code.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {/* Name */}
          <div className="col-span-2">
            <label className="text-text-secondary text-xs">Name</label>
            <input value={fields.name} onChange={e => set('name', e.target.value)}
              readOnly={!isNew && !!fields.is_system}
              className={`w-full border rounded px-3 py-1.5 text-sm mt-1 ${fe.name ? 'border-border-red' : ''} ${!isNew && fields.is_system ? 'bg-bg-deep border-border-base text-text-muted cursor-not-allowed' : 'bg-surface-2 border-border-light'}`} />
            {fe.name && <p className="text-status-red text-xs mt-0.5">{fe.name}</p>}
          </div>
          <div className="col-span-2">
            <label className="text-text-secondary text-xs">Description</label>
            <input value={fields.description || ''} onChange={e => set('description', e.target.value)}
              className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm mt-1"
              placeholder="Brief description of this source..." />
          </div>

          {/* Category */}
          <div className="col-span-2">
            <label className="text-text-secondary text-xs">Category{isNew ? ' *' : ''}</label>
            <select value={fields.category_id ?? ''} onChange={e => set('category_id', e.target.value ? parseInt(e.target.value) : null)}
              className={`w-full bg-surface-2 border rounded px-3 py-1.5 text-sm mt-1 ${fe.category_id ? 'border-border-red' : 'border-border-light'}`}>
              <option value="">— Uncategorised —</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {fe.category_id && <p className="text-status-red text-xs mt-0.5">{fe.category_id}</p>}
          </div>

          {/* Type (auth_type) — shown for both Add and Edit */}
          <div className="col-span-2">
            <label className="text-text-secondary text-xs">Type</label>
            <select value={fields.auth_type} onChange={e => handleAuthTypeChange(e.target.value)}
              className={`w-full bg-surface-2 border rounded px-3 py-1.5 text-sm mt-1 ${fe.auth_type ? 'border-border-red' : 'border-border-light'}`}>
              <option value="apikey">API Key</option>
              <option value="custom">Custom</option>
              <option value="open_access">Open Access</option>
              <option value="rss">RSS</option>
              <option value="podcast_transcript">Podcast - Transcript</option>
              <option value="podcast_audio">Podcast - Audio</option>
              <option value="email_subscription">Email Subscription</option>
              <option value="asx_announcement">ASX Announcement Connector</option>
              <option value="none">None</option>
            </select>
            {fe.auth_type && <p className="text-status-red text-xs mt-0.5">{fe.auth_type}</p>}
            {AUTH_TYPE_HELPER[fields.auth_type] && (
              <p className="text-text-muted text-xs mt-1">{AUTH_TYPE_HELPER[fields.auth_type]}</p>
            )}
          </div>

          {/* URL — hidden for email_subscription and none */}
          {showUrl && (
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1">
                <label className="text-text-secondary text-xs">URL / Endpoint</label>
                {urlLocked && <span className="text-xs text-status-amber italic">System — cannot change</span>}
              </div>
              <input value={fields.api_endpoint || ''} onChange={e => !urlLocked && set('api_endpoint', e.target.value)}
                readOnly={urlLocked}
                className={`w-full border rounded px-3 py-1.5 text-sm font-mono text-xs ${fe.api_endpoint ? 'border-border-red' : ''} ${urlLocked ? 'bg-bg-deep border-border-base text-text-muted cursor-not-allowed' : 'bg-surface-2 border-border-light'}`} />
              {fe.api_endpoint && <p className="text-status-red text-xs mt-0.5">{fe.api_endpoint}</p>}
            </div>
          )}

          {/* Gmail Label — only for email_subscription */}
          {isEmailSub && (
            <div className="col-span-2">
              <label className="text-text-secondary text-xs">Gmail Label</label>
              <input value={fields.gmail_label || ''} onChange={e => set('gmail_label', e.target.value)}
                placeholder="TradingBot/AU/MarcusToday"
                className={`w-full border rounded px-3 py-1.5 text-sm mt-1 ${fe.gmail_label ? 'border-border-red' : 'border-border-light'} bg-surface-2`} />
              {fe.gmail_label && <p className="text-status-red text-xs mt-0.5">{fe.gmail_label}</p>}
            </div>
          )}

          {/* ASX Announcement Connector info panel */}
          {isAsxAnnouncement && (
            <div className="col-span-2 bg-status-blue-soft border border-border-blue rounded p-3 text-xs text-status-blue-muted space-y-1">
              <p className="font-semibold text-status-blue-muted">Managed Connector — No credentials required</p>
              <p>This connector polls the Markit Digital ASX API every 5 minutes during market hours (09:45–16:15 AEST). PDF text is automatically extracted and classified via Haiku.</p>
              <p className="text-status-blue">Configure polling behaviour via the <span className="font-semibold">Connector Configuration</span> section in the Exchange Announcements card.</p>
            </div>
          )}

          {/* API Key — only for apikey and custom types */}
          {showApiKey && (
            <div className="col-span-2">
              <label className="text-text-secondary text-xs">API Key</label>
              <input value={fields.api_key || ''} onChange={e => set('api_key', e.target.value)}
                type="password"
                placeholder="Enter API key..."
                className="w-full border rounded px-3 py-1.5 text-sm bg-surface-2 border-border-light" />
            </div>
          )}

          {/* API Secret — only for apikey and custom types, never pre-populated */}
          {showApiKey && (
            <div className="col-span-2">
              <label className="text-text-secondary text-xs">API Secret</label>
              <input
                type="password"
                placeholder="Enter new secret (leave blank to keep existing)"
                onChange={e => set('api_secret', e.target.value)}
                className="w-full border rounded px-3 py-1.5 text-sm bg-surface-2 border-border-light" />
            </div>
          )}

          {/* Rate Limit — only for apikey, custom, open_access */}
          {showRateLimit && (
            <div>
              <label className="text-text-secondary text-xs">Rate Limit (per min)</label>
              <input
                type="number"
                value={fields.rate_limit_per_min ?? ''}
                onChange={e => set('rate_limit_per_min', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="e.g. 60"
                className="w-full border rounded px-3 py-1.5 text-sm bg-surface-2 border-border-light mt-1" />
            </div>
          )}

          {/* Refresh Rate */}
          <div>
            <label className="text-text-secondary text-xs">Refresh Rate</label>
            <select value={fields.refresh_label} onChange={e => set('refresh_label', e.target.value)}
              className={`w-full bg-surface-2 border rounded px-3 py-1.5 text-sm mt-1 ${fe.refresh_label ? 'border-border-red' : 'border-border-light'}`}>
              {['5 min', '15 min', '30 min', '1 hour', '6 hours', '24 hours'].map(t => <option key={t}>{t}</option>)}
            </select>
            {fe.refresh_label && <p className="text-status-red text-xs mt-0.5">{fe.refresh_label}</p>}
          </div>
          <div>
            <label className="text-text-secondary text-xs">Active</label>
            <div className="mt-2 flex items-center gap-2">
              <Toggle active={fields.is_active} onChange={() => set('is_active', !fields.is_active)} />
              <span className="text-xs text-text-secondary">{fields.is_active ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>

          {/* Trust Score */}
          <div>
            <label className="text-text-secondary text-xs">Trust Score</label>
            <input
              type="number" min="0" max="1" step="0.01"
              value={fields.trust_score ?? (fields.auth_type === 'asx_announcement' ? 1.00 : 0.70)}
              onChange={e => set('trust_score', parseFloat(e.target.value) || 0)}
              className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm mt-1"
            />
          </div>

          {/* Exchange weights */}
          <div className="col-span-2">
            <label className="text-text-secondary text-xs block mb-2">Exchange Weights % (source contribution within category)</label>
            <div className="grid grid-cols-3 gap-2">
              {EXCHANGES.map(ex => (
                <div key={ex}>
                  <label className="text-xs text-text-muted">{EXCHANGE_DISPLAY[ex]}</label>
                  <input type="number" min="0" max="100"
                    value={(fields.exchange_weights as Record<string, number>)[ex] ?? 0}
                    onChange={e => setEW(ex, parseInt(e.target.value) || 0)}
                    className="w-full bg-surface-2 border border-border-light rounded px-2 py-1 text-sm mt-0.5" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {err && <div className="text-status-red text-xs">{err}</div>}

        <div className="flex gap-2 pt-2 border-t border-border-base">
          <button onClick={handleSave} disabled={saving}
            className="flex-1 bg-status-green-btn hover:bg-status-green-btn-hover disabled:opacity-50 text-text-bright py-2 rounded-btn text-sm font-medium">
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button onClick={onClose}
            className="bg-surface-2 hover:bg-surface-hover text-text-bright px-4 py-1.5 rounded-btn text-sm">
            Cancel
          </button>
        </div>

        {/* Delete removed from UI — manage via DB only */}
      </div>
    </div>
  );
}

// ─── Tab: Signals & Fundamentals ──────────────────────────────────────────────

interface SignalDef {
  id: number;
  name: string;
  category: string;
  description: string;
  enabled: boolean;
  weight: number;
  threshold_buy: number;
  threshold_sell: number;
}

interface FundamentalDef {
  id: number;
  name: string;
  description: string;
  enabled: boolean;
  weight: number;
  good_above: number | null;
  good_below: number | null;
  scoring_direction: string;
  info_text: string;
}

const SIGNAL_INFO: Record<string, string> = {
  'P/E vs Sector Average': 'Compares the stock\'s P/E ratio to its sector median. A lower P/E vs peers suggests undervaluation.',
  'Debt to Equity': 'Measures financial leverage (total debt ÷ shareholders\' equity). Lower = safer. Healthy: D/E < 1.0.',
  'MA Crossover 50/200': '"Golden Cross" — 50-day MA crossing above 200-day MA signals a confirmed uptrend.',
  'RSI Oversold/Overbought': 'Relative Strength Index (0–100) measures momentum. RSI < 30 = oversold; RSI > 70 = overbought.',
  'Earnings Surprise': 'Compares actual EPS to analyst estimates. Positive surprises > 5% are strong BUY catalysts.',
  'News Sentiment': 'NLP-scored average sentiment from news articles (past 24–48h).',
  'AUD/USD Impact': 'Monitors AUD/USD exchange rate movement. Affects ASX stocks differently.',
  'Volume Surge': 'Detects unusual trading volume vs 20-day average.',
  'Bollinger Band Breakout': 'Measures price relative to ±2 standard deviation bands around the 20-day MA.',
  'MACD Signal': 'Moving Average Convergence Divergence tracks two EMAs (12 and 26-day).',
  'Price/Book Ratio': 'Market cap ÷ book value. P/B < 1 means the market values the company below its net assets.',
  'Revenue Growth': 'Year-over-year revenue growth.',
  'Free Cash Flow Yield': 'Free cash flow ÷ market cap.',
  'Dividend Yield': 'Annual dividends ÷ share price.',
  'ROE Trend': 'Tracks Return on Equity over 4+ quarters.',
  'Sector Rotation': 'Macro-driven signal tracking capital flows between sectors.',
  'Earnings Growth Rate': 'Multi-quarter EPS growth trend.',
};

const FUND_INFO: Record<string, string> = {
  'P/E Ratio': 'Price ÷ Earnings Per Share. How much investors pay per $1 of earnings.',
  'Debt/Equity': 'Total debt ÷ shareholders equity. Financial leverage risk.',
  'D/E Ratio': 'Total debt ÷ shareholders equity. Financial leverage risk.',
  'ROE': 'Net income ÷ shareholders equity. How efficiently management uses equity capital.',
  'Revenue Growth': 'Year-over-year revenue growth %.',
  'Earnings Growth': 'Year-over-year EPS growth.',
  'Gross Margin': 'Gross profit ÷ revenue. Pricing power and product economics.',
  'Current Ratio': 'Current assets ÷ current liabilities. Short-term liquidity.',
  'Price/Book': 'Market cap ÷ book value.',
  'P/B Ratio': 'Market cap ÷ book value.',
  'Free Cash Flow': 'Operating cash flow minus capex.',
  'EV/EBITDA': 'Enterprise value ÷ EBITDA. Acquisition value multiple.',
  'Dividend Yield': 'Annual dividend ÷ share price.',
  'Market Cap': 'Total market capitalisation.',
};

const CAT_COLORS: Record<string, string> = {
  Momentum: 'bg-status-amber-soft text-status-amber-muted',
  Sentiment: 'bg-status-green-soft text-status-green-muted',
  Valuation: 'bg-status-blue-soft text-status-blue-muted',
  Quality: 'bg-accent-soft text-text-secondary',
  Macro: 'bg-status-red-soft text-status-red-muted',
};

// Inline editable number field for good_above / good_below
function InlineNumberEdit({ value, onSave, placeholder = '—' }: {
  value: number | null;
  onSave: (v: number | null) => void;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value !== null ? String(value) : '');

  useEffect(() => { setDraft(value !== null ? String(value) : ''); }, [value]);

  const commit = () => {
    const trimmed = draft.trim();
    onSave(trimmed === '' ? null : parseFloat(trimmed));
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        type="number" value={draft} autoFocus
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        className="w-20 bg-surface-2 border border-border-blue rounded px-2 py-0.5 text-sm text-right"
      />
    );
  }
  return (
    <button onClick={() => setEditing(true)} title="Click to edit"
      className="text-sm hover:text-status-blue cursor-pointer min-w-[48px] text-right">
      {value !== null ? value : <span className="text-text-muted">{placeholder}</span>}
    </button>
  );
}

// Tooltip that positions upward when near bottom of viewport
function InfoTooltip({ id, name, text, tooltipId, setTooltip }: {
  id: string;
  name: string;
  text: string;
  tooltipId: string | null;
  setTooltip: (v: { id: string; text: string; name: string; upward: boolean } | null) => void;
}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const isNearBottom = () => {
    if (!btnRef.current) return false;
    const rect = btnRef.current.getBoundingClientRect();
    return rect.top > window.innerHeight * 0.67;
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onMouseEnter={() => setTooltip({ id, text, name, upward: isNearBottom() })}
        onMouseLeave={() => setTooltip(null)}
        className="w-5 h-5 rounded-full bg-surface-2 hover:bg-status-blue-btn-hover text-text-primary hover:text-text-bright text-xs flex items-center justify-center transition-colors"
      >?</button>
      {tooltipId === id && (
        <div className="absolute right-6 bottom-full mb-1 bg-bg-deep border border-border-light rounded-lg p-3 text-xs w-72 z-20 shadow-xl">
          <p className="font-semibold text-text-bright mb-1">{name}</p>
          <p className="text-text-primary leading-relaxed">{text}</p>
        </div>
      )}
    </div>
  );
}

function SignalsTab({ token }: { token: string }) {
  const [signals, setSignals] = useState<SignalDef[]>([]);
  const [fundamentals, setFundamentals] = useState<FundamentalDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveMsg, setSaveMsg] = useState('');
  const [tooltip, setTooltip] = useState<{ id: string; text: string; name: string; upward: boolean } | null>(null);
  // 2B — collapsible state for both sections
  const [sigCollapsed, setSigCollapsed] = useState(false);
  const [fundCollapsed, setFundCollapsed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [r1, r2] = await Promise.all([
          apiCall('/admin/signals-definition', token),
          apiCall('/admin/fundamentals-definition', token),
        ]);
        if (r1.ok) { const d = await r1.json(); setSignals(d.signals || []); }
        if (r2.ok) { const d = await r2.json(); setFundamentals(d.fundamentals || []); }
      } catch {}
      setLoading(false);
    };
    load();
  }, [token]);

  const saveSignals = async (updated: SignalDef[]) => {
    try {
      await apiCall('/admin/signals-definition/bulk', token, {
        method: 'POST',
        body: JSON.stringify({ signals: updated }),
      });
      setSaveMsg('Saved'); setTimeout(() => setSaveMsg(''), 2000);
    } catch {}
  };

  const saveFundamentals = async (updated: FundamentalDef[]) => {
    try {
      await apiCall('/admin/fundamentals-definition/bulk', token, {
        method: 'POST',
        body: JSON.stringify({ fundamentals: updated }),
      });
      setSaveMsg('Saved'); setTimeout(() => setSaveMsg(''), 2000);
    } catch {}
  };

  const updateSignal = (id: number, changes: Partial<SignalDef>) => {
    const updated = signals.map(s => s.id === id ? { ...s, ...changes } : s);
    setSignals(updated);
    saveSignals(updated);
  };

  const updateFundamental = (id: number, changes: Partial<FundamentalDef>) => {
    const updated = fundamentals.map(f => f.id === id ? { ...f, ...changes } : f);
    setFundamentals(updated);
    saveFundamentals(updated);
  };

  const enabledSignalWeight = signals.filter(s => s.enabled).reduce((a, s) => a + s.weight, 0);
  const sigWeightOk = Math.abs(enabledSignalWeight - 100) < 0.5 || signals.filter(s => s.enabled).length === 0;
  const enabledFundWeight = fundamentals.filter(f => f.enabled).reduce((a, f) => a + f.weight, 0);
  const fundWeightOk = Math.abs(enabledFundWeight - 100) < 0.5 || fundamentals.filter(f => f.enabled).length === 0;

  if (loading) return <div className="text-text-secondary text-sm">Loading...</div>;

  return (
    <div className="space-y-6">
      {/* 2C — Trading Signals section with standardised heading inside container */}
      <div className="bg-surface rounded-lg border border-border-base overflow-hidden">
        {/* Heading row with collapse toggle + inline weight messages */}
        <div
          className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-surface-hover/30 select-none border-b border-border-base"
          onClick={() => setSigCollapsed(c => !c)}
        >
          <span className="text-text-secondary text-xs w-3">{sigCollapsed ? '▶' : '▼'}</span>
          <h2 className="font-semibold text-sm">Trading Signals</h2>
          {/* 2A/2D — weight message immediately after heading */}
          {signals.length > 0 && (
            <span className={`text-xs font-mono ml-2 ${sigWeightOk ? 'text-status-green' : 'text-status-red'}`}>
              Active weight: {enabledSignalWeight.toFixed(0)}%{sigWeightOk ? ' ✓' : ' ⚠'}
            </span>
          )}
          {!sigWeightOk && signals.length > 0 && (
            <span className="text-status-red text-xs">⚠ Weights must equal 100%</span>
          )}
          <div className="ml-auto">
            {saveMsg && <span className="text-status-green text-xs">{saveMsg}</span>}
          </div>
        </div>

        {!sigCollapsed && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary border-b border-border-base text-xs uppercase">
                  <th className="text-left p-3">Signal</th>
                  <th className="text-left p-3">Category</th>
                  <th className="text-right p-3">Weight</th>
                  <th className="text-center p-3">Active</th>
                  <th className="p-3 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {signals.map(s => (
                  <tr key={s.id} className={`border-b border-border-base ${!s.enabled ? 'opacity-50' : ''}`}>
                    <td className="p-3">
                      <div className="font-medium">{s.name}</div>
                      <div className="text-text-muted text-xs">{s.description}</div>
                    </td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${CAT_COLORS[s.category] || 'bg-surface-2 text-text-primary'}`}>
                        {s.category}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <input type="number" min="0" max="100" value={s.weight}
                        onChange={e => updateSignal(s.id, { weight: parseFloat(e.target.value) || 0 })}
                        className="w-16 bg-surface-2 border border-border-light rounded px-2 py-0.5 text-sm text-right" />
                    </td>
                    <td className="p-3 text-center">
                      <Toggle active={s.enabled} onChange={() => updateSignal(s.id, { enabled: !s.enabled })} />
                    </td>
                    <td className="p-3">
                      <InfoTooltip
                        id={`s${s.id}`}
                        name={s.name}
                        text={SIGNAL_INFO[s.name] || s.description}
                        tooltipId={tooltip?.id || null}
                        setTooltip={setTooltip}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 2C — Fundamentals Scoring section with standardised heading inside container */}
      <div className="bg-surface rounded-lg border border-border-base overflow-hidden">
        {/* Heading row with collapse toggle + inline weight messages */}
        <div
          className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-surface-hover/30 select-none border-b border-border-base"
          onClick={() => setFundCollapsed(c => !c)}
        >
          <span className="text-text-secondary text-xs w-3">{fundCollapsed ? '▶' : '▼'}</span>
          <h2 className="font-semibold text-sm">Fundamentals Scoring</h2>
          {/* 2A/2D — weight message immediately after heading */}
          {fundamentals.length > 0 && (
            <span className={`text-xs font-mono ml-2 ${fundWeightOk ? 'text-status-green' : 'text-status-red'}`}>
              Active weight: {enabledFundWeight.toFixed(0)}%{fundWeightOk ? ' ✓' : ' ⚠'}
            </span>
          )}
          {!fundWeightOk && fundamentals.length > 0 && (
            <span className="text-status-red text-xs">⚠ Weights must equal 100%</span>
          )}
        </div>

        {!fundCollapsed && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary border-b border-border-base text-xs uppercase">
                  <th className="text-left p-3">Metric</th>
                  <th className="text-left p-3">Direction</th>
                  <th className="text-right p-3">Weight</th>
                  <th className="text-right p-3">
                    <span title="Score improves as value rises above this threshold">Good Above ⓘ</span>
                  </th>
                  <th className="text-right p-3">
                    <span title="Score improves as value falls below this threshold">Good Below ⓘ</span>
                  </th>
                  <th className="text-center p-3">Active</th>
                  <th className="p-3 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {fundamentals.map(f => {
                  const higherBetter = f.scoring_direction === 'higher_better';
                  return (
                    <tr key={f.id} className={`border-b border-border-base ${!f.enabled ? 'opacity-50' : ''}`}>
                      <td className="p-3">
                        <div className="font-medium">{f.name}</div>
                        <div className="text-text-muted text-xs">{f.description}</div>
                      </td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          higherBetter ? 'bg-status-green-soft text-status-green-muted' : 'bg-status-blue-soft text-status-blue-muted'
                        }`}>
                          {higherBetter ? '↑ Higher' : '↓ Lower'}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <input type="number" min="0" max="100" value={f.weight}
                          onChange={e => updateFundamental(f.id, { weight: parseFloat(e.target.value) || 0 })}
                          className="w-16 bg-surface-2 border border-border-light rounded px-2 py-0.5 text-sm text-right" />
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex flex-col items-end gap-0.5">
                          <InlineNumberEdit
                            value={f.good_above}
                            onSave={v => updateFundamental(f.id, { good_above: v })}
                          />
                          <span className="text-xs text-text-muted">
                            {higherBetter ? 'Score 10 above this' : 'Score 0 above this'}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex flex-col items-end gap-0.5">
                          <InlineNumberEdit
                            value={f.good_below}
                            onSave={v => updateFundamental(f.id, { good_below: v })}
                          />
                          <span className="text-xs text-text-muted">
                            {higherBetter ? 'Score 0 below this' : 'Score 10 below this'}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <Toggle active={f.enabled} onChange={() => updateFundamental(f.id, { enabled: !f.enabled })} />
                      </td>
                      <td className="p-3">
                        <InfoTooltip
                          id={`f${f.id}`}
                          name={f.name}
                          text={FUND_INFO[f.name] || f.info_text || f.description}
                          tooltipId={tooltip?.id || null}
                          setTooltip={setTooltip}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Strategy & Risk ─────────────────────────────────────────────────────

interface StrategySettings {
  minConfidence: number;
  maxPositionSize: number;
  maxSectorAllocation: number;
  stopLoss: number;
  takeProfit: number;
  cashReserveGuard: number;
  maxPositions: number;
  maxAsxPositions: number;
  maxUsPositions: number;
  tradingMode: string;
  asx: boolean;
  us: boolean;
}

const defaultStrategy: StrategySettings = {
  minConfidence: 7, maxPositionSize: 10, maxSectorAllocation: 30,
  stopLoss: 8, takeProfit: 25, cashReserveGuard: 10, maxPositions: 10,
  maxAsxPositions: 5, maxUsPositions: 5, tradingMode: 'paper', asx: true, us: true,
};

interface Exchange {
  code: string;
  name: string;
  country: string;
  region: string;
  is_active: boolean;
  connector_stub: string;
  updated_at?: string;
}

const REGION_FLAGS: Record<string, string> = {
  AU: '🇦🇺', US: '🇺🇸', GB: '🇬🇧', EU: '🇪🇺', HK: '🇭🇰',
  JP: '🇯🇵', CN: '🇨🇳', SG: '🇸🇬', CA: '🇨🇦',
};

interface BrokerConfig {
  id: number;
  broker_name: string;
  mode: string;
  username: string | null;
  account_id: string | null;
  client_portal_url: string | null;
  data_url: string | null;
  api_key_id: string | null;
  api_secret_masked: string | null;
  app_key: string | null;
  notes: string | null;
  is_active: boolean;
  updated_at?: string;
  // Tiger-specific fields
  tiger_id: string | null;
  private_key_path: string | null;
  license: string | null;
  env: string | null;
  live_account_id: string | null;
  demo_account_id: string | null;
}

interface BrokerHealth {
  broker_name: string;
  status: string;
  last_checked_at: string | null;
  last_connected_at: string | null;
  minutes_since_check: number | null;
  error_message: string | null;
}

interface ExchangeWithMode {
  code: string;
  name: string;
  country: string;
  region: string;
  is_active: boolean;
  connector_stub: string;
  trading_mode: string;
  updated_at?: string;
}

type TradingMode = 'live' | 'paper' | 'inactive';

interface PaperBrokerStatus {
  broker_config_id: number | null;
  is_active: boolean;
  cash: number;
  account_updated_at: string | null;
  open_orders_count: number;
}

function BrokerExchangeManagementTab({ token }: { token: string }) {
  const [configs, setConfigs] = useState<BrokerConfig[]>([]);
  const [exchanges, setExchanges] = useState<ExchangeWithMode[]>([]);
  const [brokerHealth, setBrokerHealth] = useState<BrokerHealth[]>([]);
  const [savingBroker, setSavingBroker] = useState<number | null>(null);
  const [savingExch, setSavingExch] = useState<string | null>(null);
  const [editingBroker, setEditingBroker] = useState<number | null>(null);
  const [brokerEdits, setBrokerEdits] = useState<Record<string, any>>({});
  const [brokerMsg, setBrokerMsg] = useState('');

  // Paper broker state (ASX internal paper broker)
  const [paperStatus, setPaperStatus] = useState<PaperBrokerStatus | null>(null);
  const [togglingPaper, setTogglingPaper] = useState(false);
  const [paperMsg, setPaperMsg] = useState('');

  // Reset Paper Portfolios (migrated from standalone ResetPortfolioSection, Session 9)
  const [botMode, setBotMode] = useState<string>('unknown');
  const [resettingAsx, setResettingAsx] = useState(false);
  const [resettingUs, setResettingUs] = useState(false);
  const [resetAsxMsg, setResetAsxMsg] = useState('');
  const [resetUsMsg, setResetUsMsg] = useState('');

  // US Paper Account (Alpaca) credential reset state
  const [usStartingBalance, setUsStartingBalance] = useState<number | null>(null);
  const [showUsUpdateForm, setShowUsUpdateForm] = useState(false);
  const [usKeyId, setUsKeyId] = useState('');
  const [usSecretKey, setUsSecretKey] = useState('');
  const [usStartingBalanceInput, setUsStartingBalanceInput] = useState('100000');
  const [usUpdateSaving, setUsUpdateSaving] = useState(false);
  const [usUpdateMsg, setUsUpdateMsg] = useState('');
  const [usUpdateOk, setUsUpdateOk] = useState(true);

  const loadUsPaperConfig = () => {
    apiCall('/admin/system-config?prefix=paper_us_starting_balance', token).then(r => r.ok ? r.json() : null).then(d => {
      const v = d?.config?.paper_us_starting_balance;
      if (v !== undefined) setUsStartingBalance(parseFloat(v));
    }).catch(() => {});
  };

  const submitUsPaperUpdate = async () => {
    setUsUpdateMsg('');
    if (!usKeyId.trim() || !usSecretKey.trim()) {
      setUsUpdateOk(false);
      setUsUpdateMsg('Alpaca Key ID and Secret Key are both required');
      return;
    }
    const bal = parseFloat(usStartingBalanceInput);
    if (isNaN(bal) || bal <= 0) {
      setUsUpdateOk(false);
      setUsUpdateMsg('Paper Starting Balance must be a positive number');
      return;
    }
    if (bal < 1000 || bal > 10000000) {
      const proceed = window.confirm(
        `$${bal.toLocaleString('en-US')} USD is outside the typical $1,000–$10,000,000 USD range. Proceed anyway?`
      );
      if (!proceed) return;
    }
    setUsUpdateSaving(true);
    try {
      const res = await apiCall('/admin/us-paper-account/update-credentials', token, {
        method: 'POST',
        body: JSON.stringify({ key_id: usKeyId.trim(), secret_key: usSecretKey.trim(), starting_balance: bal }),
      });
      const d = await res.json().catch(() => null);
      if (res.ok) {
        setUsStartingBalance(bal);
        setShowUsUpdateForm(false);
        setUsKeyId('');
        setUsSecretKey('');
        setUsUpdateOk(true);
        setUsUpdateMsg(d?.message || 'Saved. Restart required for new credentials to take effect.');
      } else {
        setUsUpdateOk(false);
        setUsUpdateMsg(d?.detail || 'Save failed');
      }
    } catch {
      setUsUpdateOk(false);
      setUsUpdateMsg('Save failed');
    }
    setUsUpdateSaving(false);
  };

  const loadPaperStatus = () => {
    apiCall('/admin/paper-broker/status', token).then(r => r.ok ? r.json() : null).then(d => {
      if (d) setPaperStatus(d);
    }).catch(() => {});
  };

  const loadBotMode = () => {
    apiCall('/admin/kill-switch/status', token).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.mode) setBotMode(d.mode);
    }).catch(() => {});
  };

  const isPassive = botMode === 'passive';

  const resetPortfolio = async (exchange: 'ASX' | 'US') => {
    if (!isPassive) return;
    if (!window.confirm(`Reset ${exchange} paper portfolio to $1,000,000 AUD? This cannot be undone.`)) return;
    const setResetting = exchange === 'ASX' ? setResettingAsx : setResettingUs;
    const setMsg = exchange === 'ASX' ? setResetAsxMsg : setResetUsMsg;
    setResetting(true);
    try {
      const res = await apiCall('/admin/reset-paper-portfolio', token, {
        method: 'POST',
        body: JSON.stringify({ exchange, starting_cash: 1000000 }),
      });
      const d = await res.json();
      setMsg(res.ok ? `${exchange} portfolio reset successfully` : `Error: ${JSON.stringify(d.detail)}`);
    } catch {
      setMsg('Reset failed');
    }
    setResetting(false);
    setTimeout(() => setMsg(''), 5000);
  };

  const togglePaperBroker = async () => {
    if (!paperStatus?.broker_config_id) return;
    setTogglingPaper(true);
    try {
      const res = await apiCall(`/admin/broker-config/${paperStatus.broker_config_id}`, token, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !paperStatus.is_active }),
      });
      if (res.ok) {
        loadPaperStatus();
        setPaperMsg(paperStatus.is_active ? 'Paper broker deactivated' : 'Paper broker activated');
      }
    } catch {}
    setTogglingPaper(false);
    setTimeout(() => setPaperMsg(''), 3000);
  };

  const maskApiKey = (key: string | null) => {
    if (!key) return '—';
    if (key.startsWith('•')) return key;
    return '••••••••' + key.slice(-3);
  };

  const loadHealth = () => {
    apiCall('/admin/broker-health', token).then(r => r.ok ? r.json() : null).then(d => {
      if (Array.isArray(d)) setBrokerHealth(d);
    }).catch(() => {});
  };

  useEffect(() => {
    apiCall('/admin/broker-config', token).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.configs) setConfigs(d.configs);
    }).catch(() => {});
    apiCall('/admin/exchange-registry', token).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.exchanges) setExchanges(d.exchanges);
    }).catch(() => {});
    loadHealth();
    loadPaperStatus();
    loadUsPaperConfig();
    loadBotMode();
    const interval = setInterval(loadHealth, 60000);
    const botModeInterval = setInterval(loadBotMode, 15000);
    return () => { clearInterval(interval); clearInterval(botModeInterval); };
  }, [token]);

  const saveBroker = async (id: number) => {
    setSavingBroker(id);
    try {
      const res = await apiCall(`/admin/broker-config/${id}`, token, {
        method: 'PUT',
        body: JSON.stringify(brokerEdits),
      });
      if (res.ok) {
        const reloaded = await apiCall('/admin/broker-config', token).then(r => r.ok ? r.json() : null);
        if (reloaded?.configs) setConfigs(reloaded.configs);
        setEditingBroker(null);
        setBrokerEdits({});
        setBrokerMsg('Saved');
        setTimeout(() => setBrokerMsg(''), 3000);
      }
    } catch {}
    setSavingBroker(null);
  };

  const setExchangeMode = async (code: string, mode: TradingMode) => {
    setSavingExch(code);
    try {
      const res = await apiCall(`/admin/exchange-registry/${code}`, token, {
        method: 'PUT',
        body: JSON.stringify({ trading_mode: mode }),
      });
      if (res.ok) {
        setExchanges(prev => prev.map(e => e.code === code
          ? { ...e, trading_mode: mode, is_active: mode !== 'inactive' }
          : e
        ));
      }
    } catch {}
    setSavingExch(null);
  };

  const byRegion: Record<string, ExchangeWithMode[]> = {};
  for (const ex of exchanges) {
    if (!byRegion[ex.region]) byRegion[ex.region] = [];
    byRegion[ex.region].push(ex);
  }
  const regionOrder = ['AU', 'US', 'GB', 'EU', 'HK'];
  const regionNames: Record<string, string> = { AU: 'Australia', US: 'United States', GB: 'United Kingdom', EU: 'Europe', HK: 'Hong Kong' };

  const modeColors: Record<TradingMode, string> = {
    live: 'bg-status-green-btn text-text-bright',
    paper: 'bg-status-blue-btn text-text-bright',
    inactive: 'bg-surface-2 text-text-secondary',
  };

  const alpacaCfg = configs.find(c => c.broker_name === 'Alpaca');
  const tigerCfg = configs.find(c => c.broker_name === 'Tiger');

  const BrokerField = ({ label, value, editKey, type = 'text', placeholder = '', isSecret = false }: {
    label: string; value: string | null; editKey: string; type?: string; placeholder?: string; isSecret?: boolean;
  }) => (
    <div className="flex flex-col gap-0.5">
      <span className="text-text-muted text-xs">{label}</span>
      {editingBroker ? (
        <input
          type={type}
          value={isSecret ? (brokerEdits[editKey] ?? '') : (brokerEdits[editKey] ?? value ?? '')}
          onChange={e => setBrokerEdits((p: any) => ({ ...p, [editKey]: e.target.value }))}
          placeholder={isSecret ? 'Enter new value to change' : placeholder}
          className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm font-mono"
        />
      ) : (
        <span className="text-text-primary font-mono text-xs">{value || '—'}</span>
      )}
    </div>
  );

  const BrokerCard = ({ cfg, title, fields }: {
    cfg: BrokerConfig | undefined;
    title: string;
    fields: Array<{ label: string; key: keyof BrokerConfig; editKey: string; isSecret?: boolean; placeholder?: string }>;
  }) => {
    if (!cfg) return null;
    const isEditing = editingBroker === cfg.id;
    return (
      <div className="border border-border-base rounded-card p-4 bg-bg-deep">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-text-bright">{title}</span>
            {cfg.is_active && <span className="text-xs px-2 py-0.5 rounded-badge bg-status-green-btn-hover text-text-bright">Active</span>}
            <span className="text-xs text-text-muted bg-surface px-2 py-0.5 rounded-badge">{cfg.mode}</span>
          </div>
          {isEditing ? (
            <div className="flex gap-2">
              <button onClick={() => saveBroker(cfg.id)} disabled={savingBroker === cfg.id}
                className="text-xs bg-status-green-btn hover:bg-status-green-btn-hover text-text-bright px-2.5 py-1 rounded-btn-sm disabled:opacity-50">
                {savingBroker === cfg.id ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => { setEditingBroker(null); setBrokerEdits({}); }}
                className="text-xs border border-border-light text-text-primary hover:text-text-bright px-2.5 py-1 rounded-btn-sm">Cancel</button>
            </div>
          ) : (
            <button onClick={() => { setEditingBroker(cfg.id); setBrokerEdits({}); }}
              className="text-xs border border-border-light text-text-primary hover:text-text-bright px-2.5 py-1 rounded-btn-sm">Edit</button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          {fields.map(f => (
            <BrokerField key={f.editKey} label={f.label} value={String(cfg[f.key] ?? '')} editKey={f.editKey} isSecret={f.isSecret} placeholder={f.placeholder} type={f.isSecret ? 'password' : 'text'} />
          ))}
          <div className="sm:col-span-2 flex flex-col gap-0.5">
            <span className="text-text-muted text-xs">Notes</span>
            {isEditing ? (
              <textarea value={brokerEdits.notes ?? cfg.notes ?? ''} onChange={e => setBrokerEdits((p: any) => ({ ...p, notes: e.target.value }))} rows={2}
                className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm resize-none" />
            ) : (
              <span className="text-text-secondary text-xs italic">{cfg.notes || '—'}</span>
            )}
          </div>
          {cfg.updated_at && !isEditing && (
            <div className="sm:col-span-2 text-text-muted text-xs">Last updated: {fmtAEST(cfg.updated_at)}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Broker Connections Health */}
      <div className="bg-surface rounded-card border border-border-base p-4">
        <h2 className="font-semibold mb-3 text-text-bright">Broker Connections</h2>
        <div className="space-y-2">
          {brokerHealth.length === 0 ? (
            <p className="text-text-muted text-sm">Loading...</p>
          ) : brokerHealth.map(bh => {
            const dotColor = bh.status === 'connected' || bh.status === 'ok' ? 'bg-status-green' : bh.status === 'disconnected' || bh.status === 'error' ? 'bg-status-red' : bh.status === 'inactive' ? 'bg-text-muted' : 'bg-status-amber';
            const textColor = bh.status === 'connected' || bh.status === 'ok' ? 'text-status-green' : bh.status === 'disconnected' || bh.status === 'error' ? 'text-status-red' : bh.status === 'inactive' ? 'text-text-muted' : 'text-status-amber';
            const statusLabel = bh.status === 'connected' ? 'Connected' : bh.status === 'disconnected' ? 'Disconnected' : bh.status === 'inactive' ? 'Inactive' : bh.status === 'ok' ? 'OK' : bh.status === 'error' ? 'Error' : 'Unknown';
            const lastCheck = bh.last_checked_at ? formatPullTime(bh.last_checked_at) : 'Never';
            return (
              <div key={bh.broker_name} className="flex items-center gap-4 py-1">
                <span className="text-sm font-medium text-text-primary w-20">{bh.broker_name}</span>
                <div className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${dotColor}`} />
                  <span className={`text-sm font-medium ${textColor}`}>{statusLabel}</span>
                </div>
                {bh.last_checked_at && (
                  <span className="text-xs text-text-muted font-mono">Last check: {lastCheck}</span>
                )}
                {bh.error_message && (
                  <span className="text-xs text-text-muted italic">{bh.error_message}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ASX Broker Details */}
      <div className="bg-surface rounded-card border border-border-base p-5">
        <h2 className="font-semibold mb-1 text-text-bright">ASX Broker Details</h2>
        <p className="text-text-secondary text-xs mb-4">Trading account credentials. Secret fields are never returned — enter a new value to update.</p>
        {brokerMsg && <p className="text-status-green text-sm mb-3">{brokerMsg}</p>}
        <div className="space-y-3">
          <BrokerCard cfg={tigerCfg} title="Live - Tiger Brokers Australia" fields={[
            { label: 'Tiger ID', key: 'tiger_id', editKey: 'tiger_id', placeholder: 'Tiger user ID' },
            { label: 'License', key: 'license', editKey: 'license', placeholder: 'TBAU' },
            { label: 'Active Account ID', key: 'account_id', editKey: 'account_id', placeholder: 'account ID' },
            { label: 'Live Account ID', key: 'live_account_id', editKey: 'live_account_id', placeholder: 'live account ID' },
            { label: 'Demo Account ID', key: 'demo_account_id', editKey: 'demo_account_id', placeholder: 'demo account ID' },
            { label: 'Private Key Path', key: 'private_key_path', editKey: 'private_key_path', placeholder: '/path/to/tiger_private.pem' },
          ]} />

          {/* Paper Broker - Internal */}
          <div className="border border-border-base rounded-card p-4 bg-bg-deep">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-sm text-text-bright">Paper Broker - Internal</span>
              {paperMsg && <span className="text-status-green text-xs">{paperMsg}</span>}
            </div>
            {paperStatus ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex flex-col gap-0.5">
                  <span className="text-text-muted text-xs">Active</span>
                  <button
                    onClick={togglePaperBroker}
                    disabled={togglingPaper || !paperStatus.broker_config_id}
                    className={`self-start text-xs px-3 py-1 rounded-btn-sm font-medium transition-colors ${
                      paperStatus.is_active
                        ? 'bg-status-green-btn-hover hover:bg-status-green-btn text-text-bright'
                        : 'bg-surface-2 hover:bg-surface-hover text-text-primary'
                    } disabled:opacity-50`}>
                    {togglingPaper ? '...' : paperStatus.is_active ? 'Active (click to disable)' : 'Inactive (click to enable)'}
                  </button>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-text-muted text-xs">Cash Balance</span>
                  <span className="text-text-primary font-mono text-xs">
                    A${paperStatus.cash.toLocaleString('en-AU', { minimumFractionDigits: 2 })} AUD
                  </span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-text-muted text-xs">Open Orders</span>
                  <span className="text-text-primary font-mono text-xs">{paperStatus.open_orders_count}</span>
                </div>
                {paperStatus.account_updated_at && (
                  <div className="sm:col-span-2 text-text-muted text-xs font-mono">
                    Last updated: {fmtAEST(paperStatus.account_updated_at)}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-text-muted text-sm">Loading paper broker status...</p>
            )}

            <div className="mt-4 pt-4 border-t border-border-base">
              {!isPassive ? (
                <p className="text-status-amber text-xs mb-3">
                  ⚠️ Bot must be in Passive mode before resetting. Use the Kill Switch to set Passive first.
                </p>
              ) : (
                <p className="text-status-green text-xs mb-3">
                  ✓ Bot is in Passive mode — safe to reset
                </p>
              )}
              <p className="text-text-secondary text-xs mb-3">Clear all positions and return portfolio to $1,000,000 AUD starting cash.</p>
              <button
                onClick={() => resetPortfolio('ASX')}
                disabled={!isPassive || resettingAsx}
                title={!isPassive ? 'Set bot to Passive first' : undefined}
                className={isPassive && !resettingAsx
                  ? 'bg-status-amber-btn hover:bg-status-amber-btn-hover text-text-bright px-4 py-1.5 rounded-btn-sm text-sm font-medium'
                  : 'bg-surface-2 text-text-muted px-4 py-1.5 rounded-btn-sm text-sm font-medium cursor-not-allowed'}>
                {resettingAsx ? 'Resetting...' : 'Reset ASX Portfolio'}
              </button>
              {resetAsxMsg && <p className={`text-sm mt-3 ${resetAsxMsg.includes('Error') || resetAsxMsg.includes('failed') ? 'text-status-red' : 'text-status-green'}`}>{resetAsxMsg}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* US Broker Details */}
      <div className="bg-surface rounded-card border border-border-base p-5">
        <h2 className="font-semibold mb-1 text-text-bright">US Broker Details</h2>
        <p className="text-text-secondary text-xs mb-4">Trading account credentials. Secret fields are never returned — enter a new value to update.</p>
        <div className="space-y-3">
          <BrokerCard cfg={alpacaCfg} title="Live - Alpaca" fields={[
            { label: 'Base URL', key: 'client_portal_url', editKey: 'client_portal_url', placeholder: 'https://paper-api.alpaca.markets' },
            { label: 'Data URL', key: 'data_url', editKey: 'data_url', placeholder: 'https://data.alpaca.markets' },
            { label: 'API Key ID', key: 'api_key_id', editKey: 'api_key_id', placeholder: 'PKZY...' },
            { label: 'API Secret Key', key: 'api_secret_masked', editKey: 'api_secret', isSecret: true },
          ]} />

          {/* Paper Broker - Alpaca */}
          <div className="border border-border-base rounded-card p-4 bg-bg-deep">
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-sm text-text-bright">Paper Broker - Alpaca</span>
              <button
                onClick={() => { setShowUsUpdateForm(v => !v); setUsUpdateMsg(''); }}
                className="text-xs border border-border-light text-text-primary hover:text-text-bright px-2.5 py-1 rounded-btn-sm"
              >
                {showUsUpdateForm ? 'Cancel' : 'Update Paper Account'}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex flex-col gap-0.5">
                <span className="text-text-muted text-xs">Paper Starting Balance</span>
                <span className="text-text-primary font-mono text-xs">
                  {usStartingBalance !== null ? `$${usStartingBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })} USD` : '—'}
                </span>
              </div>
            </div>

            {showUsUpdateForm && (
              <div className="mt-4 pt-4 border-t border-border-base space-y-3">
                <p className="text-text-secondary text-xs">
                  Use this after manually deleting and recreating the paper account on Alpaca's site (Alpaca does not support
                  resetting a paper account in place). This does not touch Trade History. The backend will NOT auto-restart —
                  you must restart manually for the new credentials to take effect.
                </p>
                <div className="flex flex-col gap-0.5">
                  <label className="text-text-muted text-xs">Alpaca Key ID</label>
                  <input
                    type="text" value={usKeyId} onChange={e => setUsKeyId(e.target.value)}
                    placeholder="PKZY..." className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm font-mono"
                  />
                </div>
                <div className="flex flex-col gap-0.5">
                  <label className="text-text-muted text-xs">Alpaca Secret Key</label>
                  <input
                    type="password" value={usSecretKey} onChange={e => setUsSecretKey(e.target.value)}
                    placeholder="Enter new secret key" className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm font-mono"
                  />
                </div>
                <div className="flex flex-col gap-0.5">
                  <label className="text-text-muted text-xs">Paper Starting Balance (USD)</label>
                  <input
                    type="number" min={0} step="0.01" value={usStartingBalanceInput}
                    onChange={e => setUsStartingBalanceInput(e.target.value)}
                    className="w-full sm:w-48 bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm font-mono"
                  />
                  {(() => {
                    const bal = parseFloat(usStartingBalanceInput);
                    if (!isNaN(bal) && (bal < 1000 || bal > 10000000)) {
                      return (
                        <span className="text-status-amber text-xs">
                          Warning: ${bal.toLocaleString('en-US')} USD is outside the typical $1,000–$10,000,000 USD range. You'll be asked to confirm on submit.
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
                {usUpdateMsg && (
                  <p className={`text-xs ${usUpdateOk ? 'text-status-green' : 'text-status-red'}`}>{usUpdateMsg}</p>
                )}
                <button
                  onClick={submitUsPaperUpdate} disabled={usUpdateSaving}
                  className="text-xs bg-status-green-btn hover:bg-status-green-btn-hover text-text-bright px-2.5 py-1 rounded-btn-sm disabled:opacity-50"
                >
                  {usUpdateSaving ? 'Saving...' : 'Save & Update'}
                </button>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-border-base">
              {!isPassive ? (
                <p className="text-status-amber text-xs mb-3">
                  ⚠️ Bot must be in Passive mode before resetting. Use the Kill Switch to set Passive first.
                </p>
              ) : (
                <p className="text-status-green text-xs mb-3">
                  ✓ Bot is in Passive mode — safe to reset
                </p>
              )}
              <p className="text-text-secondary text-xs mb-3">Clear all positions and return portfolio to $1,000,000 USD starting cash.</p>
              <button
                onClick={() => resetPortfolio('US')}
                disabled={!isPassive || resettingUs}
                title={!isPassive ? 'Set bot to Passive first' : undefined}
                className={isPassive && !resettingUs
                  ? 'bg-status-amber-btn hover:bg-status-amber-btn-hover text-text-bright px-4 py-1.5 rounded-btn-sm text-sm font-medium'
                  : 'bg-surface-2 text-text-muted px-4 py-1.5 rounded-btn-sm text-sm font-medium cursor-not-allowed'}>
                {resettingUs ? 'Resetting...' : 'Reset US Portfolio'}
              </button>
              {resetUsMsg && <p className={`text-sm mt-3 ${resetUsMsg.includes('Error') || resetUsMsg.includes('failed') ? 'text-status-red' : 'text-status-green'}`}>{resetUsMsg}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Exchange Registry */}
      <div className="bg-surface rounded-card border border-border-base p-5">
        <h2 className="font-semibold mb-1 text-text-bright">Exchange Registry</h2>
        <p className="text-text-secondary text-xs mb-4">Set each exchange to Live, Paper, or Inactive. Live = real money. Paper = simulated. Inactive = not scanned.</p>
        <div className="space-y-4">
          {regionOrder.map(region => {
            const exs = byRegion[region];
            if (!exs || exs.length === 0) return null;
            return (
              <div key={region}>
                <p className="text-xs text-text-secondary font-medium mb-2">{REGION_FLAGS[region]} {regionNames[region] || region}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {exs.map(ex => (
                    <div key={ex.code} className={`rounded-card p-3 border ${ex.trading_mode === 'live' ? 'border-border-green bg-status-green-soft/30' : ex.trading_mode === 'paper' ? 'border-border-blue bg-status-blue-soft/20' : 'border-border-base bg-bg-deep'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-sm font-semibold text-text-bright font-mono">{ex.code}</div>
                          <div className="text-xs text-text-secondary">{ex.name}</div>
                        </div>
                        {savingExch === ex.code && <span className="text-xs text-text-muted">...</span>}
                      </div>
                      <div className="flex gap-1">
                        {(['live', 'paper', 'inactive'] as TradingMode[]).map(mode => (
                          <button key={mode} onClick={() => setExchangeMode(ex.code, mode)}
                            disabled={savingExch === ex.code}
                            className={`flex-1 text-xs py-1 rounded-btn-sm capitalize ${ex.trading_mode === mode ? modeColors[mode] : 'bg-surface-2 text-text-secondary hover:bg-surface-hover'}`}>
                            {mode}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Exchange Management (parent) ─────────────────────────────────────────

const EXCHANGE_MANAGEMENT_SUB_TABS = ['Strategy & Risk', 'Broker & Exchange Management'] as const;
type ExchangeManagementSubTab = typeof EXCHANGE_MANAGEMENT_SUB_TABS[number];

function ExchangeManagementParentTab({ token, initialSubTab, navKey }: { token: string; initialSubTab: ExchangeManagementSubTab; navKey?: string }) {
  const [subTab, setSubTab] = useState<ExchangeManagementSubTab>(initialSubTab);
  useEffect(() => { setSubTab(initialSubTab); }, [initialSubTab, navKey]);

  return (
    <div className="space-y-4">
      {/* Sub-tab navigation */}
      <div className="flex border-b border-border-base space-x-1">
        {EXCHANGE_MANAGEMENT_SUB_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              subTab === tab
                ? 'border-border-blue text-text-bright'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {subTab === 'Strategy & Risk' && <StrategyTab token={token} />}
      {subTab === 'Broker & Exchange Management' && <BrokerExchangeManagementTab token={token} />}
    </div>
  );
}

function StrategyTab({ token }: { token: string }) {
  const [settings, setSettings] = useState<StrategySettings>(defaultStrategy);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [validationError, setValidationError] = useState('');
  // Signal thresholds
  const [thresholdBuy, setThresholdBuy] = useState(6.5);
  const [thresholdSell, setThresholdSell] = useState(3.5);
  const [savingThresholds, setSavingThresholds] = useState(false);
  const [thresholdMsg, setThresholdMsg] = useState('');
  // Preset modal
  const [presetConfirm, setPresetConfirm] = useState<string | null>(null);
  // Opportunity reallocation
  const [opportunityReallocEnabled, setOpportunityReallocEnabled] = useState(false);
  const [opportunityReallocMinScore, setOpportunityReallocMinScore] = useState(7.5);
  const [savingOpportReallocMsg, setSavingOpportReallocMsg] = useState('');
  // Signals & Fundamentals Blend (migrated from Signals & Fundamentals tab, Session 9)
  const [mlWeight, setMlWeight] = useState(40);
  const [savingMlWeight, setSavingMlWeight] = useState(false);
  const [buyThreshold, setBuyThreshold] = useState(6.5);
  const [sellThreshold, setSellThreshold] = useState(3.5);
  const [tooltip, setTooltip] = useState<{ id: string; text: string; name: string; upward: boolean } | null>(null);

  useEffect(() => {
    const tok = sessionStorage.getItem('token') || token;
    fetch('/api/settings/strategy', {
      headers: { Authorization: `Bearer ${tok}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.strategy?.buy_threshold != null) setBuyThreshold(data.strategy.buy_threshold);
        if (data?.strategy?.sell_threshold != null) setSellThreshold(data.strategy.sell_threshold);
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    apiCall('/admin/ml-weight', token)
      .then(r => r.json())
      .then(d => setMlWeight(Math.round((d.ml_weight || 0.4) * 100)))
      .catch(() => {});
  }, [token]);

  const saveMlWeight = async (val: number) => {
    setSavingMlWeight(true);
    await apiCall('/admin/ml-weight', token, { method: 'POST', body: JSON.stringify({ ml_weight: val / 100 }) });
    setSavingMlWeight(false);
  };

  useEffect(() => {
    apiCall('/admin/config/thresholds', token).then(r => r.ok ? r.json() : null).then(d => {
      if (!d) return;
      setThresholdBuy(d.threshold_buy ?? 6.5);
      setThresholdSell(d.threshold_sell ?? 3.5);
    }).catch(() => {});
  }, [token]);

  const saveThresholds = async () => {
    setSavingThresholds(true);
    try {
      const res = await apiCall('/admin/config/thresholds', token, {
        method: 'PUT',
        body: JSON.stringify({ threshold_buy: thresholdBuy, threshold_sell: thresholdSell }),
      });
      setThresholdMsg(res.ok ? 'Thresholds saved' : 'Save failed');
    } catch { setThresholdMsg('Save failed'); }
    setSavingThresholds(false);
    setTimeout(() => setThresholdMsg(''), 3000);
  };

  const applyPreset = async (profile: 'conservative' | 'balanced' | 'aggressive') => {
    setPresetConfirm(null);
    try {
      const res = await apiCall(`/api/settings/strategy/preset/${profile}`, token, { method: 'POST' });
      if (res.ok) {
        const settingsRes = await apiCall('/settings/strategy', token);
        if (settingsRes.ok) {
          const d = await settingsRes.json();
          if (d?.strategy) {
            const s = d.strategy;
            const parse = (key: string, def: any) => {
              const v = s[key];
              if (v === undefined || v === null) return def;
              if (typeof v === 'object') return v.value ?? def;
              return typeof def === 'number' ? parseFloat(v) : (typeof def === 'boolean' ? Boolean(v) : v);
            };
            setSettings({
              minConfidence: parse('minConfidence', 7),
              maxPositionSize: parse('maxPositionSize', 10),
              maxSectorAllocation: parse('maxSectorAllocation', 30),
              stopLoss: parse('stopLoss', 8),
              takeProfit: parse('takeProfit', 25),
              cashReserveGuard: parse('cashReserveGuard', 10),
              maxPositions: parse('maxPositions', 10),
              maxAsxPositions: parse('maxAsxPositions', 5),
              maxUsPositions: parse('maxUsPositions', 5),
              tradingMode: parse('tradingMode', 'paper'),
              asx: parse('asx', true),
              us: parse('us', true),
            });
          }
        }
      }
    } catch {}
  };

  const saveOpportReallocSettings = async () => {
    try {
      const res = await apiCall('/admin/config/opportunity-reallocation', token, {
        method: 'PUT',
        body: JSON.stringify({
          opportunity_reallocation_enabled: opportunityReallocEnabled,
          opportunity_reallocation_min_score: opportunityReallocMinScore,
        }),
      });
      setSavingOpportReallocMsg(res.ok ? 'Saved' : 'Save failed');
      setTimeout(() => setSavingOpportReallocMsg(''), 3000);
    } catch { setSavingOpportReallocMsg('Save failed'); }
  };

  useEffect(() => {
    apiCall('/admin/config/opportunity-reallocation', token).then(r => r.ok ? r.json() : null).then(d => {
      if (!d) return;
      setOpportunityReallocEnabled(d.opportunity_reallocation_enabled ?? false);
      setOpportunityReallocMinScore(d.opportunity_reallocation_min_score ?? 7.5);
    }).catch(() => {});
  }, [token]);

  useEffect(() => {
    apiCall('/settings/strategy', token).then(r => r.ok ? r.json() : null).then(d => {
      if (!d?.strategy) return;
      const s = d.strategy;
      const parse = (key: string, def: any) => {
        const v = s[key];
        if (v === undefined || v === null) return def;
        if (typeof v === 'object') return v.value ?? def;
        return typeof def === 'number' ? parseFloat(v) : (typeof def === 'boolean' ? Boolean(v) : v);
      };
      setSettings({
        minConfidence: parse('minConfidence', 7),
        maxPositionSize: parse('maxPositionSize', 10),
        maxSectorAllocation: parse('maxSectorAllocation', 30),
        stopLoss: parse('stopLoss', 8),
        takeProfit: parse('takeProfit', 25),
        cashReserveGuard: parse('cashReserveGuard', 10),
        maxPositions: parse('maxPositions', 10),
        maxAsxPositions: parse('maxAsxPositions', 5),
        maxUsPositions: parse('maxUsPositions', 5),
        tradingMode: parse('tradingMode', 'paper'),
        asx: parse('asx', true),
        us: parse('us', true),
      });
    }).catch(() => {});
  }, [token]);

  const update = (k: keyof StrategySettings, v: any) => {
    setSettings(s => {
      const next = { ...s, [k]: v };
      if (k === 'maxPositions' || k === 'maxAsxPositions' || k === 'maxUsPositions') {
        const total = next.maxPositions;
        const sum = (k === 'maxAsxPositions' ? v : next.maxAsxPositions) + (k === 'maxUsPositions' ? v : next.maxUsPositions);
        if (sum !== total) {
          setValidationError(`Per-market limits must sum to Maximum Positions (${total}). Current: ASX ${next.maxAsxPositions} + US ${next.maxUsPositions} = ${next.maxAsxPositions + next.maxUsPositions}`);
        } else {
          setValidationError('');
        }
      }
      return next;
    });
  };

  const save = async () => {
    if (settings.maxAsxPositions + settings.maxUsPositions !== settings.maxPositions) {
      setSaveMsg('Save failed: per-market limits must sum to Maximum Positions');
      setTimeout(() => setSaveMsg(''), 4000);
      return;
    }
    setSaving(true);
    try {
      const res = await apiCall('/settings/strategy', token, {
        method: 'POST',
        body: JSON.stringify({ strategy: settings }),
      });
      setSaveMsg(res.ok ? 'Settings saved' : 'Save failed');
    } catch { setSaveMsg('Save failed'); }
    setSaving(false);
    setTimeout(() => setSaveMsg(''), 3000);
  };

  const posLimitExceedsMax = settings.maxAsxPositions > settings.maxPositions || settings.maxUsPositions > settings.maxPositions;
  const sumMismatch = settings.maxAsxPositions + settings.maxUsPositions !== settings.maxPositions;

  return (
    <div className="space-y-6">
      {/* Risk Profile Presets */}
      <div className="bg-surface rounded-card border border-border-base p-5">
        <h2 className="font-semibold mb-4 text-text-bright">Risk Profile Presets</h2>
        <div className="flex flex-wrap gap-3 mb-3">
          <button onClick={() => setPresetConfirm('conservative')}
            className="border-2 border-border-blue text-status-blue px-6 py-2 rounded-btn font-medium hover:bg-status-blue-btn hover:text-text-bright transition">
            Conservative
          </button>
          <button onClick={() => setPresetConfirm('balanced')}
            className="border-2 border-border-green text-status-green px-6 py-2 rounded-btn font-medium hover:bg-status-green-btn hover:text-text-bright transition">
            Balanced
          </button>
          <button onClick={() => setPresetConfirm('aggressive')}
            className="border-2 border-border-amber text-status-amber px-6 py-2 rounded-btn font-medium hover:bg-status-amber-btn hover:text-text-bright transition">
            Aggressive
          </button>
        </div>
        <p className="text-text-secondary text-xs">Presets overwrite all settings above. Your current settings will be lost.</p>
      </div>

      {presetConfirm && (
        <div className="fixed inset-0 bg-bg-deep/50 flex items-center justify-center z-50 rounded-lg">
          <div className="bg-surface rounded-card p-6 max-w-sm border border-border-base">
            <h3 className="text-lg font-semibold mb-4 text-text-bright">Confirm Preset</h3>
            <p className="text-text-primary mb-6">This will overwrite all your current Strategy & Risk settings with the {presetConfirm.charAt(0).toUpperCase() + presetConfirm.slice(1)} preset. Continue?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setPresetConfirm(null)}
                className="px-4 py-2 rounded-btn border border-border-light text-text-primary hover:bg-surface-hover">
                Cancel
              </button>
              <button onClick={() => applyPreset(presetConfirm as any)}
                className="px-4 py-2 rounded-btn bg-status-green-btn text-text-bright hover:bg-status-green-btn-hover font-medium">
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signals & Fundamentals Blend (migrated from Signals & Fundamentals tab, Session 9) */}
      <div className="bg-surface border border-border-base rounded-card p-4">
        <style>{`
          .blend-slider { appearance: none; -webkit-appearance: none; height: 10px; border-radius: 9999px; outline: none; cursor: pointer; }
          .blend-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 20px; height: 20px; border-radius: 50%; background: #e5e7eb; border: 2px solid #9ca3af; cursor: pointer; }
          .blend-slider::-moz-range-thumb { width: 20px; height: 20px; border-radius: 50%; background: #e5e7eb; border: 2px solid #9ca3af; cursor: pointer; }
        `}</style>
        <div className="flex items-center gap-2 mb-4">
          <h3 className="font-semibold text-sm text-text-bright">Signals &amp; Fundamentals Blend</h3>
          <InfoTooltip
            id="strategy-blend"
            name="Signals & Fundamentals Blend"
            text="Controls how much weight the final combined score gives to real-time signals (price/technical/sentiment) versus fundamentals data. Higher Fundamentals weight favours longer-term value; higher Signals weight favours short-term momentum."
            tooltipId={tooltip?.id || null}
            setTooltip={setTooltip}
          />
        </div>
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs text-status-blue font-medium">Signals</span>
            <div className="bg-surface-2 border border-border-blue rounded px-2 py-1 w-16 text-center text-status-blue font-bold text-sm font-mono">{100 - mlWeight}%</div>
          </div>
          <input
            type="range" min="0" max="100" value={100 - mlWeight}
            onChange={e => setMlWeight(100 - parseInt(e.target.value))}
            onMouseUp={() => saveMlWeight(mlWeight)}
            onTouchEnd={() => saveMlWeight(mlWeight)}
            className="blend-slider flex-1"
            style={{ background: `linear-gradient(to right, #60a5fa 0%, #60a5fa ${100 - mlWeight}%, #10b981 ${100 - mlWeight}%, #10b981 100%)` }}
          />
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs text-status-green font-medium">Fundamentals</span>
            <div className="bg-surface-2 border border-border-green rounded px-2 py-1 w-16 text-center text-status-green font-bold text-sm font-mono">{mlWeight}%</div>
          </div>
          {savingMlWeight && <span className="text-xs text-status-amber">Saving...</span>}
        </div>
        <p className="text-xs text-text-muted mt-3">Default: Signals 60% / Fundamentals 40%. Changes take effect on next signal run.</p>
      </div>

      <div className="bg-status-blue-soft/30 border border-border-blue rounded-card px-4 py-3 text-sm text-status-blue-muted">
        Score &ge; {buyThreshold} = <span className="font-semibold text-status-green">BUY</span> &nbsp;&middot;&nbsp;
        {sellThreshold} – {buyThreshold} = <span className="font-semibold text-status-amber">HOLD</span> &nbsp;&middot;&nbsp;
        &le; {sellThreshold} = <span className="font-semibold text-status-red">SELL</span> &nbsp;&middot;&nbsp;
        Weights determine how much each signal contributes to the final combined score.
      </div>

      {/* Signal Thresholds */}
      <div className="bg-surface rounded-card border border-border-base p-5">
        <h2 className="font-semibold mb-1 text-text-bright">Signal Thresholds</h2>
        <p className="text-text-secondary text-xs mb-4">Control when the bot triggers BUY, HOLD, or SELL signals. Scores are on a 0–10 scale.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-text-secondary text-sm block mb-1">BUY Threshold <span className="text-status-green">(≥ triggers BUY)</span></label>
            <input
              type="number" min={0} max={10} step={0.1} value={thresholdBuy}
              onChange={e => setThresholdBuy(Math.min(10, Math.max(0, parseFloat(e.target.value) || 0)))}
              className="w-full bg-surface-2 border border-border-green rounded px-3 py-2 text-sm text-status-green font-bold font-mono focus:outline-none focus:border-status-green"
            />
            <p className="text-xs text-text-muted mt-1">Score ≥ {thresholdBuy.toFixed(1)} → BUY</p>
          </div>
          <div>
            <label className="text-text-secondary text-sm block mb-1">SELL Threshold <span className="text-status-red">(≤ triggers SELL)</span></label>
            <input
              type="number" min={0} max={10} step={0.1} value={thresholdSell}
              onChange={e => setThresholdSell(Math.min(10, Math.max(0, parseFloat(e.target.value) || 0)))}
              className="w-full bg-surface-2 border border-border-red rounded px-3 py-2 text-sm text-status-red font-bold font-mono focus:outline-none focus:border-status-red"
            />
            <p className="text-xs text-text-muted mt-1">Score ≤ {thresholdSell.toFixed(1)} → SELL</p>
          </div>
        </div>
        <div className="mt-3 px-3 py-2 bg-surface-2/50 rounded text-sm text-status-amber font-medium">
          HOLD range: {thresholdSell.toFixed(1)} – {thresholdBuy.toFixed(1)}
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button onClick={saveThresholds} disabled={savingThresholds}
            className="bg-status-green-btn hover:bg-status-green-btn-hover disabled:opacity-50 text-text-bright px-4 py-1.5 rounded-btn text-sm font-medium">
            {savingThresholds ? 'Saving...' : 'Save Thresholds'}
          </button>
          {thresholdMsg && <span className={`text-sm ${thresholdMsg.includes('failed') ? 'text-status-red' : 'text-status-green'}`}>{thresholdMsg}</span>}
        </div>
      </div>

      {/* AI Decision Settings */}
      <div className="bg-surface rounded-card border border-border-base p-5">
        <h2 className="font-semibold mb-4 text-text-bright">AI Decision Settings</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-text-secondary text-sm">Minimum Confidence Score (1–10)</label>
            <div className="flex items-center space-x-3 mt-2">
              <input type="range" min={1} max={10} value={settings.minConfidence}
                onChange={e => update('minConfidence', parseInt(e.target.value))} className="flex-1" />
              <input type="number" min={1} max={10} value={settings.minConfidence}
                onChange={e => update('minConfidence', Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
                className="w-16 bg-surface-2 border border-border-light rounded px-2 py-1 text-sm text-center text-status-green font-bold font-mono" />
            </div>
          </div>
          <div>
            <label className="text-text-secondary text-sm">Maximum Positions (total across all markets)</label>
            <div className="flex items-center space-x-3 mt-2">
              <input type="range" min={1} max={500} value={settings.maxPositions}
                onChange={e => update('maxPositions', parseInt(e.target.value))} className="flex-1" />
              <input type="number" min={1} max={500} value={settings.maxPositions}
                onChange={e => update('maxPositions', Math.min(500, Math.max(1, parseInt(e.target.value) || 1)))}
                className="w-16 bg-surface-2 border border-border-light rounded px-2 py-1 text-sm text-center text-status-green font-bold font-mono" />
            </div>
          </div>
        </div>
        <div className="mt-6 pt-5 border-t border-border-base">
          <p className="text-text-secondary text-sm font-medium mb-2">Per-Market Position Limits</p>
          <p className="text-xs text-text-muted mb-4">ASX + US must equal Maximum Positions ({settings.maxPositions}). Current sum: {settings.maxAsxPositions + settings.maxUsPositions}</p>
          {sumMismatch && (
            <div className="mb-4 bg-status-red-soft border border-border-red rounded px-3 py-2 text-status-red-muted text-sm">
              Per-market limits must sum to Maximum Positions ({settings.maxPositions}). ASX {settings.maxAsxPositions} + US {settings.maxUsPositions} = {settings.maxAsxPositions + settings.maxUsPositions}
            </div>
          )}
          {posLimitExceedsMax && (
            <div className="mb-4 bg-status-red-soft border border-border-red rounded px-3 py-2 text-status-red-muted text-sm">
              Individual per-market limits cannot exceed Maximum Positions ({settings.maxPositions})
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-text-secondary text-sm">ASX Max Positions</label>
              <div className="flex items-center space-x-3 mt-2">
                <input type="range" min={1} max={settings.maxPositions} value={settings.maxAsxPositions}
                  onChange={e => update('maxAsxPositions', parseInt(e.target.value))} className="flex-1" />
                <input type="number" min={1} max={settings.maxPositions} value={settings.maxAsxPositions}
                  onChange={e => update('maxAsxPositions', Math.min(settings.maxPositions, Math.max(1, parseInt(e.target.value) || 1)))}
                  className={`w-16 bg-surface-2 border border-border-light rounded px-2 py-1 text-sm text-center font-bold font-mono ${sumMismatch ? 'text-status-red' : 'text-status-amber'}`} />
              </div>
            </div>
            <div>
              <label className="text-text-secondary text-sm">US Max Positions</label>
              <div className="flex items-center space-x-3 mt-2">
                <input type="range" min={1} max={settings.maxPositions} value={settings.maxUsPositions}
                  onChange={e => update('maxUsPositions', parseInt(e.target.value))} className="flex-1" />
                <input type="number" min={1} max={settings.maxPositions} value={settings.maxUsPositions}
                  onChange={e => update('maxUsPositions', Math.min(settings.maxPositions, Math.max(1, parseInt(e.target.value) || 1)))}
                  className={`w-16 bg-surface-2 border border-border-light rounded px-2 py-1 text-sm text-center font-bold font-mono ${sumMismatch ? 'text-status-red' : 'text-status-blue'}`} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Management */}
      <div className="bg-surface rounded-card border border-border-base p-5">
        <h2 className="font-semibold mb-4 text-text-bright">Risk Management</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { label: 'Max Position Size (% of portfolio)', k: 'maxPositionSize' as const, min: 1, max: 25, color: 'text-status-green', suffix: '%' },
            { label: 'Stop Loss (%)', k: 'stopLoss' as const, min: 1, max: 20, color: 'text-status-red', suffix: '%' },
            { label: 'Take Profit (%)', k: 'takeProfit' as const, min: 5, max: 50, color: 'text-status-green', suffix: '%' },
            { label: 'Cash Reserve Guard (% of portfolio)', k: 'cashReserveGuard' as const, min: 0, max: 30, color: 'text-status-green', suffix: '%' },
          ].map(({ label, k, min, max, color, suffix }) => (
            <div key={k}>
              <label className="text-text-secondary text-sm">{label}</label>
              <div className="flex items-center space-x-3 mt-2">
                <input type="range" min={min} max={max} value={settings[k] as number}
                  onChange={e => update(k, parseInt(e.target.value))} className="flex-1" />
                <div className="flex items-center gap-0.5">
                  <input type="number" min={min} max={max} value={settings[k] as number}
                    onChange={e => update(k, Math.min(max, Math.max(min, parseInt(e.target.value) || min)))}
                    className={`w-16 bg-surface-2 border border-border-light rounded px-2 py-1 text-sm text-center font-bold font-mono ${color}`} />
                  <span className={`text-sm font-bold ${color}`}>{suffix}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Opportunity Reallocation */}
      <div className="bg-surface rounded-card border border-border-base p-5">
        <h2 className="font-semibold mb-4 text-text-bright">Opportunity Reallocation</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-text-primary text-sm font-medium block">Enable Opportunity Reallocation</label>
              <p className="text-text-secondary text-xs mt-1">When enabled, the bot may sell the lowest-scored current position to fund a higher-scored opportunity. Only triggers when the new opportunity scores above the threshold. Never sells manually held positions. Maximum one forced sell per cycle.</p>
            </div>
            <input type="checkbox" checked={opportunityReallocEnabled} onChange={e => setOpportunityReallocEnabled(e.target.checked)}
              className="w-5 h-5 cursor-pointer" />
          </div>
          {opportunityReallocEnabled && (
            <div>
              <label className="text-text-secondary text-sm block mb-2">Minimum Score to Trigger Reallocation</label>
              <div className="flex items-center gap-3">
                <input type="range" min="7.0" max="9.5" step="0.1" value={opportunityReallocMinScore}
                  onChange={e => setOpportunityReallocMinScore(parseFloat(e.target.value))} className="flex-1" />
                <input type="number" min="7.0" max="9.5" step="0.1" value={opportunityReallocMinScore}
                  onChange={e => setOpportunityReallocMinScore(Math.min(9.5, Math.max(7.0, parseFloat(e.target.value) || 7.5)))}
                  className="w-20 bg-surface-2 border border-border-light rounded px-2 py-1 text-sm text-center font-bold font-mono text-status-blue" />
              </div>
            </div>
          )}
          <button onClick={saveOpportReallocSettings}
            className="bg-status-blue-btn hover:bg-status-blue-btn-hover text-text-bright px-4 py-1.5 rounded-btn font-medium text-sm">
            Save Opportunity Reallocation
          </button>
          {savingOpportReallocMsg && <span className={`text-sm ${savingOpportReallocMsg.includes('failed') ? 'text-status-red' : 'text-status-green'}`}>{savingOpportReallocMsg}</span>}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving || sumMismatch || posLimitExceedsMax}
          className="bg-status-green-btn hover:bg-status-green-btn-hover disabled:opacity-50 text-text-bright px-6 py-2 rounded-btn font-medium">
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {saveMsg && <span className={`text-sm ${saveMsg.includes('failed') || saveMsg.includes('fail') ? 'text-status-red' : 'text-status-green'}`}>{saveMsg}</span>}
      </div>
    </div>
  );
}

interface FailedOrder {
  id: number;
  ticker: string;
  exchange: string;
  error_code: string;
  error_message: string;
  failed_at: string;
  cooldown_until: string;
  cleared_at: string | null;
  is_active: boolean;
}

function FailedOrderCooldownsSection({ token }: { token: string }) {
  const [orders, setOrders] = useState<FailedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState<number | null>(null);
  const [clearingAll, setClearingAll] = useState(false);

  const loadOrders = useCallback(async () => {
    try {
      const res = await apiCall('/admin/failed-orders', token);
      if (res.ok) {
        const d = await res.json();
        setOrders(d.failed_orders || []);
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => {
    loadOrders();
    const interval = setInterval(loadOrders, 60000);
    return () => clearInterval(interval);
  }, [loadOrders]);

  const clearOne = async (id: number) => {
    setClearing(id);
    try {
      const res = await apiCall(`/admin/failed-orders/${id}/clear`, token, { method: 'POST' });
      if (res.ok) {
        await loadOrders();
      }
    } catch {}
    setClearing(null);
  };

  const clearAllExpired = async () => {
    setClearingAll(true);
    try {
      const res = await apiCall('/admin/failed-orders/clear-expired', token, { method: 'POST' });
      if (res.ok) {
        await loadOrders();
      }
    } catch {}
    setClearingAll(false);
  };

  return (
    <div className="bg-surface rounded-card border border-border-base p-5">
      <h2 className="font-semibold mb-1 text-text-bright">Failed Order Cooldowns</h2>
      <p className="text-text-secondary text-xs mb-3">
        Tickers currently in cooldown after a failed order attempt. Clear a cooldown to allow the bot to retry immediately.
      </p>

      {!loading && orders.length > 0 && (
        <div className="mb-3 flex gap-2">
          <button
            onClick={clearAllExpired}
            disabled={clearingAll}
            className="bg-status-blue-btn hover:bg-status-blue-btn-hover disabled:opacity-50 text-text-bright px-4 py-1.5 rounded-btn text-sm font-medium">
            {clearingAll ? 'Clearing...' : 'Clear All Expired'}
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-text-secondary text-sm">Loading...</p>
      ) : orders.length === 0 ? (
        <p className="text-text-secondary text-sm">No failed order cooldowns</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-base">
                <th className="text-left py-2 px-2 text-text-secondary font-semibold">Ticker</th>
                <th className="text-left py-2 px-2 text-text-secondary font-semibold">Exchange</th>
                <th className="text-left py-2 px-2 text-text-secondary font-semibold">Error</th>
                <th className="text-left py-2 px-2 text-text-secondary font-semibold">Failed At</th>
                <th className="text-left py-2 px-2 text-text-secondary font-semibold">Cooldown Until</th>
                <th className="text-left py-2 px-2 text-text-secondary font-semibold">Status</th>
                <th className="text-left py-2 px-2 text-text-secondary font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.id} className="border-b border-border-base hover:bg-surface-hover">
                  <td className="py-2 px-2 text-text-bright font-semibold font-mono">{order.ticker}</td>
                  <td className="py-2 px-2 text-text-primary">{order.exchange}</td>
                  <td className="py-2 px-2 text-text-primary text-xs font-mono">{order.error_code}</td>
                  <td className="py-2 px-2 text-text-primary text-xs font-mono">{fmtAEST(order.failed_at)}</td>
                  <td className="py-2 px-2 text-text-primary text-xs font-mono">{fmtAEST(order.cooldown_until)}</td>
                  <td className="py-2 px-2">
                    <span className={`text-xs font-semibold ${order.is_active ? 'text-status-red' : 'text-text-muted'}`}>
                      {order.is_active ? 'Active' : 'Expired'}
                    </span>
                  </td>
                  <td className="py-2 px-2">
                    <button
                      onClick={() => clearOne(order.id)}
                      disabled={clearing === order.id}
                      className="bg-status-red-btn hover:bg-status-red-btn-hover disabled:opacity-50 text-text-bright px-2.5 py-1 rounded-btn-sm text-xs font-medium">
                      {clearing === order.id ? 'Clearing...' : 'Clear'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Company Aliases Section (moved from Data Sources, Session 10) ────────────

interface CompanyAlias {
  id: number; ticker: string; exchange: string; alias_name: string;
  confidence: string; source: string; first_seen_at: string; verified_by_human: boolean;
}

function CompanyAliasesSection({ token }: { token: string }) {
  const [aliases, setAliases] = useState<CompanyAlias[]>([]);
  const [aliasFilter, setAliasFilter] = useState<'unverified' | 'low' | 'all'>('unverified');
  const [aliasesOpen, setAliasesOpen] = useState(true);
  const [unverifiedCount, setUnverifiedCount] = useState(0);

  const loadAliases = useCallback(async () => {
    try {
      const filter = aliasFilter === 'unverified' ? '?verified=false&limit=100' : '?limit=100';
      const res = await apiCall(`/admin/company-aliases${filter}`, token);
      if (res.ok) {
        const data = await res.json();
        let all: CompanyAlias[] = data.aliases || [];
        if (aliasFilter === 'low') all = all.filter((a: CompanyAlias) => a.confidence === 'low' || a.confidence === 'medium');
        setAliases(all);
      }
      const ucRes = await apiCall('/admin/company-aliases?verified=false&limit=1', token);
      if (ucRes.ok) {
        const ucData = await ucRes.json();
        setUnverifiedCount((ucData.aliases || []).length);
      }
    } catch {}
  }, [token, aliasFilter]);

  useEffect(() => { if (aliasesOpen) loadAliases(); }, [aliasesOpen, aliasFilter, loadAliases]);

  const approveAlias = async (id: number) => {
    await apiCall(`/admin/company-aliases/${id}/verify`, token, { method: 'PUT' });
    loadAliases();
  };

  const rejectAlias = async (id: number) => {
    if (!window.confirm('Delete this alias?')) return;
    await apiCall(`/admin/company-aliases/${id}`, token, { method: 'DELETE' });
    loadAliases();
  };

  return (
    <div className="bg-surface rounded-lg border border-border-base">
      <button
        className="w-full p-4 text-left flex items-center justify-between"
        onClick={() => setAliasesOpen(o => !o)}
      >
        <div>
          <span className="font-semibold">Company Aliases</span>
          {unverifiedCount > 0 && (
            <span className="ml-2 bg-status-amber-btn text-text-bright text-xs px-2 py-0.5 rounded-full">
              {unverifiedCount} unverified
            </span>
          )}
          <p className="text-text-secondary text-sm mt-0.5">
            Automatically discovered company name → ticker mappings. Review and approve uncertain matches.
          </p>
        </div>
        <span className="text-text-secondary text-xs">{aliasesOpen ? '▲' : '▼'}</span>
      </button>
      {aliasesOpen && (
        <div className="border-t border-border-base p-4">
          <div className="flex gap-2 mb-4">
            {(['unverified', 'low', 'all'] as const).map(f => (
              <button
                key={f}
                onClick={() => setAliasFilter(f)}
                className={`px-3 py-1 rounded text-xs font-medium ${aliasFilter === f ? 'bg-status-blue-btn-hover text-text-bright' : 'bg-surface-2 text-text-primary hover:bg-surface-hover'}`}
              >
                {f === 'unverified' ? 'Unverified' : f === 'low' ? 'Low Confidence' : 'All'}
              </button>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary border-b border-border-base">
                  <th className="text-left py-2 pr-3">Alias Name</th>
                  <th className="text-left py-2 pr-3">Ticker</th>
                  <th className="text-left py-2 pr-3">Exchange</th>
                  <th className="text-left py-2 pr-3">Confidence</th>
                  <th className="text-left py-2 pr-3">Source</th>
                  <th className="text-left py-2 pr-3">First Seen</th>
                  <th className="text-left py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {aliases.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-4 text-center text-text-muted text-sm">No aliases to review</td>
                  </tr>
                )}
                {aliases.map(a => (
                  <tr key={a.id} className="border-b border-border-base">
                    <td className="py-2 pr-3">{a.alias_name}</td>
                    <td className="py-2 pr-3 font-mono font-medium">{a.ticker}</td>
                    <td className="py-2 pr-3">{a.exchange}</td>
                    <td className="py-2 pr-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        a.confidence === 'high' ? 'bg-status-green-btn-hover text-status-green-muted'
                        : a.confidence === 'medium' ? 'bg-status-amber-soft text-status-amber-muted'
                        : 'bg-status-red-btn-hover text-status-red-muted'
                      }`}>{a.confidence}</span>
                    </td>
                    <td className="py-2 pr-3 text-text-secondary">{a.source}</td>
                    <td className="py-2 pr-3 text-text-secondary text-xs">
                      {new Date(a.first_seen_at).toLocaleDateString()}
                    </td>
                    <td className="py-2">
                      {!a.verified_by_human && (
                        <button
                          onClick={() => approveAlias(a.id)}
                          className="bg-status-green-btn-hover hover:bg-status-green-btn-hover text-text-bright px-2.5 py-1 rounded-btn-sm text-xs mr-1"
                        >✓ Approve</button>
                      )}
                      <button
                        onClick={() => rejectAlias(a.id)}
                        className="bg-status-red-btn-hover hover:bg-status-red-btn-hover text-text-bright px-2.5 py-1 rounded-btn-sm text-xs"
                      >✗ Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Kill Switch Section ──────────────────────────────────────────────────────

type BotMode = 'active' | 'passive' | 'paused' | 'disconnected';

interface KillSwitchStatus {
  mode: BotMode;
  updated_at: string | null;
  last_cancelled_orders: number;
  system_paused: boolean;
  connections_blocked: boolean;
}

function toAESTLocal(iso: string | null): string {
  return fmtAEST(iso);
}

function KillSwitchSection({ token }: { token: string }) {
  const [status, setStatus] = useState<KillSwitchStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState<string[]>([]);
  const [confirmAction, setConfirmAction] = useState<string | null>(null);

  const CONFIRM_MESSAGES: Record<string, string> = {
    'stop-bot': 'This will stop all automated trading and cancel all pending orders. The bot enters passive mode. Data and scanning continue normally. Manual Override can still place trades. Continue?',
    'pause-all': 'This will pause ALL scheduler jobs including data fetching and scanning. Broker connections remain alive. Scanner/Data VMs will also pause. Continue?',
    'kill-all': 'This will block all external connections. Any live holdings must be managed through your broker directly until reconnected. Continue?',
    'reconnect': 'This will unblock external connections. The bot remains paused — use Restore to resume data and scanning. Continue?',
    'restore': 'This will resume all data and scanning jobs. Trade execution remains stopped. Continue?',
    'reactivate': 'This will restore full automated trading. The bot will resume buying and selling based on signals. Continue?',
  };

  const loadStatus = useCallback(async () => {
    try {
      const res = await apiCall('/admin/kill-switch/status', token);
      if (res.ok) {
        const d = await res.json();
        setStatus(d);
      }
    } catch {}
  }, [token]);

  useEffect(() => {
    loadStatus();
    const t = setInterval(loadStatus, 30000);
    return () => clearInterval(t);
  }, [loadStatus]);

  const execute = async (action: string) => {
    setConfirmAction(null);
    setBusy(true);
    setLog([`Executing ${action}...`]);
    try {
      const res = await apiCall(`/admin/kill-switch/${action}`, token, { method: 'POST' });
      const d = await res.json();
      if (res.ok) {
        const newLog: string[] = [`✓ Mode changed to: ${d.mode}`];
        if (d.cancelled_orders !== undefined) newLog.push(`✓ Cancelled ${d.cancelled_orders} orders`);
        if (d.stopped_jobs) newLog.push(`✓ Paused ${d.stopped_jobs.length} jobs`);
        if (d.resumed_jobs) newLog.push(`✓ Resumed ${d.resumed_jobs.length} jobs`);
        if (d.restarted_jobs) newLog.push(`✓ Resumed ${d.restarted_jobs.length} jobs`);
if (d.connections_blocked !== undefined) newLog.push(`✓ Connections blocked: ${d.connections_blocked}`);
        setLog(newLog);
        await loadStatus();
      } else {
        setLog([`✗ Error: ${d.detail || JSON.stringify(d)}`]);
      }
    } catch (e: any) {
      setLog([`✗ Network error: ${e.message}`]);
    }
    setBusy(false);
  };

  const mode = status?.mode || 'active';

  const modeColor =
    mode === 'active' ? 'text-status-green' :
    mode === 'passive' ? 'text-status-amber' :
    mode === 'paused' ? 'text-status-amber-muted' :
    'text-status-red';
  const modeDot =
    mode === 'active' ? 'bg-status-green' :
    mode === 'passive' ? 'bg-status-amber' :
    mode === 'paused' ? 'bg-status-amber-muted' :
    'bg-status-red';
  const modeLabel =
    mode === 'active' ? 'Active' :
    mode === 'passive' ? 'Passive' :
    mode === 'paused' ? 'Paused' :
    'Disconnected';

  const buttons: Array<{ id: string; label: string; desc: string; enabledIn: BotMode[]; color: string; disabledTip: string }> = [
    {
      id: 'stop-bot', label: 'Stop Bot',
      desc: 'Stop trading, keep data running',
      enabledIn: ['active'], color: 'bg-status-amber-btn hover:bg-status-amber-btn-hover',
      disabledTip: 'Bot must be active',
    },
    {
      id: 'pause-all', label: 'Pause All',
      desc: 'Pause everything, keep connections',
      enabledIn: ['passive'], color: 'bg-status-amber-btn-hover hover:bg-status-amber-btn-hover',
      disabledTip: 'Bot must be in passive mode',
    },
    {
      id: 'kill-all', label: 'Kill All',
      desc: 'Disconnect all external connections',
      enabledIn: ['paused'], color: 'bg-status-red-btn hover:bg-status-red-btn-hover',
      disabledTip: 'Bot must be paused first (use Pause All)',
    },
    {
      id: 'reconnect', label: 'Reconnect',
      desc: 'Restore connections, stay paused',
      enabledIn: ['disconnected'], color: 'bg-status-blue-btn hover:bg-status-blue-btn-hover',
      disabledTip: 'Bot must be disconnected',
    },
    {
      id: 'restore', label: 'Restore',
      desc: 'Resume data and scanning',
      enabledIn: ['paused'], color: 'bg-accent hover:bg-accent-hover',
      disabledTip: 'Bot must be paused',
    },
    {
      id: 'reactivate', label: 'Reactivate',
      desc: 'Resume trading',
      enabledIn: ['passive'], color: 'bg-status-green-btn hover:bg-status-green-btn-hover',
      disabledTip: 'Bot must be in passive mode',
    },
  ];

  const byId = (id: string) => buttons.find(b => b.id === id)!;

  // Stages of the kill-chain, in order — each stage's outgoing action is the
  // button that moves the bot to the next stage in that direction.
  const forwardStages: Array<{ stageMode: BotMode; stageLabel: string; action?: string }> = [
    { stageMode: 'active', stageLabel: 'Live Mode', action: 'stop-bot' },
    { stageMode: 'passive', stageLabel: 'Passive Mode', action: 'pause-all' },
    { stageMode: 'paused', stageLabel: 'All Actions Paused Mode', action: 'kill-all' },
    { stageMode: 'disconnected', stageLabel: 'Full Disconnect Mode' },
  ];

  // Uniform "node" treatment for both stage labels and action buttons, so the
  // whole sequence reads as one connected flow rather than pills-plus-buttons.
  // returnTo is only passed for return-path buttons — it renders the "loop
  // back to X" caption in place of a drawn connector line (see grid note below).
  // Descriptions and disabled-reasons moved to a hover-only tooltip (Session 12 compression pass)
  // instead of a permanent caption line under every node — the button label plus stage names already
  // carry the flow, so the extra line was the main source of vertical bulk. returnTo stays always
  // visible since it's the only indicator of where a return action leads (no drawn connector, see note
  // below), so it can't be hidden behind hover without losing that information on first glance.
  const ActionNode = ({ btn, returnTo }: { btn: typeof buttons[number]; returnTo?: string }) => {
    const enabled = btn.enabledIn.includes(mode as BotMode) && !busy;
    return (
      <div className="relative group flex flex-col items-center shrink-0">
        <button
          onClick={() => enabled && setConfirmAction(btn.id)}
          disabled={!enabled}
          className={`${btn.color} disabled:opacity-40 disabled:cursor-not-allowed text-text-bright px-3 py-1.5 rounded-btn font-medium text-xs transition-colors whitespace-nowrap`}
        >
          {btn.label}
        </button>
        {returnTo && <span className="text-text-muted text-xs mt-0.5 text-center">↩ {returnTo}</span>}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-bg-deep text-text-secondary text-xs rounded border border-border-base whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-20 text-center">
          <div>{btn.desc}</div>
          {!enabled && <div className="text-status-amber-muted">{btn.disabledTip}</div>}
        </div>
      </div>
    );
  };

  const StageNode = ({ stageMode, stageLabel }: { stageMode: BotMode; stageLabel: string }) => {
    const isCurrent = mode === stageMode;
    return (
      <div className={`shrink-0 px-3 py-1.5 rounded-btn border text-xs font-semibold whitespace-nowrap ${
        isCurrent ? 'bg-surface-2 border-border-light text-text-bright' : 'border-border-base text-text-muted'
      }`}>
        {isCurrent && <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${modeDot}`} />}
        {stageLabel}
      </div>
    );
  };

  // Forward row: 13 grid columns — stage, arrow, action, arrow, stage, ... — so
  // stage i always lands in column i*4+1. Each return action is placed in that
  // same column as the stage that triggers it (Reactivate under Passive Mode,
  // Restore under All Actions Paused Mode, Reconnect under Full Disconnect
  // Mode), sharing one grid with the forward row so the columns line up
  // exactly without measuring element positions. There's no existing
  // precedent in this codebase for SVG connectors between dynamically
  // positioned elements (the only <svg> use is a static price-chart
  // polyline), so the "flows back to X" relationship is shown with a short
  // "↓" dropping from the trigger stage and a "↩ back to X" caption on the
  // button itself, rather than a drawn curve back to the target stage.
  const stageCol = (i: number) => i * 4 + 1;
  const returnActions: Array<{ action: string; fromStageIdx: number; toLabel: string }> = [
    { action: 'reactivate', fromStageIdx: 1, toLabel: 'Live Mode' },
    { action: 'restore', fromStageIdx: 2, toLabel: 'Passive Mode' },
    { action: 'reconnect', fromStageIdx: 3, toLabel: 'All Actions Paused Mode' },
  ];

  return (
    <div className="bg-surface rounded-card border border-border-red p-4 space-y-3">
      <div>
        <h2 className="font-semibold mb-0.5 text-text-bright text-sm">Bot Control</h2>
        <p className="text-text-secondary text-xs">
          Four-level control: Active → Passive → Paused → Disconnected. Stop Bot for normal trading pause;
          Pause All to freeze everything; Kill All to cut all external connections.
        </p>
      </div>

      {/* Branched kill-chain flow — one shared grid across both rows, see stageCol note above */}
      <div className="overflow-x-auto">
        <div className="grid gap-x-1.5 gap-y-1.5" style={{ gridTemplateColumns: 'repeat(13, max-content)' }}>
          <p style={{ gridColumn: '1 / 14', gridRow: 1 }} className="text-xs text-text-muted uppercase tracking-wide">Reduce activity</p>

          {forwardStages.map((stage, i) => {
            const col = stageCol(i);
            return (
              <React.Fragment key={stage.stageMode}>
                <div style={{ gridColumn: col, gridRow: 2 }} className="flex items-center self-center">
                  <StageNode stageMode={stage.stageMode} stageLabel={stage.stageLabel} />
                </div>
                {stage.action && (
                  <>
                    <div style={{ gridColumn: col + 1, gridRow: 2 }} className="flex items-center justify-center self-center text-text-muted">→</div>
                    <div style={{ gridColumn: col + 2, gridRow: 2 }} className="flex items-center self-center">
                      <ActionNode btn={byId(stage.action)} />
                    </div>
                    <div style={{ gridColumn: col + 3, gridRow: 2 }} className="flex items-center justify-center self-center text-text-muted">→</div>
                  </>
                )}
              </React.Fragment>
            );
          })}

          <p style={{ gridColumn: '1 / 14', gridRow: 3 }} className="text-xs text-text-muted uppercase tracking-wide mt-1">Restore activity</p>

          {returnActions.map(r => (
            <div key={`${r.action}-drop`} style={{ gridColumn: stageCol(r.fromStageIdx), gridRow: 4 }} className="flex items-center justify-center text-text-muted text-xs">↓</div>
          ))}
          {returnActions.map(r => (
            <div key={r.action} style={{ gridColumn: stageCol(r.fromStageIdx), gridRow: 5 }} className="flex items-start justify-center">
              <ActionNode btn={byId(r.action)} returnTo={r.toLabel} />
            </div>
          ))}
        </div>
      </div>

      {/* Status indicator */}
      <div className="bg-bg-deep rounded-card p-2.5 space-y-1 text-sm">
        <div className="flex items-center gap-2">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${modeDot} ${mode !== 'active' ? 'animate-pulse' : ''}`} />
          <span className={`font-semibold ${modeColor}`}>{modeLabel}</span>
          {status?.updated_at && (
            <span className="text-text-muted text-xs font-mono">— last change {toAESTLocal(status.updated_at)} AEST</span>
          )}
        </div>
        <div className="flex gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className={`inline-block w-2 h-2 rounded-full ${status?.system_paused ? 'bg-status-amber' : 'bg-status-green'}`} />
            <span className="text-text-secondary">System Paused: <span className={status?.system_paused ? 'text-status-amber' : 'text-status-green'}>{status?.system_paused ? 'Yes' : 'No'}</span></span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`inline-block w-2 h-2 rounded-full ${status?.connections_blocked ? 'bg-status-red' : 'bg-status-green'}`} />
            <span className="text-text-secondary">Connections Blocked: <span className={status?.connections_blocked ? 'text-status-red' : 'text-status-green'}>{status?.connections_blocked ? 'Yes' : 'No'}</span></span>
          </div>
        </div>
        {(status?.last_cancelled_orders ?? 0) > 0 && (
          <div className="text-text-secondary text-xs">{status?.last_cancelled_orders} orders cancelled at last stop</div>
        )}
        {log.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {log.map((line, i) => (
              <div key={i} className={`text-xs ${line.startsWith('✗') ? 'text-status-red' : 'text-text-primary'}`}>{line}</div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-bg-deep/70 flex items-center justify-center z-50">
          <div className="bg-surface border border-border-light rounded-card p-6 max-w-md mx-4 space-y-4">
            <h3 className="font-semibold text-lg text-text-bright">Confirm Action</h3>
            <p className="text-text-primary text-sm">{CONFIRM_MESSAGES[confirmAction]}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmAction(null)}
                className="bg-surface-2 hover:bg-surface-hover text-text-bright px-4 py-1.5 rounded-btn text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => execute(confirmAction)}
                className="bg-status-red-btn hover:bg-status-red-btn-hover text-text-bright px-4 py-1.5 rounded-btn text-sm font-medium"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── ML Training Card ─────────────────────────────────────────────────────────

function MLTrainingCard({ token }: { token: string }) {
  const [status, setStatus] = useState<any>(null);
  const [mode, setMode] = useState<string>('fast');
  const [toastMsg, setToastMsg] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToastMsg(msg);
    setToastType(type);
    setTimeout(() => setToastMsg(''), 4000);
  };

  const loadStatus = useCallback(async () => {
    try {
      const r = await apiCall('/admin/ml/status', token);
      if (r.ok) {
        const d = await r.json();
        setStatus(d);
        if (d.current_task === 'Serving predictions' && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    } catch {}
  }, [token]);

  useEffect(() => {
    loadStatus();
    pollRef.current = setInterval(loadStatus, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadStatus]);

  const triggerRetrain = async (modelType: 'ml' | 'rl') => {
    try {
      const r = await apiCall(`/admin/ml/retrain/${modelType}?mode=${mode}`, token, { method: 'POST' });
      if (r.ok) {
        showToast(`${modelType.toUpperCase()} retrain triggered (${mode} mode)`, 'success');
        if (!pollRef.current) {
          pollRef.current = setInterval(loadStatus, 30000);
        }
        setTimeout(loadStatus, 2000);
      } else {
        const err = await r.json().catch(() => ({}));
        showToast(err.detail || 'Failed to trigger retrain', 'error');
      }
    } catch (e: any) {
      showToast(String(e), 'error');
    }
  };

  const isTraining = status?.current_task && status.current_task !== 'Serving predictions';
  const taskColor = isTraining ? 'text-status-amber' : 'text-status-green';

  const modeDescriptions: Record<string, string> = {
    fast: '2022–present, ≤10 folds — recommended for weekly auto-retrain',
    deep: '2018–present, full folds — more data, slower',
    ultra_deep: '2006–present, 3-month folds — maximum history, very slow',
  };

  return (
    <div className="bg-surface border border-border-base rounded-lg p-4">
      <h3 className="font-semibold text-sm mb-3 text-text-bright">Model Training</h3>

      {toastMsg && (
        <div className={`mb-3 px-3 py-2 rounded text-xs font-medium ${toastType === 'success' ? 'bg-status-green-soft/50 border border-border-green text-status-green-muted' : 'bg-status-red-soft/50 border border-border-red text-status-red-muted'}`}>
          {toastMsg}
        </div>
      )}

      {status ? (
        <div className="space-y-1.5 text-xs mb-4">
          <div className="flex justify-between">
            <span className="text-text-secondary">Current task:</span>
            <span className={`font-medium ${taskColor}`}>{status.current_task}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-secondary">Storage:</span>
            <span className="text-text-primary font-mono">{status.storage_used_gb}GB / {status.storage_total_gb}GB</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-secondary">Models trained:</span>
            <span className="text-text-primary font-mono">{status.model_count} unique tickers</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-secondary">Last updated:</span>
            <span className="text-text-muted font-mono">{status.timestamp ? new Date(status.timestamp + 'Z').toLocaleTimeString() : '—'}</span>
          </div>
        </div>
      ) : (
        <div className="text-xs text-text-muted mb-4">Loading status…</div>
      )}

      <div className="mb-3">
        <label className="block text-xs text-text-secondary mb-1">
          Training mode
          <span className="ml-2 text-text-muted italic">{modeDescriptions[mode]}</span>
        </label>
        <select
          value={mode}
          onChange={e => setMode(e.target.value)}
          className="bg-surface-2 border border-border-light text-text-bright text-xs rounded px-2 py-1.5 w-full"
        >
          <option value="fast">Fast (recommended)</option>
          <option value="deep">Deep (2018–present)</option>
          <option value="ultra_deep">Ultra-Deep (2006–present)</option>
        </select>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => triggerRetrain('ml')}
          disabled={isTraining}
          className="flex-1 bg-status-blue-btn hover:bg-status-blue-btn-hover disabled:opacity-50 disabled:cursor-not-allowed text-text-bright text-xs px-2.5 py-1 rounded-btn-sm flex items-center justify-center gap-1.5"
        >
          {isTraining ? <span className="animate-spin">⟳</span> : null}
          Retrain XGBoost
        </button>
        <button
          onClick={() => triggerRetrain('rl')}
          disabled={isTraining}
          className="flex-1 bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-text-bright text-xs px-2.5 py-1 rounded-btn-sm flex items-center justify-center gap-1.5"
        >
          {isTraining ? <span className="animate-spin">⟳</span> : null}
          Retrain RL
        </button>
      </div>
    </div>
  );
}

// ─── ML Simulation Card ───────────────────────────────────────────────────────

function MLSimulationCard({ token }: { token: string }) {
  const [results, setResults] = useState<any[]>([]);
  const [simList, setSimList] = useState<any[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string>('');
  const [running, setRunning] = useState(false);
  const [exportingSelected, setExportingSelected] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);

  const loadResults = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        apiCall('/admin/ml-simulation/results', token),
        apiCall('/admin/ml/simulations', token),
      ]);
      if (r1.ok) { const d = await r1.json(); setResults(d.results || []); }
      if (r2.ok) {
        const list = await r2.json();
        setSimList(list || []);
        if (list && list.length > 0 && !selectedRunId) setSelectedRunId(list[0].run_id);
      }
    } catch {}
  }, [token]);

  useEffect(() => { loadResults(); }, [loadResults]);

  const runNow = async () => {
    setRunning(true);
    try {
      await apiCall('/admin/ml-simulation/run', token, { method: 'POST', body: '{}' });
      setTimeout(() => { loadResults(); setRunning(false); }, 8000);
    } catch { setRunning(false); }
  };

  const triggerExport = async (runId?: string) => {
    if (runId) setExportingSelected(true); else setExportingAll(true);
    try {
      const tok = sessionStorage.getItem('token') || token;
      const url = runId
        ? `/api/admin/ml/simulation-export?run_id=${encodeURIComponent(runId)}`
        : '/api/admin/ml/simulation-export';
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
      if (res.ok) {
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const cd = res.headers.get('Content-Disposition') || '';
        const match = cd.match(/filename=([^\s;]+)/);
        a.download = match ? match[1] : `simulation_export.csv`;
        a.href = objUrl;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objUrl);
      }
    } catch {}
    if (runId) setExportingSelected(false); else setExportingAll(false);
  };

  const lastRun = results[0];

  return (
    <div className="bg-surface border border-border-base rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm text-text-bright">Machine Learning Trade Simulation</h3>
        <button onClick={runNow} disabled={running}
          className="bg-accent hover:bg-accent-hover disabled:opacity-50 text-text-bright text-xs px-2.5 py-1 rounded-btn-sm">
          {running ? 'Running...' : 'Run Now'}
        </button>
      </div>
      <div className="bg-status-amber-soft/30 border border-border-amber/50 rounded p-2 text-xs text-status-amber-muted mb-3">
        ⚠ Resource-intensive — recommend running outside market hours (weekends or 06:00–09:00 AEST weekdays)
      </div>

      {/* Export controls */}
      {simList.length > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <select
            value={selectedRunId}
            onChange={e => setSelectedRunId(e.target.value)}
            className="bg-surface-2 border border-border-light text-text-primary text-xs rounded px-2 py-1.5 flex-1 min-w-0"
          >
            {simList.map(s => (
              <option key={s.run_id} value={s.run_id}>
                {s.run_id} — {s.started_at ? new Date(s.started_at).toLocaleDateString('en-AU') : '?'} — {s.win_rate != null ? `${parseFloat(s.win_rate).toFixed(1)}% win` : '?'} — {s.trades_simulated ?? '?'} trades
              </option>
            ))}
          </select>
          <button onClick={() => triggerExport(selectedRunId)} disabled={exportingSelected || !selectedRunId}
            className="bg-status-blue-btn-hover hover:bg-status-blue-btn-hover disabled:opacity-50 text-text-bright text-xs px-2.5 py-1 rounded-btn-sm whitespace-nowrap">
            {exportingSelected ? 'Exporting...' : 'Export Selected'}
          </button>
          <button onClick={() => triggerExport()} disabled={exportingAll}
            className="bg-surface-2 hover:bg-surface-hover disabled:opacity-50 text-text-bright text-xs px-2.5 py-1 rounded-btn-sm whitespace-nowrap">
            {exportingAll ? 'Exporting...' : 'Export All'}
          </button>
        </div>
      )}

      {lastRun && (
        <div className="grid grid-cols-4 gap-3 mb-3 text-xs">
          {[
            { label: 'Last Run', value: lastRun.completed_at ? new Date(lastRun.completed_at).toLocaleDateString('en-AU') : 'Running...' },
            { label: 'Trades', value: lastRun.trades_simulated || '—' },
            { label: 'Win Rate', value: lastRun.win_rate != null ? `${parseFloat(lastRun.win_rate).toFixed(1)}%` : '—' },
            { label: 'Avg Return', value: lastRun.avg_return != null ? `${parseFloat(lastRun.avg_return).toFixed(2)}%` : '—', color: parseFloat(lastRun.avg_return) >= 0 ? 'text-status-green' : 'text-status-red' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-surface-2 rounded p-2">
              <div className="text-text-secondary">{label}</div>
              <div className={`font-medium font-mono ${color || 'text-text-primary'}`}>{value}</div>
            </div>
          ))}
        </div>
      )}
      {results.length > 0 && (
        <ScrollWindow showRevealButton={false}>
          <table className="w-full text-xs">
            <thead><tr className="text-text-secondary border-b border-border-base text-left">
              {['Date', 'Trades', 'Win Rate', 'Avg Return', 'Best', 'Worst', 'Status'].map(h => (
                <th key={h} className="py-1 pr-3">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {results.map((r, i) => (
                <tr key={i} className="border-b border-border-base/50">
                  <td className="py-1 pr-3 font-mono">{new Date(r.started_at).toLocaleDateString('en-AU')}</td>
                  <td className="py-1 pr-3 font-mono">{r.trades_simulated || '—'}</td>
                  <td className="py-1 pr-3 font-mono">{r.win_rate != null ? `${parseFloat(r.win_rate).toFixed(1)}%` : '—'}</td>
                  <td className={`py-1 pr-3 font-mono ${parseFloat(r.avg_return) >= 0 ? 'text-status-green' : 'text-status-red'}`}>{r.avg_return != null ? `${parseFloat(r.avg_return).toFixed(2)}%` : '—'}</td>
                  <td className="py-1 pr-3 text-status-green font-mono">{r.best_ticker || '—'}</td>
                  <td className="py-1 pr-3 text-status-red font-mono">{r.worst_ticker || '—'}</td>
                  <td className="py-1"><span className={`px-1.5 py-0.5 rounded ${r.status === 'completed' ? 'bg-status-green-soft text-status-green-muted' : 'bg-status-amber-soft text-status-amber-muted'}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollWindow>
      )}
    </div>
  );
}

// ─── AI System Review Card ────────────────────────────────────────────────────

function AISystemReviewCard({ token }: { token: string }) {
  const [reviewing, setReviewing] = useState(false);
  const [review, setReview] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');

  const runReview = async () => {
    setReviewing(true);
    setError('');
    try {
      const res = await apiCall('/admin/system-review', token, { method: 'POST', body: '{}' });
      if (res.ok) {
        const d = await res.json();
        setReview(d.review || 'No review available');
        setShowModal(true);
      } else {
        const d = await res.json();
        setError(d.detail || 'Review failed');
      }
    } catch (e: any) { setError(e.message); }
    setReviewing(false);
  };

  return (
    <>
      <div className="bg-surface border border-border-base rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-sm text-text-bright">AI System Review</h3>
            <p className="text-text-secondary text-xs mt-1">Uses Claude AI to analyse system health, data quality, and performance.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={runReview} disabled={reviewing}
              className="bg-accent hover:bg-accent-hover disabled:opacity-50 text-text-bright text-sm px-4 py-1.5 rounded-btn font-medium flex items-center gap-2">
              {reviewing ? '⏳ Analysing...' : '🤖 Run Analysis'}
            </button>
            <button onClick={async () => {
              try {
                const res = await apiCall('/admin/system-review/export', token);
                if (!res.ok) throw new Error('Failed to export review');
                const blob = await res.blob();
                const contentDisposition = res.headers.get('Content-Disposition') || '';
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                const filename = filenameMatch ? filenameMatch[1] : 'system_review.md';
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
              } catch (err) {
                console.error('Review export failed:', err);
                alert('Failed to export review');
              }
            }}
              className="bg-status-blue-btn hover:bg-status-blue-btn-hover text-text-bright text-sm px-4 py-2 rounded-btn font-medium flex items-center gap-2">
              📥 Export Review
            </button>
          </div>
        </div>
        {error && <div className="text-status-red text-xs mt-2">{error}</div>}
      </div>

      {showModal && review && (
        <div className="fixed inset-0 bg-bg-deep/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface border border-border-base rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-text-bright">AI System Review — Analysis</h3>
              <button onClick={() => setShowModal(false)} className="text-text-secondary hover:text-text-bright text-xl">×</button>
            </div>
            <div className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed">{review}</div>
            <button onClick={() => setShowModal(false)} className="mt-4 w-full bg-surface-2 hover:bg-surface-hover text-text-bright py-2 rounded-btn text-sm">Close</button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── QA/QC Agent Card (Task 7) ────────────────────────────────────────────────

function QAQCAgentCard({ token }: { token: string }) {
  const [status, setStatus] = useState<any>(null);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    try {
      const res = await apiCall('/admin/system/qaqc-status', token);
      if (res.ok) setStatus(await res.json());
    } catch {}
  };

  useEffect(() => { load(); }, [token]);

  const runNow = async () => {
    setRunning(true);
    try {
      await apiCall('/admin/system/qaqc-run', token, { method: 'POST', body: '{}' });
      setMsg('QA/QC agent triggered — results will appear in ~30 seconds');
      setTimeout(() => { load(); setMsg(''); }, 30000);
    } catch { setMsg('Failed to trigger agent'); }
    setRunning(false);
  };

  const formatAEST = (iso?: string) => fmtAEST(iso);

  const flags: string[] = status?.flags || [];

  return (
    <div className="bg-surface border border-border-base rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm">QA/QC Agent</h3>
        <button onClick={runNow} disabled={running}
          className="bg-accent hover:bg-accent-hover disabled:opacity-50 text-text-bright text-xs px-2.5 py-1 rounded-btn-sm">
          {running ? 'Running...' : 'Run Now'}
        </button>
      </div>
      {msg && <p className="text-status-amber text-xs mb-2">{msg}</p>}
      <div className="grid grid-cols-3 gap-3 text-xs mb-3">
        <div className="bg-surface-2 rounded p-2">
          <div className="text-text-secondary">Last Run</div>
          <div className="font-medium text-text-primary text-xs">{formatAEST(status?.last_run_at)}</div>
        </div>
        <div className="bg-surface-2 rounded p-2">
          <div className="text-text-secondary">Passed</div>
          <div className="font-medium text-status-green">{status?.checks_passed ?? '—'}</div>
        </div>
        <div className="bg-surface-2 rounded p-2">
          <div className="text-text-secondary">Failed</div>
          <div className={`font-medium ${(status?.checks_failed ?? 0) > 0 ? 'text-status-red' : 'text-text-primary'}`}>{status?.checks_failed ?? '—'}</div>
        </div>
      </div>
      {status?.summary && <p className="text-text-secondary text-xs mb-2">{status.summary}</p>}
      {flags.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-text-muted font-medium">Active Flags ({flags.length}):</p>
          <div className="space-y-1 max-h-60 overflow-y-auto pr-1">
            {flags.map((f: string, i: number) => (
              <div key={i} className="text-xs bg-status-red-soft border border-border-red rounded px-2 py-1 text-status-red-muted">{f}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ticker Universe Health Check Card ───────────────────────────────────────

function TickerHealthCard({ token }: { token: string }) {
  const [data, setData] = useState<any>(null);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    try {
      const res = await apiCall('/admin/ticker-health-summary', token);
      if (res.ok) setData(await res.json());
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 300000);
    return () => clearInterval(t);
  }, [token]);

  const runNow = async () => {
    setRunning(true);
    try {
      await apiCall('/scheduler/run/ticker_universe_health_check', token, { method: 'POST' });
      setMsg('Health check triggered — results will appear in ~3 minutes');
      setTimeout(() => { load(); setMsg(''); }, 180000);
    } catch { setMsg('Failed to trigger health check'); }
    setRunning(false);
  };

  const summary = data?.summary;
  const failingTickers: any[] = data?.failing_tickers?.slice(0, 10) || [];

  return (
    <div className="bg-surface border border-border-base rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm">Ticker Universe Health Check</h3>
        <button onClick={runNow} disabled={running}
          className="bg-accent hover:bg-accent-hover disabled:opacity-50 text-text-bright text-xs px-2.5 py-1 rounded-btn-sm">
          {running ? 'Running...' : 'Run Now'}
        </button>
      </div>
      {msg && <p className="text-status-amber text-xs mb-2">{msg}</p>}
      {!summary || summary.total_checked === 0 ? (
        <p className="text-text-muted text-xs">No checks run yet — first run at 07:00 AEST daily</p>
      ) : (
        <>
          <p className="text-text-secondary text-xs mb-3">Last checked: {fmtAEST(summary.last_checked_at)} AEST</p>
          <div className="grid grid-cols-3 gap-3 text-xs mb-3">
            <div className="bg-surface-2 rounded p-2">
              <div className="text-text-secondary">Total Checked</div>
              <div className="font-medium text-text-primary">{summary.total_checked}</div>
            </div>
            <div className="bg-surface-2 rounded p-2">
              <div className="text-text-secondary">Passing</div>
              <div className="font-medium text-status-green">{summary.passing}</div>
            </div>
            <div className="bg-surface-2 rounded p-2">
              <div className="text-text-secondary">Failing</div>
              <div className={`font-medium ${summary.failing > 0 ? 'text-status-red' : 'text-text-primary'}`}>{summary.failing}</div>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2 text-xs mb-3">
            <div className="bg-surface-2 rounded p-2">
              <div className="text-text-secondary">Price</div>
              <div className={`font-medium ${summary.price_failures > 0 ? 'text-status-red' : 'text-text-primary'}`}>{summary.price_failures}</div>
            </div>
            <div className="bg-surface-2 rounded p-2">
              <div className="text-text-secondary">Broker</div>
              <div className={`font-medium ${summary.broker_failures > 0 ? 'text-status-red' : 'text-text-primary'}`}>{summary.broker_failures}</div>
            </div>
            <div className="bg-surface-2 rounded p-2">
              <div className="text-text-secondary">Exchange</div>
              <div className={`font-medium ${summary.exchange_failures > 0 ? 'text-status-red' : 'text-text-primary'}`}>{summary.exchange_failures}</div>
            </div>
            <div className="bg-surface-2 rounded p-2">
              <div className="text-text-secondary">Markit XID</div>
              <div className={`font-medium ${summary.markit_failures > 0 ? 'text-status-red' : 'text-text-primary'}`}>{summary.markit_failures}</div>
            </div>
          </div>
          {failingTickers.length > 0 && (
            <div>
              <p className="text-xs text-text-muted font-medium mb-1">Failing Tickers (up to 10):</p>
              <div className="overflow-y-auto max-h-48">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-text-secondary border-b border-border-base">
                      <th className="text-left pb-1">Ticker</th>
                      <th className="text-left pb-1">Exchange</th>
                      <th className="text-left pb-1">Failure Reasons</th>
                      <th className="text-left pb-1">Last Checked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failingTickers.map((t: any, i: number) => (
                      <tr key={i} className="border-b border-border-base/50">
                        <td className="py-1 text-text-primary font-medium">{t.ticker}</td>
                        <td className="py-1 text-text-secondary">{t.exchange}</td>
                        <td className="py-1 text-status-red">{(t.failure_reasons || []).join(', ')}</td>
                        <td className="py-1 text-text-muted">{fmtAEST(t.checked_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Tab: System ─────────────────────────────────────────────────────────────

interface VMInfo {
  id: string;
  name: string;
  vm: string;
  host: string;
  port?: number;
  status: 'online' | 'offline' | 'error';
  worker_status?: string;
  latency_ms?: number;
  timestamp?: string;
  error?: string;
  storage_used_gb?: number;
  storage_total_gb?: number;
}

interface HealthCheck {
  check: string;
  ok: boolean;
  detail?: string;
}

interface HealthReport {
  checks: HealthCheck[];
  summary?: { overall_ok: boolean; total: number; passed: number; failed: number };
}

function HealthReportPanel({ token }: { token: string }) {
  const [report, setReport] = useState<HealthReport | null>(null);
  const [checkedAt, setCheckedAt] = useState('');
  const [overallOk, setOverallOk] = useState<boolean | null>(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadReport = async () => {
    try {
      const res = await apiCall('/health/daily-report', token);
      if (res.ok) {
        const d = await res.json();
        if (d.report) {
          setReport(d.report);
          setOverallOk(d.overall_ok);
          setCheckedAt(d.checked_at ? fmtAEST(d.checked_at) : '');
        } else {
          setReport(null);
        }
      }
    } catch {}
    setLoading(false);
  };

  const runCheck = async () => {
    setRunning(true);
    try {
      await apiCall('/health/run', token, { method: 'POST' });
      await new Promise(r => setTimeout(r, 5000));
      await loadReport();
    } catch {}
    setRunning(false);
  };

  useEffect(() => { loadReport(); }, [token]);

  const checks: HealthCheck[] = report?.checks || [];

  return (
    <div className="bg-surface rounded-lg border border-border-base p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold text-sm text-text-bright">Daily Health Report</h2>
          {overallOk !== null && (
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${overallOk ? 'bg-status-green-soft text-status-green-muted' : 'bg-status-red-soft text-status-red-muted'}`}>
              {overallOk ? 'HEALTHY' : 'ISSUES FOUND'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {checkedAt && <span className="text-text-muted text-xs">{checkedAt}</span>}
          <button onClick={runCheck} disabled={running}
            className="bg-status-blue-btn-hover hover:bg-status-blue-btn-hover disabled:opacity-50 text-text-bright text-xs px-2.5 py-1 rounded-btn-sm">
            {running ? 'Running...' : 'Run Health Check Now'}
          </button>
        </div>
      </div>
      {loading ? (
        <p className="text-text-secondary text-sm">Loading...</p>
      ) : checks.length === 0 ? (
        <p className="text-text-muted text-sm">No health reports yet. Click "Run Health Check Now" to generate one.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {checks.map((c, i) => (
            <div key={i} className={`flex items-start gap-2 rounded p-2 text-xs ${c.ok ? 'bg-status-green-soft border border-border-green' : 'bg-status-red-soft border border-border-red'}`}>
              <span className={`mt-0.5 shrink-0 ${c.ok ? 'text-status-green' : 'text-status-red'}`}>{c.ok ? '✓' : '✗'}</span>
              <div>
                <p className={`font-medium ${c.ok ? 'text-status-green-muted' : 'text-status-red-muted'}`}>{c.check}</p>
                {c.detail && <p className="text-text-secondary mt-0.5">{c.detail}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
      {report?.summary && (
        <p className="text-text-muted text-xs mt-3">
          {report.summary.passed}/{report.summary.total} checks passed
        </p>
      )}
    </div>
  );
}

interface SignalHealth {
  buy_count: number;
  sell_count: number;
  hold_count: number;
  total_count: number;
  status: 'ok' | 'warning' | 'critical';
  message: string | null;
  last_buy_at: string | null;
  last_sell_at: string | null;
  ml_coverage_pct: number;
}

function SignalPipelineHealthCard({ token }: { token: string }) {
  const [health, setHealth] = useState<SignalHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await apiCall('/admin/system/signal-health', token);
      if (res.ok) {
        setHealth(await res.json());
        setLastChecked(new Date(Date.now() + 10 * 60 * 60 * 1000).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false }) + ' AEST');
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 300000); // refresh every 5 min
    return () => clearInterval(t);
  }, [load]);

  const statusBg = !health ? 'bg-bg-deep border-border-base' : health.status === 'ok' ? 'bg-status-green-soft border-border-green' : health.status === 'warning' ? 'bg-status-amber-soft border-border-amber' : 'bg-status-red-soft border-border-red';
  const statusBadge = !health ? '' : health.status === 'ok' ? 'bg-status-green-soft text-status-green-muted' : health.status === 'warning' ? 'bg-status-amber-soft text-status-amber-muted' : 'bg-status-red-soft text-status-red-muted';

  const fmtTime = (iso: string | null) => fmtAEST(iso);

  return (
    <div className={`rounded-lg border p-4 ${statusBg}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold text-sm">Signal Pipeline Health</h2>
          {health && (
            <span className={`text-xs px-2 py-0.5 rounded font-medium uppercase ${statusBadge}`}>
              {health.status}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {lastChecked && <span className="text-text-muted text-xs">Updated {lastChecked}</span>}
          <button onClick={load} className="bg-surface-2 hover:bg-surface-hover text-text-bright text-xs px-2.5 py-1 rounded-btn-sm">Refresh</button>
        </div>
      </div>

      {loading ? (
        <p className="text-text-secondary text-sm">Loading...</p>
      ) : !health ? (
        <p className="text-text-muted text-sm">Could not load signal health data.</p>
      ) : (
        <>
          {health.message && (
            <div className={`mb-3 px-3 py-2 rounded text-sm font-medium ${health.status === 'critical' ? 'bg-status-red-soft text-status-red-muted' : 'bg-status-amber-soft text-status-amber-muted'}`}>
              {health.message}
            </div>
          )}
          <div className="grid grid-cols-3 gap-3 mb-3">
            {[
              { label: 'BUY', count: health.buy_count, color: 'text-status-green' },
              { label: 'HOLD', count: health.hold_count, color: 'text-status-amber' },
              { label: 'SELL', count: health.sell_count, color: 'text-status-red' },
            ].map(({ label, count, color }) => (
              <div key={label} className="bg-surface rounded p-3 text-center">
                <div className={`text-2xl font-bold ${color}`}>{count}</div>
                <div className="text-xs text-text-secondary">{label} (24h)</div>
              </div>
            ))}
          </div>
          <div className="space-y-1 text-xs text-text-secondary">
            <div className="flex justify-between">
              <span>Last BUY signal</span>
              <span className="text-text-primary">{fmtTime(health.last_buy_at)}</span>
            </div>
            <div className="flex justify-between">
              <span>Last SELL signal</span>
              <span className="text-text-primary">{fmtTime(health.last_sell_at)}</span>
            </div>
            <div className="flex justify-between">
              <span>ML coverage (24h)</span>
              <span className={`font-medium ${health.ml_coverage_pct >= 80 ? 'text-status-green' : health.ml_coverage_pct >= 50 ? 'text-status-amber' : 'text-status-red'}`}>
                {health.ml_coverage_pct}%
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const VM_ROLE_DESCRIPTIONS: Record<string, string> = {
  'tradingbotV2': 'Signal engine, paper trading execution, API',
  'tradingbot-scanner': 'Market scanning, opportunity detection',
  'tradingbot-data': 'Price feeds, news, fundamentals, macroeconomic data',
  'tradingbot-ml': 'XGBoost training, PPO/A2C reinforcement learning',
};

function SystemDetailsSubTab({ token }: { token: string }) {
  const [master, setMaster] = useState<VMInfo | null>(null);
  const [workers, setWorkers] = useState<VMInfo[]>([]);
  const [lastChecked, setLastChecked] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await apiCall('/workers/health', token);
      if (res.ok) {
        const d = await res.json();
        setMaster(d.master);
        setWorkers(d.workers || []);
        setLastChecked(new Date(Date.now() + 10 * 60 * 60 * 1000).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false }) + ' AEST');
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const StatusDot = ({ status }: { status: string }) => {
    const colors: Record<string, string> = {
      online: 'bg-status-green', offline: 'bg-status-red', error: 'bg-status-amber',
    };
    return <span className={`inline-block w-2.5 h-2.5 rounded-full ${colors[status] || 'bg-text-muted'}`} />;
  };

  const VMCard = ({ vm }: { vm: VMInfo }) => (
    <div className={`bg-surface rounded-card border p-4 ${
      vm.status === 'online' ? 'border-border-green' :
      vm.status === 'error' ? 'border-border-amber' : 'border-border-red'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <StatusDot status={vm.status} />
          <span className="font-semibold text-sm text-text-bright">{vm.name}</span>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded font-medium ${
          vm.status === 'online' ? 'bg-status-green-soft text-status-green-muted' :
          vm.status === 'error' ? 'bg-status-amber-soft text-status-amber-muted' : 'bg-status-red-soft text-status-red-muted'
        }`}>
          {vm.status.toUpperCase()}
        </span>
      </div>
      <div className="space-y-1 text-xs text-text-secondary">
        {VM_ROLE_DESCRIPTIONS[vm.vm] && (
          <div className="text-text-muted italic mb-1.5">{VM_ROLE_DESCRIPTIONS[vm.vm]}</div>
        )}
        <div className="flex justify-between"><span>VM</span><span className="text-text-primary font-mono">{vm.vm}</span></div>
        <div className="flex justify-between"><span>IP</span><span className="text-text-primary font-mono">{vm.host}{vm.port ? `:${vm.port}` : ''}</span></div>
        {vm.latency_ms && <div className="flex justify-between"><span>Latency</span><span className="text-text-primary font-mono">{vm.latency_ms}ms</span></div>}
        <div className="flex justify-between">
          <span>Task</span>
          <span className={`font-medium ${vm.worker_status && vm.worker_status !== 'Idle' && vm.worker_status !== 'ok' ? 'text-status-amber-muted' : 'text-text-primary'}`}>
            {vm.worker_status || (vm.status === 'online' ? 'Idle' : '—')}
          </span>
        </div>
        {vm.timestamp && (
          <div className="flex justify-between">
            <span>Heartbeat</span>
            <span className="text-text-primary font-mono">{new Date(new Date(vm.timestamp).getTime() + 10 * 60 * 60 * 1000).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false })} AEST</span>
          </div>
        )}
        {vm.storage_used_gb != null && vm.storage_total_gb != null && (() => {
          const pct = vm.storage_total_gb > 0 ? vm.storage_used_gb / vm.storage_total_gb : 0;
          const color = pct > 0.9 ? 'text-status-red' : pct > 0.75 ? 'text-status-amber' : 'text-text-secondary';
          const barColor = pct > 0.9 ? 'bg-status-red' : pct > 0.75 ? 'bg-status-amber' : 'bg-status-green';
          return (
            <div className="mt-1">
              <div className={`flex justify-between ${color}`}>
                <span>Storage</span>
                <span className="font-mono">{vm.storage_used_gb} GB / {vm.storage_total_gb} GB</span>
              </div>
              <div className="mt-1 h-1.5 bg-surface-2 rounded-full overflow-hidden">
                <div className={`h-full ${barColor} rounded-full`} style={{ width: `${Math.min(pct * 100, 100).toFixed(1)}%` }} />
              </div>
            </div>
          );
        })()}
        {vm.error && <div className="text-status-red mt-1">{vm.error}</div>}
      </div>
    </div>
  );

  const allVMs = master ? [master, ...workers] : workers;

  return (
    <div className="space-y-4">
      {/* Bot Control / Kill Switch — first section */}
      <KillSwitchSection token={token} />

      {/* VM Fleet Status (merged with Worker Assignments, Session 11) */}
      <div className="bg-surface rounded-card border border-border-base p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-text-bright">VM Fleet Status</h2>
          <div className="flex items-center gap-3">
            {lastChecked && <span className="text-text-muted text-xs font-mono">Updated {lastChecked} AEST</span>}
            <button onClick={load} className="bg-surface-2 hover:bg-surface-hover text-text-bright text-xs px-2.5 py-1 rounded-btn-sm">Refresh</button>
          </div>
        </div>

        {loading ? <div className="text-text-secondary text-sm">Checking VMs...</div> : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {allVMs.map(vm => <VMCard key={vm.id} vm={vm} />)}
          </div>
        )}
      </div>
    </div>
  );
}

const SYSTEM_SUB_TABS = ['System Details', 'Security', 'Accounting'] as const;
type SystemSubTab = typeof SYSTEM_SUB_TABS[number];

function SystemParentTab({ token, initialSubTab, navKey }: { token: string; initialSubTab: SystemSubTab; navKey?: string }) {
  const [subTab, setSubTab] = useState<SystemSubTab>(initialSubTab);
  useEffect(() => { setSubTab(initialSubTab); }, [initialSubTab, navKey]);

  return (
    <div className="space-y-4">
      <div className="flex border-b border-border-base space-x-1">
        {SYSTEM_SUB_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              subTab === tab
                ? 'border-border-red text-text-bright'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {subTab === 'System Details' && <SystemDetailsSubTab token={token} />}
      {subTab === 'Security' && <LoginSecuritySection token={token} />}
      {subTab === 'Accounting' && <AccountingSubTab token={token} />}
    </div>
  );
}

// ─── Account Details field component (must be outside AccountDetailsSubTab to avoid remount on each keystroke) ─────
function AccountField({ label, field, type = 'text', placeholder = '', readonlyNote = '', vals, editing, set }: {
  label: string; field: string; type?: string; placeholder?: string; readonlyNote?: string;
  vals: Record<string, string>; editing: boolean; set: (k: string, v: string) => void;
}) {
  return (
    <div>
      <label className="block text-xs text-text-secondary mb-1">{label}</label>
      {editing ? (
        <input type={type} value={vals[field] || ''} onChange={e => set(field, e.target.value)} placeholder={placeholder}
          className="w-full bg-surface-2 border border-border-light rounded px-3 py-2 text-sm focus:outline-none focus:border-status-green" />
      ) : (
        <div className="w-full bg-bg-deep border border-border-base rounded px-3 py-2 text-sm text-text-primary">
          {type === 'password' && vals[field] ? '••••••••' : (vals[field] || <span className="text-text-muted italic">{placeholder || 'Not set'}</span>)}
        </div>
      )}
      {readonlyNote && <p className="text-xs text-text-muted mt-1">{readonlyNote}</p>}
    </div>
  );
}

// ─── Accounting moved to System (Session 11a) — Account tab deleted ───────────

// ─── Notifications Sub-tab ────────────────────────────────────────────────────

interface NotifConfig {
  id: number;
  notification_name: string;
  category: string;
  severity: string;
  show_dashboard: boolean;
  show_watch_this: boolean;
  send_email: boolean;
  show_app: boolean;
  is_system: boolean;
  is_active: boolean;
}

interface NotifRecipient {
  id: number;
  category: string;
  email: string;
  is_active: boolean;
  position: number;
}

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: 'bg-status-red-soft text-status-red-muted',
  HIGH: 'bg-status-amber-soft text-status-amber-muted',
  MEDIUM: 'bg-status-amber-soft text-status-amber-muted',
  LOW: 'bg-surface-2 text-text-secondary',
  INFO: 'bg-surface-2 text-text-primary',
};

// Must be outside NotificationsSubTab to avoid remount on each recipients state update
function RecipientGroup({ title, items, category, onUpdate, onAdd, onDelete }: {
  title: string;
  items: NotifRecipient[];
  category: string;
  onUpdate: (id: number, email: string) => void;
  onAdd: (category: string) => void;
  onDelete: (id: number) => void;
}) {
  return (
    <div>
      <p className="text-sm font-medium text-text-primary mb-2">{title}</p>
      <div className="space-y-2">
        {items.map((r, i) => (
          <RecipientInput key={r.id} index={i} recipient={r} onUpdate={onUpdate} onDelete={onDelete} />
        ))}
        {items.length < 5 && (
          <button onClick={() => onAdd(category)} className="text-xs text-status-blue hover:text-status-blue-muted">
            + Add recipient
          </button>
        )}
      </div>
    </div>
  );
}

// Separate component with local state so typing doesn't re-render parent
function RecipientInput({ index, recipient, onUpdate, onDelete }: {
  index: number;
  recipient: NotifRecipient;
  onUpdate: (id: number, email: string) => void;
  onDelete: (id: number) => void;
}) {
  const [email, setEmail] = useState(recipient.email || '');
  useEffect(() => { setEmail(recipient.email || ''); }, [recipient.email]);
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-text-muted w-16">Email {index + 1}</span>
      <input
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        onBlur={() => onUpdate(recipient.id, email)}
        placeholder="recipient@example.com"
        className="flex-1 bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm focus:outline-none focus:border-status-blue"
      />
      <button
        onClick={() => onDelete(recipient.id)}
        title="Remove recipient"
        className="text-xs text-status-red hover:text-status-red-muted px-2.5 py-1"
      >
        ✕
      </button>
    </div>
  );
}

function NotificationsSubTab({ token }: { token: string }) {
  const [notifications, setNotifications] = useState<NotifConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    apiCall('/account/notifications', token)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setNotifications(d.notifications || []);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const updateNotif = async (id: number, changes: Partial<NotifConfig>) => {
    const updated = notifications.map(n => n.id === id ? { ...n, ...changes } : n);
    setNotifications(updated);
    const notif = updated.find(n => n.id === id);
    if (!notif) return;
    try {
      await apiCall(`/account/notifications/${id}`, token, {
        method: 'PUT',
        body: JSON.stringify(notif),
      });
      setSaveMsg('Saved'); setTimeout(() => setSaveMsg(''), 2000);
    } catch {}
  };

  const systemNotifs = notifications.filter(n => n.category === 'system');
  const stockNotifs = notifications.filter(n => n.category === 'stock');
  const brokerNotifs = notifications.filter(n => n.category === 'broker');

  if (loading) return <div className="text-text-secondary text-sm">Loading...</div>;

  const NotifTable = ({ items, showWatchThis }: { items: NotifConfig[]; showWatchThis: boolean }) => (
    <div className="bg-surface rounded-card border border-border-base overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-text-secondary border-b border-border-base text-xs uppercase">
            <th className="text-left p-3">Notification</th>
            <th className="text-center p-3">Severity</th>
            <th className="text-center p-3">Dashboard</th>
            {showWatchThis && <th className="text-center p-3">Watch This</th>}
            <th className="text-center p-3">Email</th>
            <th className="text-center p-3">App</th>
          </tr>
        </thead>
        <tbody>
          {items.map(n => (
            <tr key={n.id} className="border-b border-border-base">
              <td className="p-3 font-medium text-text-primary">{n.notification_name}</td>
              <td className="p-3 text-center">
                <span className={`text-xs px-2 py-0.5 rounded ${SEVERITY_COLORS[n.severity.toUpperCase()] || 'bg-surface-2 text-text-primary'}`}>
                  {n.severity.toUpperCase()}
                </span>
              </td>
              <td className="p-3 text-center">
                <Toggle active={n.show_dashboard} onChange={() => updateNotif(n.id, { show_dashboard: !n.show_dashboard })} />
              </td>
              {showWatchThis && (
                <td className="p-3 text-center">
                  <Toggle active={n.show_watch_this} onChange={() => updateNotif(n.id, { show_watch_this: !n.show_watch_this })} />
                </td>
              )}
              <td className="p-3 text-center">
                <Toggle active={n.send_email} onChange={() => updateNotif(n.id, { send_email: !n.send_email })} />
              </td>
              <td className="p-3 text-center">
                <Toggle active={n.show_app} onChange={() => updateNotif(n.id, { show_app: !n.show_app })} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6">
      {saveMsg && <div className="text-status-green text-sm">{saveMsg}</div>}

      <div>
        <h3 className="font-semibold mb-3 text-text-bright">System Notifications</h3>
        <NotifTable items={systemNotifs} showWatchThis={false} />
      </div>

      <div>
        <h3 className="font-semibold mb-3 text-text-bright">Stock Notifications</h3>
        <NotifTable items={stockNotifs} showWatchThis={true} />
      </div>

      {brokerNotifs.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3 text-text-bright">Broker Notifications</h3>
          <NotifTable items={brokerNotifs} showWatchThis={false} />
        </div>
      )}
    </div>
  );
}

// ─── Accounting Sub-tab ───────────────────────────────────────────────────────

interface Cost {
  id: number;
  name: string;
  description: string;
  link: string;
  amount: number;
  currency: string;
  frequency: string;
  estimated_actions_per_month: number | null;
}

function monthlyEstimate(cost: Cost): number {
  const amt = Number(cost.amount) || 0;
  switch (cost.frequency) {
    case 'monthly': return amt;
    case 'daily': return amt * 30;
    case 'yearly': return amt / 12;
    case 'per_action': return amt * (cost.estimated_actions_per_month || 0);
    default: return amt;
  }
}

const AUD_RATE: Record<string, number> = { AUD: 1, USD: 1 / 0.65, EUR: 1 / 0.60, GBP: 1 / 0.50 };

function AccountingSubTab({ token }: { token: string }) {
  const [costs, setCosts] = useState<Cost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingCost, setEditingCost] = useState<Cost | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const loadCosts = useCallback(async () => {
    try {
      const res = await apiCall('/account/costs', token);
      if (res.ok) {
        const d = await res.json();
        setCosts(d.costs || []);
      }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { loadCosts(); }, [loadCosts]);

  const deleteCost = async (id: number) => {
    if (!window.confirm('Delete this cost entry?')) return;
    try {
      await apiCall(`/account/costs/${id}`, token, { method: 'DELETE' });
      setCosts(prev => prev.filter(c => c.id !== id));
    } catch {}
  };

  const saveCost = async (data: Partial<Cost>) => {
    try {
      if (data.id && data.id > 0) {
        const res = await apiCall(`/account/costs/${data.id}`, token, {
          method: 'PUT',
          body: JSON.stringify(data),
        });
        if (res.ok) {
          setCosts(prev => prev.map(c => c.id === data.id ? { ...c, ...data } as Cost : c));
        }
      } else {
        const res = await apiCall('/account/costs', token, {
          method: 'POST',
          body: JSON.stringify(data),
        });
        if (res.ok) {
          await loadCosts();
        }
      }
      setSaveMsg('Saved'); setTimeout(() => setSaveMsg(''), 2000);
      setShowModal(false);
      setEditingCost(null);
    } catch {}
  };

  const totalAud = costs.reduce((sum, c) => {
    const monthly = monthlyEstimate(c);
    const rate = AUD_RATE[c.currency] || 1;
    return sum + monthly * rate;
  }, 0);

  const blankCost: Cost = { id: -1, name: '', description: '', link: '', amount: 0, currency: 'AUD', frequency: 'monthly', estimated_actions_per_month: null };

  if (loading) return <div className="text-text-muted text-sm">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-text-bright">Running Costs</h3>
        <div className="flex items-center gap-3">
          {saveMsg && <span className="text-status-green text-sm">{saveMsg}</span>}
          <button onClick={() => { setEditingCost(blankCost); setShowModal(true); }}
            className="bg-status-green-btn hover:bg-status-green-btn-hover text-text-bright text-sm px-4 py-1.5 rounded-btn">
            + Add Cost
          </button>
        </div>
      </div>

      <div className="bg-surface rounded-card border border-border-base overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-text-secondary border-b border-border-base text-xs uppercase">
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Description</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-left p-3">Currency</th>
              <th className="text-left p-3">Frequency</th>
              <th className="text-right p-3">Monthly Est.</th>
              <th className="p-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {costs.map(c => {
              const monthly = monthlyEstimate(c);
              const inAud = monthly * (AUD_RATE[c.currency] || 1);
              return (
                <tr key={c.id} className="border-b border-border-base hover:bg-surface-hover">
                  <td className="p-3">
                    <div className="font-medium text-text-primary">{c.name}</div>
                    {c.link && (
                      <a href={c.link} target="_blank" rel="noopener noreferrer"
                        className="text-xs text-status-blue hover:underline">{c.link}</a>
                    )}
                  </td>
                  <td className="p-3 text-text-secondary text-xs max-w-[200px]">{c.description}</td>
                  <td className="p-3 text-right text-text-primary font-mono">{Number(c.amount).toFixed(4)}</td>
                  <td className="p-3 text-text-secondary">{c.currency}</td>
                  <td className="p-3 text-text-secondary">{c.frequency}{c.frequency === 'per_action' && c.estimated_actions_per_month ? ` (×${c.estimated_actions_per_month}/mo)` : ''}</td>
                  <td className="p-3 text-right">
                    <div className="font-medium text-text-primary font-mono">A${inAud.toFixed(2)}</div>
                    {c.currency !== 'AUD' && <div className="text-xs text-text-muted font-mono">{c.currency} {monthly.toFixed(4)}</div>}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => { setEditingCost(c); setShowModal(true); }}
                        className="text-xs bg-surface-2 hover:bg-surface-hover text-text-primary px-2.5 py-1 rounded-btn-sm">Edit</button>
                      <button onClick={() => deleteCost(c.id)}
                        className="text-xs bg-status-red-soft hover:bg-status-red-btn-hover text-status-red px-2.5 py-1 rounded-btn-sm">Del</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            <tr className="bg-surface-2">
              <td colSpan={5} className="p-3 font-semibold text-text-primary">Total Monthly (AUD)</td>
              <td className="p-3 text-right font-bold text-status-green font-mono">A${totalAud.toFixed(2)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      {showModal && editingCost && (
        <CostModal cost={editingCost} onSave={saveCost} onClose={() => { setShowModal(false); setEditingCost(null); }} />
      )}
    </div>
  );
}

function CostModal({ cost, onSave, onClose }: { cost: Cost; onSave: (c: Partial<Cost>) => void; onClose: () => void }) {
  const [fields, setFields] = useState({ ...cost });
  const set = (k: keyof Cost, v: any) => setFields(f => ({ ...f, [k]: v }));
  const isNew = cost.id === -1;

  return (
    <div className="fixed inset-0 bg-bg-deep/70 flex items-center justify-center z-50 p-4">
      <div className="bg-surface border border-border-light rounded-card w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg text-text-bright">{isNew ? 'Add Cost' : `Edit: ${cost.name}`}</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text-bright text-xl">×</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-text-secondary text-xs">Name</label>
            <input value={fields.name} onChange={e => set('name', e.target.value)}
              className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm mt-1 text-text-primary" />
          </div>
          <div className="col-span-2">
            <label className="text-text-secondary text-xs">Description</label>
            <input value={fields.description || ''} onChange={e => set('description', e.target.value)}
              className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm mt-1 text-text-primary" />
          </div>
          <div className="col-span-2">
            <label className="text-text-secondary text-xs">Link (URL)</label>
            <input value={fields.link || ''} onChange={e => set('link', e.target.value)}
              className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm mt-1 text-text-primary" />
          </div>
          <div>
            <label className="text-text-secondary text-xs">Amount</label>
            <input type="number" step="0.0001" value={fields.amount} onChange={e => set('amount', parseFloat(e.target.value) || 0)}
              className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm mt-1 text-text-primary font-mono" />
          </div>
          <div>
            <label className="text-text-secondary text-xs">Currency</label>
            <select value={fields.currency} onChange={e => set('currency', e.target.value)}
              className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm mt-1 text-text-primary">
              {['AUD', 'USD', 'EUR', 'GBP'].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-text-secondary text-xs">Frequency</label>
            <select value={fields.frequency} onChange={e => set('frequency', e.target.value)}
              className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm mt-1 text-text-primary">
              {['monthly', 'daily', 'yearly', 'per_action'].map(f => <option key={f}>{f}</option>)}
            </select>
          </div>
          {fields.frequency === 'per_action' && (
            <div>
              <label className="text-text-secondary text-xs">Est. Actions/Month</label>
              <input type="number" value={fields.estimated_actions_per_month ?? ''} onChange={e => set('estimated_actions_per_month', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm mt-1 text-text-primary font-mono" />
            </div>
          )}
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={() => onSave(fields)}
            className="flex-1 bg-status-green-btn hover:bg-status-green-btn-hover text-text-bright py-2 rounded-btn text-sm font-medium">
            Save
          </button>
          <button onClick={onClose}
            className="flex-1 bg-surface-2 hover:bg-surface-hover text-text-bright py-2 rounded-btn text-sm">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Login Security Section ───────────────────────────────────────────────────

interface LoginAttempt {
  id: number;
  ip_address: string;
  username?: string;
  attempted_at: string;
  reason?: string;
}

interface LoginBlock {
  id: number;
  ip_address: string;
  blocked_at: string;
  reason?: string;
  expires_at?: string;
}

function LoginSecuritySection({ token }: { token: string }) {
  const [attempts, setAttempts] = useState<LoginAttempt[]>([]);
  const [blocks, setBlocks] = useState<LoginBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState<number | null>(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const tok = sessionStorage.getItem('token') || token;
      const [attRes, blkRes] = await Promise.all([
        fetch('/api/security/login-attempts', { headers: { Authorization: `Bearer ${tok}` } }),
        fetch('/api/security/login-blocks', { headers: { Authorization: `Bearer ${tok}` } }),
      ]);
      if (attRes.ok) { const d = await attRes.json(); setAttempts(d.attempts || d.data || (Array.isArray(d) ? d : [])); }
      if (blkRes.ok) { const d = await blkRes.json(); setBlocks(d.blocks || d.data || (Array.isArray(d) ? d : [])); }
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const unblock = async (id: number) => {
    setUnblocking(id);
    try {
      const tok = sessionStorage.getItem('token') || token;
      const res = await fetch(`/api/security/login-blocks/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.ok) {
        setBlocks(prev => prev.filter(b => b.id !== id));
        setMsg('IP unblocked');
        setTimeout(() => setMsg(''), 3000);
      } else {
        setMsg('Unblock failed');
        setTimeout(() => setMsg(''), 3000);
      }
    } catch {
      setMsg('Unblock failed');
      setTimeout(() => setMsg(''), 3000);
    }
    setUnblocking(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-text-bright">Login Security</h2>
        <div className="flex items-center gap-3">
          {msg && <span className={`text-sm ${msg.includes('failed') ? 'text-status-red' : 'text-status-green'}`}>{msg}</span>}
          <button onClick={load} className="bg-surface-2 hover:bg-surface-hover text-text-bright text-xs px-2.5 py-1 rounded-btn-sm">Refresh</button>
        </div>
      </div>

      {/* Blocked IPs */}
      <div className="bg-surface rounded-card border border-border-base">
        <div className="px-4 py-3 border-b border-border-base">
          <h3 className="font-semibold text-sm text-text-bright">Blocked IPs</h3>
          <p className="text-text-muted text-xs mt-0.5">IPs currently blocked from logging in (most recent 10)</p>
        </div>
        {loading ? (
          <div className="px-4 py-3 text-text-secondary text-sm">Loading...</div>
        ) : blocks.length === 0 ? (
          <div className="px-4 py-3 text-text-muted text-sm">No IPs currently blocked.</div>
        ) : (
          <ScrollWindow showRevealButton={false}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-muted border-b border-border-base text-xs uppercase">
                  <th className="text-left px-4 py-2">IP Address</th>
                  <th className="text-left px-4 py-2">Blocked At</th>
                  <th className="text-left px-4 py-2">Expires</th>
                  <th className="text-left px-4 py-2">Reason</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {blocks.map(b => (
                  <tr key={b.id} className="border-b border-border-base/50 hover:bg-surface-hover/30">
                    <td className="px-4 py-2.5 font-mono text-status-red-muted">{b.ip_address}</td>
                    <td className="px-4 py-2.5 text-text-secondary text-xs font-mono">
                      {fmtAEST(b.blocked_at)}
                    </td>
                    <td className="px-4 py-2.5 text-text-secondary text-xs font-mono">
                      {b.expires_at ? fmtAEST(b.expires_at) : 'Permanent'}
                    </td>
                    <td className="px-4 py-2.5 text-text-secondary text-xs">{b.reason || '—'}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => unblock(b.id)}
                        disabled={unblocking === b.id}
                        className="text-xs bg-status-green-btn-hover hover:bg-status-green-btn-hover disabled:opacity-50 text-text-bright px-2.5 py-1 rounded-btn-sm"
                      >
                        {unblocking === b.id ? 'Unblocking...' : 'Unblock'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollWindow>
        )}
      </div>

      {/* Recent Failed Login Attempts */}
      <div className="bg-surface rounded-card border border-border-base">
        <div className="px-4 py-3 border-b border-border-base">
          <h3 className="font-semibold text-sm text-text-bright">Recent Failed Login Attempts</h3>
          <p className="text-text-muted text-xs mt-0.5">Last {attempts.length} failed attempts</p>
        </div>
        {loading ? (
          <div className="px-4 py-3 text-text-secondary text-sm">Loading...</div>
        ) : attempts.length === 0 ? (
          <div className="px-4 py-3 text-text-muted text-sm">No failed login attempts recorded.</div>
        ) : (
          <ScrollWindow showRevealButton={false}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-muted border-b border-border-base text-xs uppercase">
                  <th className="text-left px-4 py-2">IP Address</th>
                  <th className="text-left px-4 py-2">Username</th>
                  <th className="text-left px-4 py-2">Time</th>
                  <th className="text-left px-4 py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map(a => (
                  <tr key={a.id} className="border-b border-border-base/50 hover:bg-surface-hover/30">
                    <td className="px-4 py-2.5 font-mono text-status-amber-muted">{a.ip_address}</td>
                    <td className="px-4 py-2.5 text-text-primary">{a.username || '—'}</td>
                    <td className="px-4 py-2.5 text-text-secondary text-xs font-mono">
                      {fmtAEST(a.attempted_at)}
                    </td>
                    <td className="px-4 py-2.5 text-text-secondary text-xs">{a.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollWindow>
        )}
      </div>
    </div>
  );
}

// ─── Notification Inbox ───────────────────────────────────────────────────────

interface InboxAlert {
  id: number;
  notification_key: string;
  severity: string;
  heading: string;
  short_body: string;
  long_body: string;
  ticker?: string;
  exchange?: string;
  is_read: boolean;
  delivered_email: boolean;
  created_at: string;
}

function NotificationInboxTab({ token, initialAlertId, navKey }: { token: string; initialAlertId: number | null; navKey?: string }) {
  const [alerts, setAlerts] = useState<InboxAlert[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(initialAlertId);
  useEffect(() => { setSelectedId(initialAlertId); }, [initialAlertId, navKey]);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterRead, setFilterRead] = useState('');
  const LIMIT = 20;

  const load = async (off = 0) => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: String(LIMIT), offset: String(off) });
      if (filterCategory) p.set('category', filterCategory);
      if (filterSeverity) p.set('severity', filterSeverity);
      if (filterRead !== '') p.set('is_read', filterRead);
      const r = await fetch(`/api/admin/alerts?${p}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) {
        const d = await r.json();
        setAlerts(d.items || []);
        setTotal(d.total || 0);
        setOffset(off);
      }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(0); }, [filterCategory, filterSeverity, filterRead]);

  useEffect(() => {
    if (initialAlertId) {
      fetch(`/api/admin/alerts/${initialAlertId}/read`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  }, [initialAlertId]);

  const severityBadge = (sev: string) => SEVERITY_COLORS[sev] || 'bg-surface-2 text-text-primary';

  const selected = alerts.find(a => a.id === selectedId) || null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          className="bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm text-text-primary">
          <option value="">All Categories</option>
          <option value="system">System</option>
          <option value="stock">Stock</option>
        </select>
        <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}
          className="bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm text-text-primary">
          <option value="">All Severities</option>
          <option value="CRITICAL">Critical</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="INFO">Info</option>
        </select>
        <select value={filterRead} onChange={e => setFilterRead(e.target.value)}
          className="bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm text-text-primary">
          <option value="">All</option>
          <option value="false">Unread</option>
          <option value="true">Read</option>
        </select>
        <span className="text-text-muted text-sm self-center">{total} notifications</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-2">
          {loading && <p className="text-text-muted text-sm">Loading...</p>}
          {!loading && alerts.length === 0 && <p className="text-text-muted text-sm">No notifications found.</p>}
          {alerts.map(a => (
            <div key={a.id} onClick={() => {
                setSelectedId(a.id);
                if (!a.is_read) {
                  fetch(`/api/admin/alerts/${a.id}/read`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
                  setAlerts(prev => prev.map(x => x.id === a.id ? { ...x, is_read: true } : x));
                }
              }}
              className={`p-3 rounded-card border cursor-pointer transition-colors ${selectedId === a.id ? 'border-border-blue bg-status-blue-soft' : 'border-border-base hover:border-border-light'} ${!a.is_read ? 'bg-surface' : 'bg-bg-deep opacity-75'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {!a.is_read && <span className="w-2 h-2 bg-status-blue rounded-full shrink-0" />}
                  <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${severityBadge(a.severity)}`}>{a.severity}</span>
                  {a.ticker && <span className="text-xs text-text-secondary font-mono">{a.ticker}{a.exchange ? `:${a.exchange}` : ''}</span>}
                </div>
                <span className="text-xs text-text-muted shrink-0">
                  {new Date(a.created_at.endsWith('Z') ? a.created_at : a.created_at + 'Z').toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <p className="text-sm font-medium text-text-primary mt-1">{a.heading || a.notification_key}</p>
              <p className="text-xs text-text-secondary mt-0.5 line-clamp-2">{a.short_body}</p>
            </div>
          ))}
          {total > LIMIT && (
            <div className="flex gap-2 pt-2">
              <button disabled={offset === 0} onClick={() => load(offset - LIMIT)}
                className="px-4 py-1.5 text-sm bg-surface-2 text-text-primary rounded-btn disabled:opacity-40">← Prev</button>
              <span className="text-text-muted text-sm self-center">{offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
              <button disabled={offset + LIMIT >= total} onClick={() => load(offset + LIMIT)}
                className="px-4 py-1.5 text-sm bg-surface-2 text-text-primary rounded-btn disabled:opacity-40">Next →</button>
            </div>
          )}
        </div>

        <div className="bg-surface border border-border-base rounded-card p-4 min-h-48">
          {!selected ? (
            <p className="text-text-muted text-sm">Select a notification to view full details.</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${severityBadge(selected.severity)}`}>{selected.severity}</span>
                {selected.ticker && <span className="text-sm font-medium text-text-secondary font-mono">{selected.ticker}{selected.exchange ? `:${selected.exchange}` : ''}</span>}
                <span className="text-xs text-text-muted ml-auto">
                  {new Date(selected.created_at.endsWith('Z') ? selected.created_at : selected.created_at + 'Z').toLocaleString('en-AU', { timeZone: 'Australia/Sydney', dateStyle: 'medium', timeStyle: 'short' })} AEST
                </span>
              </div>
              <h3 className="font-semibold text-text-bright">{selected.heading || selected.notification_key}</h3>
              <div className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed border-t border-border-base pt-3">
                {selected.long_body || selected.short_body || selected.heading}
              </div>
              <div className="flex gap-3 text-xs text-text-muted border-t border-border-base pt-2">
                {selected.delivered_email && <span>✉ Emailed</span>}
                <span className="font-mono text-text-muted">{selected.notification_key}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationEmailsSubTab({ token }: { token: string }) {
  const [config, setConfig] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [recipients, setRecipients] = useState<NotifRecipient[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(true);
  const [recipientMsg, setRecipientMsg] = useState('');

  useEffect(() => {
    apiCall('/account/full', token)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.config) { setConfig(d.config); setDraft(d.config); } })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    apiCall('/account/notifications', token)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setRecipients(d.recipients || []); })
      .catch(() => {})
      .finally(() => setRecipientsLoading(false));
  }, [token]);

  const startEdit = () => { setDraft({ ...config }); setEditing(true); };
  const cancelEdit = () => { setDraft({ ...config }); setEditing(false); };
  const set = (k: string, v: string) => setDraft(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiCall('/account/save', token, { method: 'POST', body: JSON.stringify(draft) });
      if (res.ok) {
        setConfig({ ...draft });
        setEditing(false);
        setMsg('Saved');
        setTimeout(() => setMsg(''), 3000);
      } else {
        setMsg('Save failed');
        setTimeout(() => setMsg(''), 3000);
      }
    } catch { setMsg('Error'); setTimeout(() => setMsg(''), 3000); }
    finally { setSaving(false); }
  };

  const updateRecipient = async (id: number, email: string) => {
    const updated = recipients.map(r => r.id === id ? { ...r, email } : r);
    setRecipients(updated);
    try {
      await apiCall(`/account/notification-recipients/${id}`, token, {
        method: 'PUT',
        body: JSON.stringify({ email, is_active: true }),
      });
      setRecipientMsg('Saved'); setTimeout(() => setRecipientMsg(''), 2000);
    } catch {}
  };

  const addRecipient = async (category: string) => {
    const catRecipients = recipients.filter(r => r.category === category);
    const pos = catRecipients.length + 1;
    try {
      const res = await apiCall('/account/notification-recipients', token, {
        method: 'POST',
        body: JSON.stringify({ category, email: '', position: pos, is_active: true }),
      });
      if (res.ok) {
        const d = await res.json();
        setRecipients(prev => [...prev, { id: d.id, category, email: '', is_active: true, position: pos }]);
      }
    } catch {}
  };

  const deleteRecipient = async (id: number) => {
    const prev = recipients;
    setRecipients(r => r.filter(x => x.id !== id));
    try {
      const res = await apiCall(`/account/notification-recipients/${id}`, token, { method: 'DELETE' });
      if (res.ok) {
        setRecipientMsg('Removed'); setTimeout(() => setRecipientMsg(''), 2000);
      } else {
        setRecipients(prev);
      }
    } catch {
      setRecipients(prev);
    }
  };

  const systemRecipients = recipients.filter(r => r.category === 'system');
  const stockRecipients = recipients.filter(r => r.category === 'stock');

  const vals = editing ? draft : config;

  return (
    <div className="space-y-6">
      {/* Edit toolbar */}
      <div className="flex items-center gap-3">
        {!editing ? (
          <button onClick={startEdit} className="bg-status-blue-btn hover:bg-status-blue-btn-hover text-text-bright px-4 py-1.5 rounded-btn text-sm font-medium">
            Edit
          </button>
        ) : (
          <>
            <button onClick={save} disabled={saving} className="bg-status-green-btn hover:bg-status-green-btn-hover disabled:opacity-50 text-text-bright px-4 py-1.5 rounded-btn text-sm font-medium">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={cancelEdit} className="bg-surface-2 hover:bg-surface-hover text-text-bright px-4 py-1.5 rounded-btn text-sm">
              Cancel
            </button>
          </>
        )}
        {msg && <span className={`text-sm ${msg === 'Saved' ? 'text-status-green' : 'text-status-red'}`}>{msg}</span>}
        {!editing && <span className="text-text-muted text-xs">All fields are read-only. Click Edit to make changes.</span>}
      </div>

      {/* Bot Email Configuration (moved from Account > Account Details, Session 11) */}
      <div className="bg-surface border border-border-base rounded-card p-5">
        <h3 className="font-semibold mb-1 text-text-bright">Bot Email Configuration</h3>
        <p className="text-text-secondary text-xs mb-4">Used for inbound email monitoring and alert delivery. Requires an Email App Password (not your login password).</p>
        <div className="grid grid-cols-2 gap-4">
          <AccountField label="Email Address" field="gmail_address" placeholder="camerontrade9999@gmail.com" readonlyNote="Written to config/.env as GMAIL_ADDRESS" vals={vals} editing={editing} set={set} />
          <AccountField label="Email App Password" field="gmail_app_password" type="password" placeholder="xxxx xxxx xxxx xxxx" readonlyNote="Written to config/.env as GMAIL_APP_PASSWORD" vals={vals} editing={editing} set={set} />
        </div>
      </div>

      {/* Alert Recipients (moved from Notification Matrix, Session 11a) */}
      <div className="bg-surface border border-border-base rounded-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-text-bright">Alert Recipients</h3>
          {recipientMsg && <span className="text-status-green text-sm">{recipientMsg}</span>}
        </div>
        {recipientsLoading ? (
          <div className="text-text-secondary text-sm">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <RecipientGroup title="System Alert Recipients" items={systemRecipients} category="system" onUpdate={updateRecipient} onAdd={addRecipient} onDelete={deleteRecipient} />
            <RecipientGroup title="Stock Alert Recipients" items={stockRecipients} category="stock" onUpdate={updateRecipient} onAdd={addRecipient} onDelete={deleteRecipient} />
          </div>
        )}
      </div>
    </div>
  );
}

const NOTIFICATIONS_SUB_TABS = ['Inbox', 'Matrix', 'Emails'] as const;
type NotificationsSubTabId = typeof NOTIFICATIONS_SUB_TABS[number];

function NotificationsPageTab({ token, initialAlertId, navKey }: { token: string; initialAlertId: number | null; navKey?: string }) {
  const [subTab, setSubTab] = useState<NotificationsSubTabId>('Inbox');
  // A deep link carrying a specific alert (e.g. from the header notification
  // bell) should always land on the Inbox, even if the user was previously
  // sitting on a different Notifications sub-tab. navKey changes on every
  // navigation (even a repeat click for the same alert id), which a
  // dependency on initialAlertId alone would miss.
  useEffect(() => { if (initialAlertId != null) setSubTab('Inbox'); }, [initialAlertId, navKey]);

  const subTabLabel = (t: NotificationsSubTabId) =>
    t === 'Inbox' ? 'Notification Inbox' : t === 'Matrix' ? 'Notification Matrix' : 'Notification Emails';

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-text-bright">Notifications</h1>
        <p className="text-text-secondary text-sm mt-1">Notification inbox and delivery configuration</p>
      </div>
      <div className="flex gap-2 border-b border-border-base pb-0">
        {NOTIFICATIONS_SUB_TABS.map(t => (
          <button key={t} onClick={() => setSubTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${subTab === t ? 'border-border-blue text-status-blue' : 'border-transparent text-text-secondary hover:text-text-primary'}`}>
            {subTabLabel(t)}
          </button>
        ))}
      </div>
      {subTab === 'Inbox' && <NotificationInboxTab token={token} initialAlertId={initialAlertId} navKey={navKey} />}
      {subTab === 'Matrix' && <NotificationsSubTab token={token} />}
      {subTab === 'Emails' && <NotificationEmailsSubTab token={token} />}
    </div>
  );
}

// ─── Ticker Management Tab ───────────────────────────────────────────────────

interface FlaggedTicker {
  ticker: string;
  exchange: string;
  company_name: string;
  consecutive_failures: number;
  flag_status: string | null;
  flag_reason: string | null;
  delisted_flagged_at: string | null;
  last_failure_at: string | null;
  is_active: boolean;
}

function TickerManagementTab({ token }: { token: string }) {
  const [flagged, setFlagged] = useState<FlaggedTicker[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [filterExchange, setFilterExchange] = useState('All');
  const [clearingFlags, setClearingFlags] = useState(false);

  // Add ticker state
  const [tickerInput, setTickerInput] = useState('');
  const [exchangeInput, setExchangeInput] = useState('ASX');
  const [companyInput, setCompanyInput] = useState('');
  const [validationResult, setValidationResult] = useState<{
    success: boolean; validated: boolean; price: number | null;
    message: string; ticker?: string;
  } | null>(null);
  const [adding, setAdding] = useState(false);

  const loadFlagged = async () => {
    setLoading(true);
    try {
      const res = await apiCall('/admin/ticker-management/flagged', token);
      if (res.ok) {
        const d = await res.json();
        setFlagged(d.flagged || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => {
    loadFlagged();
    const interval = setInterval(loadFlagged, 60000);
    return () => clearInterval(interval);
  }, [token]);

  const doAction = async (action: string, ticker: string, exchange: string) => {
    try {
      const res = await apiCall(
        `/admin/ticker-management/${action}/${ticker}/${exchange}`,
        token, { method: 'POST' }
      );
      const d = await res.json();
      if (res.ok && d.success) {
        setActionMsg(`${ticker}: ${action} successful`);
        loadFlagged();
      } else {
        setActionMsg(`Error: ${d.detail || JSON.stringify(d)}`);
      }
    } catch {
      setActionMsg('Action failed');
    }
    setTimeout(() => setActionMsg(''), 4000);
  };

  const doClearFlags = async () => {
    const pendingCount = flagged.filter(t => t.flag_status === 'pending_review').length;
    const msg = `This will clear ${pendingCount} flagged ticker${pendingCount !== 1 ? 's' : ''}. Archived tickers will not be affected. Continue?`;
    if (!window.confirm(msg)) return;
    setClearingFlags(true);
    try {
      const res = await apiCall('/admin/ticker-management/clear-flags', token, { method: 'POST' });
      const d = await res.json();
      if (res.ok && d.success) {
        setActionMsg(`Cleared ${d.cleared} flagged ticker${d.cleared !== 1 ? 's' : ''}`);
        loadFlagged();
      } else {
        setActionMsg(`Error: ${d.detail || JSON.stringify(d)}`);
      }
    } catch {
      setActionMsg('Clear flags failed');
    }
    setClearingFlags(false);
    setTimeout(() => setActionMsg(''), 5000);
  };

  const doAdd = async (force = false) => {
    if (!tickerInput.trim()) return;
    setAdding(true);
    setValidationResult(null);
    try {
      const res = await apiCall('/admin/ticker-management/add', token, {
        method: 'POST',
        body: JSON.stringify({
          ticker: tickerInput.trim().toUpperCase(),
          exchange: exchangeInput,
          company_name: companyInput.trim(),
          force,
        }),
      });
      const d = await res.json();
      if (!res.ok) {
        setValidationResult({ success: false, validated: false, price: null, message: d.detail || 'Error' });
      } else {
        setValidationResult(d);
        if (d.success) {
          setTickerInput('');
          setCompanyInput('');
          loadFlagged();
        }
      }
    } catch {
      setValidationResult({ success: false, validated: false, price: null, message: 'Request failed' });
    }
    setAdding(false);
  };

  const statusBadge = (status: string | null) => {
    if (!status) return null;
    const colours: Record<string, string> = {
      pending_review: 'bg-status-amber-soft text-status-amber-muted',
      archived: 'bg-status-red-soft text-status-red-muted',
      reinstated: 'bg-status-green-soft text-status-green-muted',
    };
    return (
      <span className={`text-xs px-1.5 py-0.5 rounded ${colours[status] || 'bg-surface-2 text-text-primary'}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const relativeTime = (iso: string | null) => {
    if (!iso) return '—';
    const diff = Date.now() - new Date(iso).getTime();
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(h / 24);
    if (d > 0) return `${d}d ago`;
    if (h > 0) return `${h}h ago`;
    return '<1h ago';
  };

  const filtered = filterExchange === 'All'
    ? flagged
    : flagged.filter(t => t.exchange === filterExchange);

  const exchanges = ['All', ...Array.from(new Set(flagged.map(t => t.exchange)))];

  return (
    <div className="space-y-6">
      {/* Add Ticker Section */}
      <div className="bg-surface rounded-lg border border-border-base p-5">
        <h2 className="font-semibold mb-3">Add Ticker</h2>
        <div className="flex gap-2 flex-wrap items-end">
          <div>
            <label className="text-xs text-text-secondary block mb-1">Ticker</label>
            <input
              value={tickerInput}
              onChange={e => setTickerInput(e.target.value.toUpperCase())}
              placeholder="e.g. CBA"
              className="bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm w-28"
            />
          </div>
          <div>
            <label className="text-xs text-text-secondary block mb-1">Exchange</label>
            <select
              value={exchangeInput}
              onChange={e => setExchangeInput(e.target.value)}
              className="bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm"
            >
              <option>ASX</option>
              <option>NYSE</option>
              <option>NASDAQ</option>
              <option>AMEX</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-text-secondary block mb-1">Company (optional)</label>
            <input
              value={companyInput}
              onChange={e => setCompanyInput(e.target.value)}
              placeholder="Auto-lookup"
              className="bg-surface-2 border border-border-light rounded px-3 py-1.5 text-sm w-48"
            />
          </div>
          <button
            onClick={() => doAdd(false)}
            disabled={adding || !tickerInput.trim()}
            className="bg-status-blue-btn hover:bg-status-blue-btn-hover disabled:opacity-50 text-text-bright px-4 py-1.5 rounded-btn text-sm font-medium"
          >
            {adding ? 'Checking...' : 'Validate & Add'}
          </button>
        </div>

        {validationResult && (
          <div className={`mt-3 text-sm ${validationResult.success ? 'text-status-green' : validationResult.validated === false && validationResult.message?.includes('force') ? 'text-status-amber' : 'text-status-red'}`}>
            {validationResult.success
              ? `✓ ${validationResult.ticker} added to watchlist${validationResult.price ? ` — $${validationResult.price.toFixed(2)}` : ''}`
              : validationResult.message}
            {!validationResult.success && validationResult.message?.includes('force') && (
              <button
                onClick={() => doAdd(true)}
                className="ml-3 underline text-status-amber-muted text-xs"
              >
                Add Anyway
              </button>
            )}
          </div>
        )}
      </div>

      {/* Flagged Tickers Section */}
      <div className="bg-surface rounded-lg border border-border-base p-5">
        <p className="text-text-muted text-xs mb-3">
          Review queue for tickers the automated scan has flagged. See System Monitoring → QC/QA Checks → Ticker Universe Health for the underlying pass/fail scan results.
        </p>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h2 className="font-semibold">Flagged Tickers</h2>
            {flagged.length > 0 && (
              <span className="bg-status-amber-soft text-status-amber-muted text-xs px-2 py-0.5 rounded-full">
                {flagged.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={filterExchange}
              onChange={e => setFilterExchange(e.target.value)}
              className="bg-surface-2 border border-border-light rounded px-2 py-1 text-xs"
            >
              {exchanges.map(ex => <option key={ex}>{ex}</option>)}
            </select>
            {flagged.some(t => t.flag_status === 'pending_review') && (
              <button
                onClick={doClearFlags}
                disabled={clearingFlags}
                className="bg-status-red-soft hover:bg-status-red-btn-hover disabled:opacity-50 text-status-red-muted px-2.5 py-1 rounded-btn-sm text-xs font-medium"
              >
                {clearingFlags ? 'Clearing...' : 'Clear All Flags'}
              </button>
            )}
            <button
              onClick={loadFlagged}
              className="bg-surface-2 hover:bg-surface-hover text-text-primary px-2.5 py-1 rounded-btn-sm text-xs"
            >
              ↻ Refresh
            </button>
          </div>
        </div>

        {actionMsg && (
          <p className={`text-sm mb-3 ${actionMsg.includes('Error') ? 'text-status-red' : 'text-status-green'}`}>
            {actionMsg}
          </p>
        )}

        {loading ? (
          <p className="text-text-secondary text-sm">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-text-muted text-sm">No flagged tickers</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary text-xs border-b border-border-base">
                  <th className="text-left py-2 pr-4">Ticker</th>
                  <th className="text-left py-2 pr-4">Exchange</th>
                  <th className="text-left py-2 pr-4">Company</th>
                  <th className="text-right py-2 pr-4">Failures</th>
                  <th className="text-left py-2 pr-4">Status</th>
                  <th className="text-left py-2 pr-4">Flagged</th>
                  <th className="text-left py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={`${t.ticker}-${t.exchange}`} className="border-b border-border-base/50 hover:bg-surface-hover/30">
                    <td className="py-2 pr-4 font-mono font-medium">{t.ticker}</td>
                    <td className="py-2 pr-4 text-text-primary">{t.exchange}</td>
                    <td className="py-2 pr-4 text-text-secondary text-xs">{t.company_name || '—'}</td>
                    <td className="py-2 pr-4 text-right text-status-amber">{t.consecutive_failures}</td>
                    <td className="py-2 pr-4">{statusBadge(t.flag_status)}</td>
                    <td className="py-2 pr-4 text-text-secondary text-xs">{relativeTime(t.delisted_flagged_at || t.last_failure_at)}</td>
                    <td className="py-2">
                      <div className="flex gap-1">
                        <button
                          onClick={() => doAction('archive', t.ticker, t.exchange)}
                          className="bg-status-amber-soft hover:bg-status-amber-btn text-status-amber-muted px-2.5 py-1 rounded-btn-sm text-xs"
                        >
                          Archive
                        </button>
                        <button
                          onClick={() => doAction('dismiss', t.ticker, t.exchange)}
                          className="bg-status-blue-soft hover:bg-status-blue-btn-hover text-status-blue-muted px-2.5 py-1 rounded-btn-sm text-xs"
                        >
                          Keep
                        </button>
                        <a
                          href={`https://www.google.com/search?q=${t.ticker}+${t.exchange}+delisted`}
                          target="_blank"
                          rel="noreferrer"
                          className="bg-surface-2 hover:bg-surface-hover text-text-primary px-2 py-0.5 rounded text-xs"
                        >
                          Search
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── System Monitoring Tab (parent) — Session 10 ──────────────────────────────

function ExchangeMonitoringSubTab({ token }: { token: string }) {
  return (
    <div className="space-y-4">
      <FailedOrderCooldownsSection token={token} />
      <CompanyAliasesSection token={token} />
    </div>
  );
}

function QCQAChecksSubTab({ token }: { token: string }) {
  return (
    <div className="space-y-4">
      <p className="text-text-muted text-xs">
        Automated data-quality and pipeline checks. See Ticker Management → Flagged Tickers for the manual review/action queue these checks feed into.
      </p>
      <AISystemReviewCard token={token} />
      <HealthReportPanel token={token} />
      <SignalPipelineHealthCard token={token} />
      <TickerHealthCard token={token} />
      <QAQCAgentCard token={token} />
    </div>
  );
}

function MachineLearningSubTab({ token }: { token: string }) {
  return (
    <div className="space-y-4">
      <MLTrainingCard token={token} />
      <MLSimulationCard token={token} />
    </div>
  );
}

const SYSTEM_MONITORING_SUB_TABS = ['Exchange Monitoring', 'QC/QA Checks', 'Machine Learning'] as const;
type SystemMonitoringSubTab = typeof SYSTEM_MONITORING_SUB_TABS[number];

function SystemMonitoringParentTab({ token, initialSubTab, navKey }: { token: string; initialSubTab: SystemMonitoringSubTab; navKey?: string }) {
  const [subTab, setSubTab] = useState<SystemMonitoringSubTab>(initialSubTab);
  useEffect(() => { setSubTab(initialSubTab); }, [initialSubTab, navKey]);

  return (
    <div className="space-y-4">
      <div className="flex border-b border-border-base space-x-1">
        {SYSTEM_MONITORING_SUB_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              subTab === tab
                ? 'border-border-blue text-text-bright'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {subTab === 'Exchange Monitoring' && <ExchangeMonitoringSubTab token={token} />}
      {subTab === 'QC/QA Checks' && <QCQAChecksSubTab token={token} />}
      {subTab === 'Machine Learning' && <MachineLearningSubTab token={token} />}
    </div>
  );
}

// ─── Data Management Tab (parent) ────────────────────────────────────────────

const DATA_MANAGEMENT_SUB_TABS = ['Data Sources', 'Signals & Fundamentals', 'Ticker Management'] as const;
type DataManagementSubTab = typeof DATA_MANAGEMENT_SUB_TABS[number];

function DataManagementTab({ token, initialSubTab, navKey }: { token: string; initialSubTab: DataManagementSubTab; navKey?: string }) {
  const [subTab, setSubTab] = useState<DataManagementSubTab>(initialSubTab);
  useEffect(() => { setSubTab(initialSubTab); }, [initialSubTab, navKey]);

  return (
    <div className="space-y-4">
      <div className="flex border-b border-border-base space-x-1">
        {DATA_MANAGEMENT_SUB_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              subTab === tab
                ? 'border-border-green text-text-bright'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {subTab === 'Data Sources' && <DataSourcesTab token={token} />}
      {subTab === 'Signals & Fundamentals' && <SignalsTab token={token} />}
      {subTab === 'Ticker Management' && <TickerManagementTab token={token} />}
    </div>
  );
}

// ─── Main Admin Page ──────────────────────────────────────────────────────────

const TABS = ['Data Management', 'Exchange Management', 'System Monitoring', 'Notifications', 'System'] as const;
type Tab = typeof TABS[number];

function Admin() {
  const token = sessionStorage.getItem('token') || '';
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialAlertId = params.get('alert') ? parseInt(params.get('alert')!) : null;
  const tabFromUrl = params.get('tab');
  const initialDataManagementSubTab: DataManagementSubTab = tabFromUrl === 'signals' ? 'Signals & Fundamentals' : 'Data Sources';
  const initialExchangeManagementSubTab: ExchangeManagementSubTab = tabFromUrl === 'exchanges' ? 'Broker & Exchange Management' : 'Strategy & Risk';
  const initialSystemMonitoringSubTab: SystemMonitoringSubTab = 'Exchange Monitoring';
  const viewFromUrl = params.get('view');
  const initialSystemSubTab: SystemSubTab = viewFromUrl === 'security' ? 'Security'
    : viewFromUrl === 'accounting' ? 'Accounting'
    : tabFromUrl === 'account' ? 'Accounting'
    : 'System Details';
  const initialTab: Tab = tabFromUrl === 'Notifications' ? 'Notifications'
    : tabFromUrl === 'system' ? 'System'
    : tabFromUrl === 'account' ? 'System'
    : tabFromUrl === 'signals' ? 'Data Management'
    : tabFromUrl === 'strategy' ? 'Exchange Management'
    : tabFromUrl === 'exchanges' ? 'Exchange Management'
    : tabFromUrl === 'monitoring' ? 'System Monitoring'
    : 'Data Management';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  // Admin stays mounted across query-string-only navigation on this same
  // route (e.g. the header's "Go to Kill Switch" or notification bell links
  // while already on /admin), so the tab state must react to further URL
  // changes rather than only reading them once at mount. location.key changes
  // on every navigation (even a repeat of the identical tab/view), which a
  // dependency on the derived tab value alone would not catch — e.g. clicking
  // "Go to Kill Switch" a second time while already on System, after having
  // manually clicked over to a different System sub-tab in between.
  useEffect(() => { setActiveTab(initialTab); }, [initialTab, location.key]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Admin</h1>

      <div className="flex border-b border-border-base space-x-1 overflow-x-auto pb-px">
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              activeTab === tab
                ? 'border-border-green text-text-bright'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div>
        {activeTab === 'Data Management' && <DataManagementTab token={token} initialSubTab={initialDataManagementSubTab} navKey={location.key} />}
        {activeTab === 'Exchange Management' && <ExchangeManagementParentTab token={token} initialSubTab={initialExchangeManagementSubTab} navKey={location.key} />}
        {activeTab === 'System Monitoring' && <SystemMonitoringParentTab token={token} initialSubTab={initialSystemMonitoringSubTab} navKey={location.key} />}
        {activeTab === 'Notifications' && <NotificationsPageTab token={token} initialAlertId={initialAlertId} navKey={location.key} />}
        {activeTab === 'System' && <SystemParentTab token={token} initialSubTab={initialSystemSubTab} navKey={location.key} />}
      </div>
    </div>
  );
}

export default Admin;
